import { defineSchema, defineTable } from 'convex/server';
import { type Infer, v } from 'convex/values';

export const SOCIAL_PLATFORMS = {
    INSTAGRAM: 'instagram',
    TIKTOK: 'tiktok',
    YOUTUBE: 'youtube',
    TWITTER: 'twitter',
    FACEBOOK: 'facebook',
} as const;
export const socialPlatformValidator = v.union(
    v.literal(SOCIAL_PLATFORMS.INSTAGRAM),
    v.literal(SOCIAL_PLATFORMS.TIKTOK),
    v.literal(SOCIAL_PLATFORMS.YOUTUBE),
    v.literal(SOCIAL_PLATFORMS.TWITTER),
    v.literal(SOCIAL_PLATFORMS.FACEBOOK)
);
export type SocialPlatform = Infer<typeof socialPlatformValidator>;

/**
 * Who an email send is narrowed to, on top of its audience (all users, or labeled pages): what the
 * creators have done. Picks inside a group widen (TikTok OR Instagram earners); groups narrow
 * (AND). The earnings tiers are in cents and must match EARNINGS_TIERS in
 * resend/emailSegmentsModel.ts; see that file for the definitions.
 */
export const audienceSegmentsValidator = v.object({
    /** Earns on any of these platforms. */
    platforms: v.optional(v.array(v.union(v.literal('tiktok'), v.literal('instagram')))),
    /** Earns on two or more platforms (any of Instagram, TikTok, YouTube, X, Facebook). */
    multiPlatform: v.optional(v.boolean()),
    /** Lifetime earnings (paid + pending) at least this many cents: one of the earnings tiers. */
    minEarnedCents: v.optional(v.union(v.literal(10_000), v.literal(100_000), v.literal(500_000), v.literal(1_000_000))),
    /** Has a connected account, never joined a campaign. */
    connectedNeverJoined: v.optional(v.boolean()),
});
export type AudienceSegments = Infer<typeof audienceSegmentsValidator>;

export const CONNECTED_ACCOUNT_STATUS = {
    PENDING: 'pending',
    VERIFIED: 'verified',
    // @deprecated — no longer produced by any mutation. Kept for existing documents.
    REJECTED: 'rejected',
} as const;
export const connectedAccountStatusValidator = v.union(
    v.literal(CONNECTED_ACCOUNT_STATUS.PENDING),
    v.literal(CONNECTED_ACCOUNT_STATUS.VERIFIED),
    v.literal(CONNECTED_ACCOUNT_STATUS.REJECTED)
);
export type ConnectedAccountStatus = Infer<typeof connectedAccountStatusValidator>;

// Page data sync lifecycle.
export const PAGE_FETCH_STATUS = {
    QUEUED: 'queued',
    PROCESSING: 'processing',
    SUCCESS: 'success',
    FAILED: 'failed',
} as const;
export const pageFetchStatusValidator = v.union(
    v.literal(PAGE_FETCH_STATUS.QUEUED),
    v.literal(PAGE_FETCH_STATUS.PROCESSING),
    v.literal(PAGE_FETCH_STATUS.SUCCESS),
    v.literal(PAGE_FETCH_STATUS.FAILED)
);
export type PageFetchStatus = Infer<typeof pageFetchStatusValidator>;

// Page global moderation status (platform-wide ban).
export const PAGE_GLOBAL_STATUS = {
    ACTIVE: 'active',
    BANNED: 'banned',
} as const;
export const pageGlobalStatusValidator = v.union(v.literal(PAGE_GLOBAL_STATUS.ACTIVE), v.literal(PAGE_GLOBAL_STATUS.BANNED));

// Why migrations/pageOwnershipRepair.ts changed a page's owner (pageOwnershipRepairs.reason).
export const pageOwnershipRepairReasonValidator = v.union(
    v.literal('pending_owner'),
    v.literal('no_account'),
    v.literal('retired_merged'),
    v.literal('displaced')
);
export type PageOwnershipRepairReason = Infer<typeof pageOwnershipRepairReasonValidator>;

// Why a pending-earnings correction left a creator alone (lib/pendingEarnings.ts).
export const pendingCorrectionSkipReasonValidator = v.union(
    v.literal('payout_in_progress'),
    v.literal('payout_clearing'),
    v.literal('queue_pending'),
    v.literal('too_many_rows')
);
export type PageGlobalStatus = Infer<typeof pageGlobalStatusValidator>;

// User global moderation status (platform-wide ban). A HARD ban auto-cascades to the user's pages
// (lib/banning.ts applyUserBan → admin/userCascade.ts); a soft ban never does. Unbanning reverses
// neither — lib/banRestoration.ts does, for a wrongful hard ban.
export const USER_GLOBAL_STATUS = {
    ACTIVE: 'active',
    SOFT_BANNED: 'soft_banned',
    BANNED: 'banned',
} as const;
export const userGlobalStatusValidator = v.union(
    v.literal(USER_GLOBAL_STATUS.ACTIVE),
    v.literal(USER_GLOBAL_STATUS.SOFT_BANNED),
    v.literal(USER_GLOBAL_STATUS.BANNED)
);
export type UserGlobalStatus = Infer<typeof userGlobalStatusValidator>;

// The subset of userGlobalStatusValidator that banUser (admin/users.ts) accepts — ACTIVE is
// reached only via unbanUser, never requested directly. Shared with banRequests below so a
// user-targeted ban request can specify which level the requester wants.
export const banUserStatusValidator = v.union(v.literal(USER_GLOBAL_STATUS.SOFT_BANNED), v.literal(USER_GLOBAL_STATUS.BANNED));
export type BanUserStatus = Infer<typeof banUserStatusValidator>;

// Three-level moderation flag — shared shape for both `pages` and `users`. Distinct from
// globalStatus (ban): a flag is a soft/preventative signal, never a retroactive enforcement.
// GREEN = accept when niche aligned, YELLOW = be careful (informational only, matches the
// legacy `isFlagged` behavior), RED = never accept. No level blocks applying: flagged creators
// apply normally and are never shown their flag. Red only limits who may approve — Promote staff
// on the campaign, never its brand (lib/pageIdentity.ts isRedFlaggedApplication). Staff see the
// applicant's own user flag on every page they apply with in the Pages queue.
export const FLAG_LEVEL = {
    GREEN: 'green',
    YELLOW: 'yellow',
    RED: 'red',
} as const;
export const flagLevelValidator = v.union(v.literal(FLAG_LEVEL.GREEN), v.literal(FLAG_LEVEL.YELLOW), v.literal(FLAG_LEVEL.RED));
export type FlagLevel = Infer<typeof flagLevelValidator>;

// Cached result of checking whether a page is still live on its social platform (distinct from
// PAGE_FETCH_STATUS, which reflects our own sync pipeline's health, not platform-side liveness).
// There is no explicit "unknown" literal: "never checked" and "last check failed transiently"
// are both represented by platformActivityStatus being undefined on the page doc, never by a
// written enum value — see lib/pageActivity.ts and pages.ts:recordPageActivityResult, which only
// ever patches this field to ACTIVE or INACTIVE.
export const PAGE_ACTIVITY_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
} as const;
export const pageActivityStatusValidator = v.union(v.literal(PAGE_ACTIVITY_STATUS.ACTIVE), v.literal(PAGE_ACTIVITY_STATUS.INACTIVE));
export type PageActivityStatus = Infer<typeof pageActivityStatusValidator>;

// Moderator-submitted ban request lifecycle — see admin/banRequests.ts.
export const BAN_REQUEST_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
} as const;
export const banRequestStatusValidator = v.union(
    v.literal(BAN_REQUEST_STATUS.PENDING),
    v.literal(BAN_REQUEST_STATUS.APPROVED),
    v.literal(BAN_REQUEST_STATUS.REJECTED)
);
export type BanRequestStatus = Infer<typeof banRequestStatusValidator>;

export const BAN_REQUEST_TARGET_TYPE = {
    PAGE: 'page',
    USER: 'user',
} as const;
export const banRequestTargetTypeValidator = v.union(v.literal(BAN_REQUEST_TARGET_TYPE.PAGE), v.literal(BAN_REQUEST_TARGET_TYPE.USER));
export type BanRequestTargetType = Infer<typeof banRequestTargetTypeValidator>;

export const PAGE_LABEL_COLORS = ['slate', 'red', 'orange', 'amber', 'emerald', 'teal', 'cyan', 'blue', 'violet', 'pink'] as const;
export type PageLabelColor = (typeof PAGE_LABEL_COLORS)[number];
export const pageLabelColorValidator = v.union(...PAGE_LABEL_COLORS.map((c) => v.literal(c)));

// Page event types for the ownership/participation event log.
export const PAGE_EVENT_TYPE = {
    PAGE_CREATED: 'page_created',
    ACCOUNT_CONNECTED: 'account_connected',
    ACCOUNT_DISCONNECTED: 'account_disconnected',
    OWNERSHIP_TRANSFERRED: 'ownership_transferred',
    CMA_CREATED: 'cma_created',
    CMA_STATUS_CHANGED: 'cma_status_changed',
    CMA_WENT_ROGUE: 'cma_went_rogue',
    CMA_RECLAIMED: 'cma_reclaimed',
    // Emitted when a PENDING CMA on an ACTIVE campaign is deleted at
    // disconnect time (instead of being left as a stranded rogue row).
    CMA_DELETED_ON_DISCONNECT: 'cma_deleted_on_disconnect',
    GLOBAL_STATUS_CHANGED: 'global_status_changed',
    PAGE_FLAGGED: 'page_flagged',
    PAGE_UNFLAGGED: 'page_unflagged',
    // Three-color flag system — emitted by setPageFlag/clearPageFlag alongside (eventually
    // replacing) the legacy PAGE_FLAGGED/PAGE_UNFLAGGED pair. metadata carries `level`.
    PAGE_FLAG_LEVEL_CHANGED: 'page_flag_level_changed',
    // Ban request lifecycle for page targets. metadata carries `banRequestId`, and for
    // BAN_REQUEST_DECIDED, `status` ('approved' | 'rejected') and `reason`.
    BAN_REQUESTED: 'ban_requested',
    BAN_REQUEST_DECIDED: 'ban_request_decided',
    // Duplicate-page merge (migrations/pageDuplicateMerge). Written to BOTH pages: metadata
    // carries `role` ('survivor' | 'retired'), the other page's id/username, the retired row's
    // original identityKey/platformAccountId/platformSecUid, and the moved reference counts.
    PAGE_MERGED: 'page_merged',
} as const;
export const pageEventTypeValidator = v.union(
    v.literal(PAGE_EVENT_TYPE.PAGE_CREATED),
    v.literal(PAGE_EVENT_TYPE.ACCOUNT_CONNECTED),
    v.literal(PAGE_EVENT_TYPE.ACCOUNT_DISCONNECTED),
    v.literal(PAGE_EVENT_TYPE.OWNERSHIP_TRANSFERRED),
    v.literal(PAGE_EVENT_TYPE.CMA_CREATED),
    v.literal(PAGE_EVENT_TYPE.CMA_STATUS_CHANGED),
    v.literal(PAGE_EVENT_TYPE.CMA_WENT_ROGUE),
    v.literal(PAGE_EVENT_TYPE.CMA_RECLAIMED),
    v.literal(PAGE_EVENT_TYPE.CMA_DELETED_ON_DISCONNECT),
    v.literal(PAGE_EVENT_TYPE.GLOBAL_STATUS_CHANGED),
    v.literal(PAGE_EVENT_TYPE.PAGE_FLAGGED),
    v.literal(PAGE_EVENT_TYPE.PAGE_UNFLAGGED),
    v.literal(PAGE_EVENT_TYPE.PAGE_FLAG_LEVEL_CHANGED),
    v.literal(PAGE_EVENT_TYPE.BAN_REQUESTED),
    v.literal(PAGE_EVENT_TYPE.BAN_REQUEST_DECIDED),
    v.literal(PAGE_EVENT_TYPE.PAGE_MERGED)
);
export type PageEventType = Infer<typeof pageEventTypeValidator>;

export const CAMPAIGN_STATUS = {
    /** @deprecated Campaigns are now created directly as active */
    PENDING: 'pending',
    ACTIVE: 'active',
    /** @deprecated Campaigns are no longer rejected via review flow */
    REJECTED: 'rejected',
    ARCHIVED: 'archived',
    COMPLETED: 'completed',
} as const;
export const campaignStatusValidator = v.union(
    v.literal(CAMPAIGN_STATUS.PENDING),
    v.literal(CAMPAIGN_STATUS.ACTIVE),
    v.literal(CAMPAIGN_STATUS.REJECTED),
    v.literal(CAMPAIGN_STATUS.ARCHIVED),
    v.literal(CAMPAIGN_STATUS.COMPLETED)
);
export type CampaignStatus = Infer<typeof campaignStatusValidator>;

// Earnings freezing
export const EARNINGS_FREEZE_REASON = {
    MANUAL: 'manual',
    AUTO: 'auto',
    // DEPRECATED: kept for backwards compat during migration
    AUTO_90: 'auto90',
    AUTO_100: 'auto100',
} as const;
export const earningsFreezeReasonValidator = v.union(
    v.literal(EARNINGS_FREEZE_REASON.MANUAL),
    v.literal(EARNINGS_FREEZE_REASON.AUTO),
    // DEPRECATED: kept for backwards compat during migration
    v.literal(EARNINGS_FREEZE_REASON.AUTO_90),
    v.literal(EARNINGS_FREEZE_REASON.AUTO_100)
);
export type EarningsFreezeReason = Infer<typeof earningsFreezeReasonValidator>;

// DEPRECATED: kept for backwards compat during migration. Will be removed post-migration.
export const EARNINGS_AUTO_FREEZE_STATE = {
    PRE_90: 'pre90',
    PRE_100: 'pre100',
    FINAL: 'final',
} as const;
export const earningsAutoFreezeStateValidator = v.union(
    v.literal(EARNINGS_AUTO_FREEZE_STATE.PRE_90),
    v.literal(EARNINGS_AUTO_FREEZE_STATE.PRE_100),
    v.literal(EARNINGS_AUTO_FREEZE_STATE.FINAL)
);
export type EarningsAutoFreezeState = Infer<typeof earningsAutoFreezeStateValidator>;

/** @deprecated Campaign approval/rejection flow removed */
export const CAMPAIGN_REVIEW_ACTIONS = {
    APPROVED: 'approved',
    REJECTED: 'rejected',
} as const;
/** @deprecated Campaign approval/rejection flow removed */
export const campaignReviewActionValidator = v.union(v.literal(CAMPAIGN_REVIEW_ACTIONS.APPROVED), v.literal(CAMPAIGN_REVIEW_ACTIONS.REJECTED));
/** @deprecated Campaign approval/rejection flow removed */
export type CampaignReviewAction = Infer<typeof campaignReviewActionValidator>;

export const SUBMISSION_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    DENIED: 'denied',
    // System-only: set when a still-pending submission's campaign ends (see lib/campaignExpire.ts).
    // Not a denial — no creator notification, excluded from all moderation metrics. Never
    // settable via a moderator-facing mutation; only the expire batch job writes this.
    EXPIRED: 'expired',
} as const;
export const submissionStatusValidator = v.union(
    v.literal(SUBMISSION_STATUS.PENDING),
    v.literal(SUBMISSION_STATUS.APPROVED),
    v.literal(SUBMISSION_STATUS.DENIED),
    v.literal(SUBMISSION_STATUS.EXPIRED)
);
export type SubmissionStatus = Infer<typeof submissionStatusValidator>;

export const SUBMISSION_FETCH_STATUS = {
    QUEUED: 'queued',
    PROCESSING: 'processing',
    SUCCESS: 'success',
    FAILED: 'failed',
} as const;
// Facebook views read from page listings (lib/facebookListings.ts): /page/videos is exact, /page/reels
// rounds to 100s.
export const facebookViewCountPrecisionValidator = v.union(v.literal('exact'), v.literal('rounded'));
export type FacebookViewCountPrecision = Infer<typeof facebookViewCountPrecisionValidator>;
export const facebookListingWalkValidator = v.object({ horizonSec: v.number(), walkedAt: v.number() });
export const facebookListingResumeValidator = v.object({ cursor: v.string(), oldestSec: v.optional(v.number()), startedAt: v.number() });

export const submissionFetchStatusValidator = v.union(
    v.literal(SUBMISSION_FETCH_STATUS.QUEUED),
    v.literal(SUBMISSION_FETCH_STATUS.PROCESSING),
    v.literal(SUBMISSION_FETCH_STATUS.SUCCESS),
    v.literal(SUBMISSION_FETCH_STATUS.FAILED)
);
export type SubmissionFetchStatus = Infer<typeof submissionFetchStatusValidator>;

// Lifecycle of a row in the fair-share refresh queue (refreshQueueItems table).
// Distinct from SUBMISSION_FETCH_STATUS: that one tracks the most recent attempt's
// outcome on the per-submission satellite; this one tracks the dispatch lifecycle
// of a single queued attempt (pending → processing → completed|failed).
export const REFRESH_QUEUE_ITEM_STATUS = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed',
} as const;
export const refreshQueueItemStatusValidator = v.union(
    v.literal(REFRESH_QUEUE_ITEM_STATUS.PENDING),
    v.literal(REFRESH_QUEUE_ITEM_STATUS.PROCESSING),
    v.literal(REFRESH_QUEUE_ITEM_STATUS.COMPLETED),
    v.literal(REFRESH_QUEUE_ITEM_STATUS.FAILED)
);
export type RefreshQueueItemStatus = Infer<typeof refreshQueueItemStatusValidator>;

export const SNAPSHOT_SOURCE = {
    INITIAL_PROCESSING: 'initial_processing',
    SCHEDULED_REFRESH: 'scheduled_refresh',
    ADMIN_REFRESH: 'admin_refresh',
    USER_REFRESH: 'user_refresh',
} as const;
export const snapshotSourceValidator = v.union(
    v.literal(SNAPSHOT_SOURCE.INITIAL_PROCESSING),
    v.literal(SNAPSHOT_SOURCE.SCHEDULED_REFRESH),
    v.literal(SNAPSHOT_SOURCE.ADMIN_REFRESH),
    v.literal(SNAPSHOT_SOURCE.USER_REFRESH)
);
export type SnapshotSource = Infer<typeof snapshotSourceValidator>;

// Reusable validator for campaign earnings timeline source
export const EARNINGS_TIMELINE_SOURCE = {
    TRIGGER: 'trigger',
    SCHEDULED_REFRESH: 'scheduled_refresh',
    ADMIN_REFRESH: 'admin_refresh',
    INITIAL_PROCESSING: 'initial_processing',
    USER_REFRESH: 'user_refresh',
    SNAPSHOT_RECALCULATE: 'snapshot_recalculate',
    SNAPSHOT_CLEAR: 'snapshot_clear',
    BUDGET_CHANGE: 'budget_change',
    ADMIN_UPDATE: 'admin_update',
    RECREDIT: 'recredit',
} as const;
export const earningsTimelineSourceValidator = v.union(
    v.literal(EARNINGS_TIMELINE_SOURCE.TRIGGER),
    v.literal(EARNINGS_TIMELINE_SOURCE.SCHEDULED_REFRESH),
    v.literal(EARNINGS_TIMELINE_SOURCE.ADMIN_REFRESH),
    v.literal(EARNINGS_TIMELINE_SOURCE.INITIAL_PROCESSING),
    v.literal(EARNINGS_TIMELINE_SOURCE.USER_REFRESH),
    v.literal(EARNINGS_TIMELINE_SOURCE.SNAPSHOT_RECALCULATE),
    v.literal(EARNINGS_TIMELINE_SOURCE.SNAPSHOT_CLEAR),
    v.literal(EARNINGS_TIMELINE_SOURCE.BUDGET_CHANGE),
    v.literal(EARNINGS_TIMELINE_SOURCE.ADMIN_UPDATE),
    v.literal(EARNINGS_TIMELINE_SOURCE.RECREDIT)
);
export type EarningsTimelineSource = Infer<typeof earningsTimelineSourceValidator>;

export const ROLES = {
    SUPER_USER: 'super_user',
    ADMIN: 'admin',
    MODERATOR: 'moderator',
} as const;
export const roleValidator = v.union(v.literal(ROLES.SUPER_USER), v.literal(ROLES.ADMIN), v.literal(ROLES.MODERATOR));
export type Role = Infer<typeof roleValidator>;

// ============================================================================
// Referrals (docs/systems/REFERRAL_SYSTEM.md)
// ============================================================================

export const REFERRAL_PROGRAM_STATUS = {
    ENABLED: 'enabled',
    DISABLED: 'disabled',
} as const;
export const referralProgramStatusValidator = v.union(v.literal(REFERRAL_PROGRAM_STATUS.ENABLED), v.literal(REFERRAL_PROGRAM_STATUS.DISABLED));
export type ReferralProgramStatus = Infer<typeof referralProgramStatusValidator>;

export const REFERRAL_LINK_STATUS = {
    ACTIVE: 'active',
    DEACTIVATED: 'deactivated',
} as const;
export const referralLinkStatusValidator = v.union(v.literal(REFERRAL_LINK_STATUS.ACTIVE), v.literal(REFERRAL_LINK_STATUS.DEACTIVATED));
export type ReferralLinkStatus = Infer<typeof referralLinkStatusValidator>;

// Funnel stages, in order. A referral's stage only ever moves forward (latched).
export const REFERRAL_STAGES = {
    SIGNED_UP: 'signed_up', // account created + attributed
    ONBOARDED: 'onboarded', // username set — the headline "signups" number
    ACCOUNT_VERIFIED: 'account_verified', // first verified, connected social account
    CAMPAIGN_JOINED: 'campaign_joined', // joinCampaign succeeded
    COMPLETED: 'completed', // first approved, non-duplicate submission
} as const;
export const referralStageValidator = v.union(
    v.literal(REFERRAL_STAGES.SIGNED_UP),
    v.literal(REFERRAL_STAGES.ONBOARDED),
    v.literal(REFERRAL_STAGES.ACCOUNT_VERIFIED),
    v.literal(REFERRAL_STAGES.CAMPAIGN_JOINED),
    v.literal(REFERRAL_STAGES.COMPLETED)
);
export type ReferralStage = Infer<typeof referralStageValidator>;
export const REFERRAL_STAGE_RANK: Record<ReferralStage, number> = {
    signed_up: 1,
    onboarded: 2,
    account_verified: 3,
    campaign_joined: 4,
    completed: 5,
};

export const REFERRAL_STATUS = {
    ACTIVE: 'active',
    VOIDED: 'voided',
} as const;
export const referralStatusValidator = v.union(v.literal(REFERRAL_STATUS.ACTIVE), v.literal(REFERRAL_STATUS.VOIDED));
export type ReferralStatus = Infer<typeof referralStatusValidator>;

export const REFERRAL_SOURCES = {
    LINK: 'link',
    MANUAL_CODE: 'manual_code',
} as const;
export const referralSourceValidator = v.union(v.literal(REFERRAL_SOURCES.LINK), v.literal(REFERRAL_SOURCES.MANUAL_CODE));

export const REFERRAL_ACTOR_ROLES = {
    REFERRER: 'referrer',
    ADMIN: 'admin',
    SYSTEM: 'system',
} as const;
export const referralActorRoleValidator = v.union(
    v.literal(REFERRAL_ACTOR_ROLES.REFERRER),
    v.literal(REFERRAL_ACTOR_ROLES.ADMIN),
    v.literal(REFERRAL_ACTOR_ROLES.SYSTEM)
);
export type ReferralActorRole = Infer<typeof referralActorRoleValidator>;

export const REFERRAL_AUDIT_ACTIONS = {
    PROGRAM_ENABLED: 'program_enabled',
    PROGRAM_DISABLED: 'program_disabled',
    LINK_CREATED: 'link_created',
    LINK_RENAMED: 'link_renamed',
    LINK_DEACTIVATED: 'link_deactivated',
    LINK_REACTIVATED: 'link_reactivated',
    REFERRAL_VOIDED: 'referral_voided',
    REFERRAL_UNVOIDED: 'referral_unvoided',
    // An admin changed the referrer's active-link cap (previousValue/newValue: the cap, '' = default).
    LINK_CAP_CHANGED: 'link_cap_changed',
} as const;
export const referralAuditActionValidator = v.union(
    v.literal(REFERRAL_AUDIT_ACTIONS.PROGRAM_ENABLED),
    v.literal(REFERRAL_AUDIT_ACTIONS.PROGRAM_DISABLED),
    v.literal(REFERRAL_AUDIT_ACTIONS.LINK_CREATED),
    v.literal(REFERRAL_AUDIT_ACTIONS.LINK_RENAMED),
    v.literal(REFERRAL_AUDIT_ACTIONS.LINK_DEACTIVATED),
    v.literal(REFERRAL_AUDIT_ACTIONS.LINK_REACTIVATED),
    v.literal(REFERRAL_AUDIT_ACTIONS.REFERRAL_VOIDED),
    v.literal(REFERRAL_AUDIT_ACTIONS.REFERRAL_UNVOIDED),
    v.literal(REFERRAL_AUDIT_ACTIONS.LINK_CAP_CHANGED)
);
export type ReferralAuditAction = Infer<typeof referralAuditActionValidator>;

// Admin referral analytics snapshots (docs/systems/REFERRAL_SYSTEM.md §13).
export const REFERRAL_STATS_RUN_STATUS = {
    RUNNING: 'running',
    COMPLETE: 'complete',
    FAILED: 'failed',
} as const;
export const referralStatsRunStatusValidator = v.union(
    v.literal(REFERRAL_STATS_RUN_STATUS.RUNNING),
    v.literal(REFERRAL_STATS_RUN_STATUS.COMPLETE),
    v.literal(REFERRAL_STATS_RUN_STATUS.FAILED)
);
export const referralStatsPhaseValidator = v.union(v.literal('links'), v.literal('referrals'), v.literal('programs'));
export type ReferralStatsPhase = Infer<typeof referralStatsPhaseValidator>;

/**
 * Counts shared by a platform run and a per-referrer row. Funnel fields are "reached at least this
 * stage" over active referrals, the same semantics as aggregateReferralFunnel; voided are counted
 * apart. Earnings are the referred users' first-campaign figures as admins see them (private
 * profiles included), summed; paidCents is the part already paid out.
 */
export const referralStatsCountsFields = {
    signedUp: v.number(),
    onboarded: v.number(),
    accountVerified: v.number(),
    campaignJoined: v.number(),
    completed: v.number(),
    voided: v.number(),
    earningsCents: v.number(),
    paidCents: v.number(),
    activeLinks: v.number(),
    totalLinks: v.number(),
};

export const REFERRAL_EVENT_TYPES = {
    ATTRIBUTED: 'attributed',
    STAGE_REACHED: 'stage_reached',
    COMPLETION_REVERSED: 'completion_reversed',
} as const;
export const referralEventTypeValidator = v.union(
    v.literal(REFERRAL_EVENT_TYPES.ATTRIBUTED),
    v.literal(REFERRAL_EVENT_TYPES.STAGE_REACHED),
    v.literal(REFERRAL_EVENT_TYPES.COMPLETION_REVERSED)
);

/**
 * Per-moderator view-permission surfaces (DEV-66).
 *
 * Moderators are default-deny: holding the role grants nothing beyond acting on
 * assigned campaigns. An admin can grant a moderator extra read surfaces beyond
 * their assigned-campaign work by inserting a `moderatorViewPermissions` row.
 * These map to the global reads blocked for moderators in the data-access
 * boundary (DEV-65):
 * - `creator_profiles` → global user/creator profile reads (list, detail, connected accounts, notes, picker)
 * - `pages`            → global page reads (list, detail, hover, campaigns, events, page notes)
 * - `exports`          → CSV export of submissions (on top of the per-campaign assignment check)
 */
export const MODERATOR_VIEW_SURFACES = {
    CREATOR_PROFILES: 'creator_profiles',
    PAGES: 'pages',
    EXPORTS: 'exports',
} as const;
export const moderatorViewSurfaceValidator = v.union(
    v.literal(MODERATOR_VIEW_SURFACES.CREATOR_PROFILES),
    v.literal(MODERATOR_VIEW_SURFACES.PAGES),
    v.literal(MODERATOR_VIEW_SURFACES.EXPORTS)
);
export type ModeratorViewSurface = Infer<typeof moderatorViewSurfaceValidator>;

/**
 * @deprecated Membership status is vestigial — all memberships are created as APPROVED
 * and no mutation ever changes the status. Kept for schema compatibility with existing
 * documents. Moderation at the membership level is handled by control flags
 * (activityFrozen, canAddNewPages), not by status.
 */
export const MEMBERSHIP_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    DENIED: 'denied',
    BANNED: 'banned',
} as const;
export const membershipStatusValidator = v.union(
    v.literal(MEMBERSHIP_STATUS.PENDING),
    v.literal(MEMBERSHIP_STATUS.APPROVED),
    v.literal(MEMBERSHIP_STATUS.DENIED),
    v.literal(MEMBERSHIP_STATUS.BANNED)
);
export type MembershipStatus = Infer<typeof membershipStatusValidator>;

export const MEMBER_ACCOUNT_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    DENIED: 'denied',
    /** @deprecated Functionally identical to DENIED. Not writable (updateAccountStatus refuses it); old rows are renamed by migrations/legacyBannedMemberAccounts. Kept for schema compatibility until that has run everywhere. */
    BANNED: 'banned',
    // System-only: set when a still-pending page's campaign ends (see lib/campaignExpire.ts).
    // Not a denial — no creator notification, excluded from all moderation metrics. Never
    // settable via a moderator-facing mutation; only the expire batch job writes this.
    EXPIRED: 'expired',
} as const;
export const memberAccountStatusValidator = v.union(
    v.literal(MEMBER_ACCOUNT_STATUS.PENDING),
    v.literal(MEMBER_ACCOUNT_STATUS.APPROVED),
    v.literal(MEMBER_ACCOUNT_STATUS.DENIED),
    v.literal(MEMBER_ACCOUNT_STATUS.BANNED),
    v.literal(MEMBER_ACCOUNT_STATUS.EXPIRED)
);
export type MemberAccountStatus = Infer<typeof memberAccountStatusValidator>;

// Status history source constants
export const STATUS_HISTORY_SOURCE = {
    ADMIN: 'admin',
    AUTO_DENY: 'auto-deny',
    SYSTEM: 'system',
    USER: 'user',
} as const;
export const statusHistorySourceValidator = v.union(
    v.literal(STATUS_HISTORY_SOURCE.ADMIN),
    v.literal(STATUS_HISTORY_SOURCE.AUTO_DENY),
    v.literal(STATUS_HISTORY_SOURCE.SYSTEM),
    v.literal(STATUS_HISTORY_SOURCE.USER)
);
export type StatusHistorySource = Infer<typeof statusHistorySourceValidator>;

// Reusable validator for submission status history entries
export const statusHistoryEntryValidator = v.object({
    previousStatus: v.optional(submissionStatusValidator), // status before the change
    status: submissionStatusValidator,
    changedBy: v.optional(v.id('users')), // undefined for system/auto actions
    changedAt: v.number(),
    reason: v.optional(v.string()),
    source: statusHistorySourceValidator,
    // Set only when this transition was produced by a bulk-deny action (page-ban cascade,
    // "deny all" for a user/account) so many individually-denied submissions can be traced
    // back to and counted as the single moderator decision that caused them. Undefined for
    // every individual/manual/auto decision. See lib/moderationEvents.ts.
    cascadeGroupId: v.optional(v.string()),
    // Set when a ban restoration (lib/banRestoration.ts) reversed this transition: the entry stays
    // for the audit trail, but every earnings/window reader skips it (effectiveStatusHistory in
    // lib/types.ts), so the submission earns as if the wrongful ban never happened.
    voidedAt: v.optional(v.number()),
    voidedBy: v.optional(v.id('users')),
    voidReason: v.optional(v.string()),
    // The restoration that voided this entry, or that wrote it (restoration entries are born voided).
    banRestorationId: v.optional(v.id('banRestorations')),
});
export type StatusHistoryEntry = Infer<typeof statusHistoryEntryValidator>;

// Reusable validator for page global status history entries
export const pageGlobalStatusHistoryEntryValidator = v.object({
    previousStatus: pageGlobalStatusValidator,
    status: pageGlobalStatusValidator,
    changedBy: v.id('users'),
    changedAt: v.number(),
    reason: v.string(),
    // Set when this ban/unban originated from an approved ban request rather than a direct
    // admin action — see admin/banRequests.ts:approveBanRequest.
    sourceBanRequestId: v.optional(v.id('banRequests')),
});
export type PageGlobalStatusHistoryEntry = Infer<typeof pageGlobalStatusHistoryEntryValidator>;

// Reusable validator for user global status history entries
export const userGlobalStatusHistoryEntryValidator = v.object({
    previousStatus: userGlobalStatusValidator,
    status: userGlobalStatusValidator,
    changedBy: v.id('users'),
    changedAt: v.number(),
    reason: v.string(),
    sourceBanRequestId: v.optional(v.id('banRequests')),
});
export type UserGlobalStatusHistoryEntry = Infer<typeof userGlobalStatusHistoryEntryValidator>;

// Ban hold (lib/banHolds.ts): a ban that holds instead of denying. One row per banned page.
export const BAN_HOLD_STATUS = {
    ACTIVE: 'active',
    RELEASED: 'released',
    EXPIRED: 'expired',
} as const;
export const banHoldStatusValidator = v.union(v.literal(BAN_HOLD_STATUS.ACTIVE), v.literal(BAN_HOLD_STATUS.RELEASED), v.literal(BAN_HOLD_STATUS.EXPIRED));
export type BanHoldStatus = Infer<typeof banHoldStatusValidator>;
export const banHoldSourceValidator = v.union(v.literal('page_ban'), v.literal('user_ban'));

// Ban restoration (lib/banRestoration.ts): reversing what a wrongful hard ban's cascade changed.
export const BAN_RESTORATION_STATUS = {
    RUNNING: 'running',
    COMPLETED: 'completed',
    COMPLETED_WITH_ISSUES: 'completed_with_issues',
    FAILED: 'failed',
} as const;
export const banRestorationStatusValidator = v.union(
    v.literal(BAN_RESTORATION_STATUS.RUNNING),
    v.literal(BAN_RESTORATION_STATUS.COMPLETED),
    v.literal(BAN_RESTORATION_STATUS.COMPLETED_WITH_ISSUES),
    v.literal(BAN_RESTORATION_STATUS.FAILED)
);

// Something a restoration could not put back, shown to staff as a flag.
export const banRestorationIssueValidator = v.object({
    kind: v.union(v.literal('page'), v.literal('cma'), v.literal('submission'), v.literal('campaign')),
    id: v.optional(v.string()),
    message: v.string(),
});
export type BanRestorationIssue = Infer<typeof banRestorationIssueValidator>;

export const banRestorationPageOutcomeValidator = v.object({
    pageId: v.id('pages'),
    platform: v.string(),
    username: v.string(),
    // expired: banned more than 30 days before the restoration started — left banned.
    outcome: v.union(v.literal('unbanned'), v.literal('already_active'), v.literal('still_banned'), v.literal('expired')),
});

export const BAN_RESTORATION_CAMPAIGN_OUTCOME = {
    RESTORED: 'restored',
    NOTHING_TO_DO: 'nothing_to_do',
    PAID_OUT: 'paid_out',
    BLOCKED: 'blocked',
    SKIPPED_REDUCES_OTHERS: 'skipped_reduces_others',
    FAILED: 'failed',
} as const;
export const banRestorationCampaignOutcomeValidator = v.object({
    campaignId: v.id('campaigns'),
    title: v.string(),
    outcome: v.union(
        v.literal(BAN_RESTORATION_CAMPAIGN_OUTCOME.RESTORED),
        v.literal(BAN_RESTORATION_CAMPAIGN_OUTCOME.NOTHING_TO_DO),
        v.literal(BAN_RESTORATION_CAMPAIGN_OUTCOME.PAID_OUT),
        v.literal(BAN_RESTORATION_CAMPAIGN_OUTCOME.BLOCKED),
        v.literal(BAN_RESTORATION_CAMPAIGN_OUTCOME.SKIPPED_REDUCES_OTHERS),
        v.literal(BAN_RESTORATION_CAMPAIGN_OUTCOME.FAILED)
    ),
    cmasRestored: v.number(),
    submissionsRestored: v.number(),
    // Submissions whose status was already right (a manual rollback) but whose earnings needed the
    // wrongful deny voided.
    submissionsEarningsOnly: v.number(),
    earningsBeforeCents: v.optional(v.number()),
    earningsAfterCents: v.optional(v.number()),
    // Paid-out campaigns only: what the creator would have been paid minus what they were paid.
    owedCents: v.optional(v.number()),
    // A newer earnings rebuild of the campaign started while this one ran (newest-wins tickets,
    // lib/rebuildTicket.ts), so earningsAfterCents may not be final — the newer rebuild finishes the job.
    earningsPending: v.optional(v.boolean()),
    issues: v.array(banRestorationIssueValidator),
});
export type BanRestorationCampaignOutcome = Infer<typeof banRestorationCampaignOutcomeValidator>;

// Page/user flag history entry — tracks flag/unflag actions. 'flagged'/'unflagged' are the
// legacy (pre-3-color) actions, kept valid for existing rows. New writes use 'level_changed'
// with `level` set to the color that was applied ('flagged'/'unflagged' are never written by
// new code — see admin/pages.ts:setPageFlag/clearPageFlag).
export const pageFlagHistoryEntryValidator = v.object({
    action: v.union(v.literal('flagged'), v.literal('unflagged'), v.literal('level_changed')),
    level: v.optional(flagLevelValidator),
    reason: v.optional(v.string()),
    changedBy: v.id('users'),
    changedAt: v.number(),
});
export type PageFlagHistoryEntry = Infer<typeof pageFlagHistoryEntryValidator>;

// Ban request status history entry — mirrors submissions.statusHistory's shape.
export const banRequestStatusHistoryEntryValidator = v.object({
    previousStatus: v.optional(banRequestStatusValidator), // undefined for the initial 'pending' entry
    status: banRequestStatusValidator,
    changedBy: v.id('users'),
    changedAt: v.number(),
    note: v.optional(v.string()),
});
export type BanRequestStatusHistoryEntry = Infer<typeof banRequestStatusHistoryEntryValidator>;

// A link the requester attached to show what the ban is for (the video they were botting on, etc.).
// submissionId/campaignId/videoId are set only when the link matched one of the target's own
// submissions — see admin/banRequests.ts:matchEvidence. Unmatched links are kept as-is.
export const banRequestEvidenceValidator = v.object({
    url: v.string(),
    submissionId: v.optional(v.id('submissions')),
    campaignId: v.optional(v.id('campaigns')),
    videoId: v.optional(v.string()),
});
export type BanRequestEvidence = Infer<typeof banRequestEvidenceValidator>;

// Reusable validator for page note edit history entries
export const pageNoteEditHistoryEntryValidator = v.object({
    previousContent: v.string(),
    editedBy: v.id('users'),
    editedAt: v.number(),
});
export type PageNoteEditHistoryEntry = Infer<typeof pageNoteEditHistoryEntryValidator>;

// Reusable validator for video statistics
export const videoStatsValidator = v.object({
    accountName: v.optional(v.string()),
    viewCount: v.optional(v.number()),
    likeCount: v.optional(v.number()),
    commentCount: v.optional(v.number()),
    shareCount: v.optional(v.number()),
    // Absent when the platform doesn't report them (only Instagram does today).
    saveCount: v.optional(v.number()),
    repostCount: v.optional(v.number()),
    thumbnail: v.optional(v.string()),
    videoCreatedTime: v.optional(v.number()),
});

// The shape of an enriched submission's `earnings` (lib/submissionEnrichment.ts, built from the
// submissionEarnings table). No table field uses it since submissions.earnings was dropped (#535).
export const submissionEarningsDataValidator = v.object({
    rawEarningsCents: v.number(),
    videoCappedEarningsCents: v.number(),
    potentialEarningsCents: v.number(),
    isVideoCapped: v.boolean(),
    isWindowClosed: v.optional(v.boolean()), // Derived each reallocation (mirrors isVideoCapped). Optional → existing rows need no backfill.
    lastCalculatedAt: v.number(),
});

// Reusable validator for platform metrics
export const platformMetricsValidator = v.object({
    views: v.number(),
    likes: v.number(),
    comments: v.number(),
    shares: v.number(),
});

// Reusable validator for platform breakdowns
export const platformBreakdownValidator = v.object({
    instagram: v.optional(platformMetricsValidator),
    tiktok: v.optional(platformMetricsValidator),
    youtube: v.optional(platformMetricsValidator),
    twitter: v.optional(platformMetricsValidator),
    facebook: v.optional(platformMetricsValidator),
});

// Reusable validator for creator counts by platform
export const creatorsByPlatformValidator = v.object({
    instagram: v.optional(v.number()),
    tiktok: v.optional(v.number()),
    youtube: v.optional(v.number()),
    twitter: v.optional(v.number()),
    facebook: v.optional(v.number()),
});

// ============================================
// Campaign Rules Validators
// ============================================

// Audience Tiers (fixed values representing engagement quality)
export const AUDIENCE_TIERS = {
    T1: 't1',
    T2: 't2',
    T3: 't3',
} as const;
export const audienceTierValidator = v.union(v.literal(AUDIENCE_TIERS.T1), v.literal(AUDIENCE_TIERS.T2), v.literal(AUDIENCE_TIERS.T3));
export type AudienceTier = Infer<typeof audienceTierValidator>;

// Profile Requirements - audience and language restrictions
export const profileRequirementsValidator = v.object({
    textRequirements: v.optional(v.array(v.string())),
    audienceTiers: v.optional(v.array(audienceTierValidator)),
    contentLanguages: v.optional(v.array(v.string())), // Simple string array for flexibility
    minFollowers: v.optional(v.number()), // Minimum followers required to join campaign
});
export type ProfileRequirements = Infer<typeof profileRequirementsValidator>;

// Asset link with title and URL
export const assetLinkValidator = v.object({
    title: v.string(),
    url: v.string(),
});
export type AssetLink = Infer<typeof assetLinkValidator>;

// One required audio track for the campaign's Sounds block: title, link, and an optional
// timestamp the creator should start the clip from (e.g. "Start at 0:38").
export const soundRequirementValidator = v.object({
    title: v.string(),
    url: v.string(),
    startAtSeconds: v.optional(v.number()),
});
export type SoundRequirement = Infer<typeof soundRequirementValidator>;

// Content Requirements - asset links and content toggles with details
export const contentRequirementsValidator = v.object({
    textRequirements: v.optional(v.array(v.string())),
    assetLinks: v.optional(v.array(assetLinkValidator)),
    // Multiple required tracks (unlike audioRequirement below, which is a single legacy toggle).
    // See campaignContentValidator's doc comment for why this lives here rather than in
    // campaignContent despite Overview's Sounds block reading it unredacted.
    soundRequirements: v.optional(v.array(soundRequirementValidator)),
    linkInBio: v.optional(
        v.object({
            enabled: v.boolean(),
            url: v.optional(v.string()),
        })
    ),
    audioRequirement: v.optional(
        v.object({
            enabled: v.boolean(),
            audioUrl: v.optional(v.string()),
            description: v.optional(v.string()),
        })
    ),
    captionRequirement: v.optional(
        v.object({
            enabled: v.boolean(),
            requiredText: v.optional(v.string()),
            description: v.optional(v.string()),
        })
    ),
    logoRequirement: v.optional(
        v.object({
            enabled: v.boolean(),
            logoUrl: v.optional(v.string()),
            description: v.optional(v.string()),
        })
    ),
});
export type ContentRequirements = Infer<typeof contentRequirementsValidator>;

// Posting Requirements - payout and posting limits
export const postingRequirementsValidator = v.object({
    textRequirements: v.optional(v.array(v.string())),
    minViewsPerPost: v.optional(v.number()), // Auto-deny submissions below this per-post view threshold
    minViewsForPayout: v.optional(v.number()), // User-level minimum cumulative approved views required for payout
    maxPostsPerPage: v.optional(v.number()),
    maxPayoutPerCreator: v.optional(v.number()), // Store in cents for precision
    maxPayoutPerVideo: v.optional(v.number()), // Store in cents for precision
    videoPaymentWindowDays: v.optional(v.number()), // N days after most-recent approval during which a video earns; after that earnings freeze at the boundary view count. Omitted = no window.
});
export type PostingRequirements = Infer<typeof postingRequirementsValidator>;

// Demographics proof requirement - campaign join gating
export const demographicsProofRequirementValidator = v.object({
    enabled: v.boolean(),
    description: v.optional(v.string()),
});
export type DemographicsProofRequirement = Infer<typeof demographicsProofRequirementValidator>;

// Spark code requirement - creator must attach a platform ad-code (e.g. TikTok Spark Code) to each video submission
export const sparkCodeRequirementValidator = v.object({
    enabled: v.boolean(),
    description: v.optional(v.string()),
});
export type SparkCodeRequirement = Infer<typeof sparkCodeRequirementValidator>;

// Combined Campaign Rules
export const campaignRulesValidator = v.object({
    profileRequirements: v.optional(profileRequirementsValidator),
    contentRequirements: v.optional(contentRequirementsValidator),
    postingRequirements: v.optional(postingRequirementsValidator),
    sparkCodeRequirement: v.optional(sparkCodeRequirementValidator),
});
export type CampaignRules = Infer<typeof campaignRulesValidator>;

// ============================================================================
// Campaign Content (structured Overview blocks — see docs/systems for the spec)
// ============================================================================
// Unlike campaignRules, this is NEVER redacted: campaignRules is stripped wholesale for a
// logged-out visitor or a non-member below the follower threshold (see campaigns.ts's
// redactRulesFields), which is exactly the audience the Overview tab exists to convert. Assets
// and Sounds are the one exception — their source of truth stays in
// contentRequirements.assetLinks/soundRequirements (single admin edit surface, unchanged
// Rules-tab gating), and getCampaign additionally copies them here, unredacted, for Overview to
// read. See campaigns.ts's getCampaign handler for that response-shape composition.

// One card in the What to Post / Talking Points grids: a short headline, optional supporting
// detail, and (Talking Points only) a "weight" flag for the single most important angle.
export const creativeCardValidator = v.object({
    headline: v.string(),
    description: v.optional(v.string()),
    weight: v.optional(v.literal('heaviest')),
});
export type CreativeCard = Infer<typeof creativeCardValidator>;

// One row in the Must Include grid — admin-authored label/value pairs (not a fixed enum): the
// spec's own "becomes checkable" intent is future validation work, not scope today. A `kind`
// discriminator (hashtag/tag/logo/...) is the additive path when that validation gets built.
export const labelValuePairValidator = v.object({
    label: v.string(),
    value: v.string(),
});
export type LabelValuePair = Infer<typeof labelValuePairValidator>;

export const campaignContentValidator = v.object({
    // Required block (always renders, even empty — an empty array is a real, intentional state,
    // not "unset"). What to Post is the campaign's creative direction: 2-4 cards.
    whatToPost: v.optional(v.array(creativeCardValidator)),
    // Optional block, same card shape as What to Post — renders only when non-empty. One card may
    // carry weight: 'heaviest' to flag the single most important talking point.
    talkingPoints: v.optional(v.array(creativeCardValidator)),
    // Optional, admin-ordered (drag-sortable in the editor).
    mustInclude: v.optional(v.array(labelValuePairValidator)),
    // Optional — instant-rejection reasons, rendered with a red-X icon (visually distinct from
    // What to Post/Talking Points' green check).
    avoid: v.optional(v.array(v.string())),
    // Optional — page-type chips (e.g. "Sports pages", "HQ Meme pages").
    acceptedPages: v.optional(v.array(v.string())),
});
export type CampaignContent = Infer<typeof campaignContentValidator>;

// ============================================================================
// Wallet Validators (USD-only for now)
// ============================================================================

export const WALLET_CURRENCIES = {
    USD: 'usd',
} as const;
export const walletCurrencyValidator = v.literal(WALLET_CURRENCIES.USD);
export type WalletCurrency = Infer<typeof walletCurrencyValidator>;

export const WALLET_TRANSACTION_DIRECTIONS = {
    CREDIT: 'credit',
    DEBIT: 'debit',
} as const;
export const walletTransactionDirectionValidator = v.union(v.literal(WALLET_TRANSACTION_DIRECTIONS.CREDIT), v.literal(WALLET_TRANSACTION_DIRECTIONS.DEBIT));
export type WalletTransactionDirection = Infer<typeof walletTransactionDirectionValidator>;

export const SOLANA_CLUSTERS = {
    DEVNET: 'devnet',
    MAINNET: 'mainnet',
} as const;
export const solanaClusterValidator = v.union(v.literal(SOLANA_CLUSTERS.DEVNET), v.literal(SOLANA_CLUSTERS.MAINNET));
export type SolanaCluster = Infer<typeof solanaClusterValidator>;

export const SOLANA_WITHDRAWAL_STATUSES = {
    PENDING: 'pending',
    SENDING: 'sending',
    SENT: 'sent',
    CONFIRMED: 'confirmed',
    FAILED: 'failed',
} as const;
// Every notification kind. notifications/kinds.ts defines one payload per kind and email/content.ts one
// email per kind (both keyed by this), so a new kind needs both before it compiles.
export const notificationKindValidator = v.union(
    v.literal('submission_approved'),
    v.literal('submission_denied'),
    v.literal('application_approved'),
    v.literal('application_denied'),
    v.literal('withdrawal_confirmed'),
    v.literal('withdrawal_failed')
);
export type NotificationKindName = Infer<typeof notificationKindValidator>;

// Where a creator wants notifications (Account settings → Preferences). Default 'both'.
export const notificationChannelValidator = v.union(v.literal('in_app'), v.literal('email'), v.literal('both'));
export type NotificationChannel = Infer<typeof notificationChannelValidator>;

// Why a planned notification email was not sent (notifications/email/plan.ts).
export const notificationEmailSkipReasonValidator = v.union(
    v.literal('no_email'),
    v.literal('notification_gone'),
    v.literal('seen_in_app'),
    v.literal('superseded'),
    v.literal('channel_off'),
    v.literal('suppressed'),
    v.literal('non_prod'),
    v.literal('address_changed'),
    v.literal('render_failed')
);
export type NotificationEmailSkipReason = Infer<typeof notificationEmailSkipReasonValidator>;

export const solanaWithdrawalStatusValidator = v.union(
    v.literal(SOLANA_WITHDRAWAL_STATUSES.PENDING),
    v.literal(SOLANA_WITHDRAWAL_STATUSES.SENDING),
    v.literal(SOLANA_WITHDRAWAL_STATUSES.SENT),
    v.literal(SOLANA_WITHDRAWAL_STATUSES.CONFIRMED),
    v.literal(SOLANA_WITHDRAWAL_STATUSES.FAILED)
);
export type SolanaWithdrawalStatus = Infer<typeof solanaWithdrawalStatusValidator>;

/** How a FAILED withdrawal's hold was released — see failAndReleaseHold
 * (withdrawals/solana.ts) for what each value asserts. Persisted on the row (not just
 * passed as a mutation arg) so later code — specifically reinitiateFailedWithdrawal —
 * can tell whether a signed transaction was ever actually verified dead before allowing
 * a second send of the same amount. */
export const SOLANA_RELEASE_SAFETY = {
    NEVER_BROADCAST: 'never_broadcast',
    TRANSPORT_REJECTED: 'transport_rejected',
    VERIFIED_DEAD: 'verified_dead',
    ADMIN_OVERRIDE: 'admin_override',
} as const;
export const solanaReleaseSafetyValidator = v.union(
    v.literal(SOLANA_RELEASE_SAFETY.NEVER_BROADCAST),
    v.literal(SOLANA_RELEASE_SAFETY.TRANSPORT_REJECTED),
    v.literal(SOLANA_RELEASE_SAFETY.VERIFIED_DEAD),
    v.literal(SOLANA_RELEASE_SAFETY.ADMIN_OVERRIDE)
);

/** Where in the pipeline a FAILED withdrawal failed. Diagnostic only — never read by any
 * money-safety decision (that's releaseSafety's job). Pre-send stages ('setup', 'blockhash',
 * 'sign') mean nothing was broadcast; 'send' means the broadcast call itself was provably
 * rejected; 'reconcile' means an on-chain check settled it; 'admin' is a super-user force-fail. */
export const SOLANA_FAILURE_STAGES = {
    SETUP: 'setup',
    BLOCKHASH: 'blockhash',
    SIGN: 'sign',
    SEND: 'send',
    RECONCILE: 'reconcile',
    ADMIN: 'admin',
} as const;
export const solanaFailureStageValidator = v.union(
    v.literal(SOLANA_FAILURE_STAGES.SETUP),
    v.literal(SOLANA_FAILURE_STAGES.BLOCKHASH),
    v.literal(SOLANA_FAILURE_STAGES.SIGN),
    v.literal(SOLANA_FAILURE_STAGES.SEND),
    v.literal(SOLANA_FAILURE_STAGES.RECONCILE),
    v.literal(SOLANA_FAILURE_STAGES.ADMIN)
);
export type SolanaFailureStage = Infer<typeof solanaFailureStageValidator>;
export type SolanaReleaseSafety = Infer<typeof solanaReleaseSafetyValidator>;

// ============================================================================
// Wallet Transaction Types
// ============================================================================
// Each type is specific and references external tables where applicable.
// The wallet transaction ledger stays simple (who, what, when, how much),
// while details live in external tables.

export const WALLET_TRANSACTION_KINDS = {
    // Credits
    CAMPAIGN_PAYOUT: 'campaign_payout',
    ADMIN_CREDIT: 'admin_credit',
    REFERRAL_BONUS: 'referral_bonus',
    PROMOTIONAL_CREDIT: 'promotional_credit',
    REFUND: 'refund',
    // Debits
    ADMIN_DEBIT: 'admin_debit',
    SOLANA_WITHDRAWAL: 'solana_withdrawal',
} as const;

export const walletTransactionTypeValidator = v.union(
    // ===== CREDITS =====
    v.object({
        kind: v.literal(WALLET_TRANSACTION_KINDS.CAMPAIGN_PAYOUT),
        campaignId: v.id('campaigns'),
    }),
    v.object({
        kind: v.literal(WALLET_TRANSACTION_KINDS.ADMIN_CREDIT),
        reason: v.string(),
    }),
    v.object({
        kind: v.literal(WALLET_TRANSACTION_KINDS.REFERRAL_BONUS),
        referralId: v.optional(v.id('referrals')),
    }),
    v.object({
        kind: v.literal(WALLET_TRANSACTION_KINDS.PROMOTIONAL_CREDIT),
        // Future: promotionId: v.id('promotions'),
    }),
    v.object({
        // Not created by any mutation yet — reserved for a future implementation. See the
        // reversalOfTransactionId/reversedByTransactionId pair on the walletTransactions table
        // below for the (separate, already-implemented) admin-reversal lineage mechanism.
        kind: v.literal(WALLET_TRANSACTION_KINDS.REFUND),
        originalTransactionId: v.optional(v.id('walletTransactions')),
        reason: v.string(),
    }),
    // ===== DEBITS =====
    v.object({
        kind: v.literal(WALLET_TRANSACTION_KINDS.ADMIN_DEBIT),
        reason: v.string(),
    }),
    v.object({
        kind: v.literal(WALLET_TRANSACTION_KINDS.SOLANA_WITHDRAWAL),
        solanaWithdrawalId: v.id('solanaWithdrawals'),
    })
    // Future: bank_withdrawal, paypal_withdrawal, etc.
);
export type WalletTransactionType = Infer<typeof walletTransactionTypeValidator>;

// ============================================================================
// Payout Event Types (Audit Logging)
// ============================================================================

export const PAYOUT_EVENT_TYPES = {
    PAYOUT_SUCCESS: 'payout_success',
    PAYOUT_SKIPPED: 'payout_skipped',
    PAYOUT_STARTED: 'payout_started',
    PAYOUT_COMPLETED: 'payout_completed',
    PAYOUT_FAILED: 'payout_failed',
    MANUAL_ADJUSTMENT: 'manual_adjustment',
    TEST_RESET: 'test_reset',
    // A ban hold resolved after the campaign paid out: released = the withheld amount credited late,
    // forfeited = the hold expired and the withheld amount was never paid (lib/banHolds.ts).
    HELD_RELEASED: 'held_released',
    HELD_FORFEITED: 'held_forfeited',
} as const;
export const payoutEventTypeValidator = v.union(
    v.literal(PAYOUT_EVENT_TYPES.PAYOUT_SUCCESS),
    v.literal(PAYOUT_EVENT_TYPES.PAYOUT_SKIPPED),
    v.literal(PAYOUT_EVENT_TYPES.PAYOUT_STARTED),
    v.literal(PAYOUT_EVENT_TYPES.PAYOUT_COMPLETED),
    v.literal(PAYOUT_EVENT_TYPES.PAYOUT_FAILED),
    v.literal(PAYOUT_EVENT_TYPES.MANUAL_ADJUSTMENT),
    v.literal(PAYOUT_EVENT_TYPES.TEST_RESET),
    v.literal(PAYOUT_EVENT_TYPES.HELD_RELEASED),
    v.literal(PAYOUT_EVENT_TYPES.HELD_FORFEITED)
);
export type PayoutEventType = Infer<typeof payoutEventTypeValidator>;

export const PAYOUT_SKIP_REASONS = {
    ALREADY_PAID: 'already_paid',
    BELOW_MINIMUM: 'below_minimum',
    ZERO_EARNINGS: 'zero_earnings',
    USER_NOT_FOUND: 'user_not_found',
    BELOW_MINIMUM_VIEWS: 'below_minimum_views',
    PAGE_UNOWNED: 'page_unowned',
    // Everything this creator earned is on banned pages under an active ban hold — withheld, not paid.
    ON_BAN_HOLD: 'on_ban_hold',
} as const;
export const payoutSkipReasonValidator = v.union(
    v.literal(PAYOUT_SKIP_REASONS.ALREADY_PAID),
    v.literal(PAYOUT_SKIP_REASONS.BELOW_MINIMUM),
    v.literal(PAYOUT_SKIP_REASONS.ZERO_EARNINGS),
    v.literal(PAYOUT_SKIP_REASONS.USER_NOT_FOUND),
    v.literal(PAYOUT_SKIP_REASONS.BELOW_MINIMUM_VIEWS),
    v.literal(PAYOUT_SKIP_REASONS.PAGE_UNOWNED),
    v.literal(PAYOUT_SKIP_REASONS.ON_BAN_HOLD)
);
export type PayoutSkipReason = Infer<typeof payoutSkipReasonValidator>;

// ============================================================================
// Discord webhook audit log
// ============================================================================
// Tracks outbound Discord webhook announcements. Polymorphic by eventType —
// start with campaign payouts; extend as new event types are added.

export const DISCORD_WEBHOOK_EVENT_TYPES = {
    CAMPAIGN_PAYOUT: 'campaign_payout',
} as const;
export const discordWebhookEventTypeValidator = v.union(v.literal(DISCORD_WEBHOOK_EVENT_TYPES.CAMPAIGN_PAYOUT));
export type DiscordWebhookEventType = Infer<typeof discordWebhookEventTypeValidator>;

export const DISCORD_WEBHOOK_STATUSES = {
    SENT: 'sent',
    SKIPPED: 'skipped',
    FAILED: 'failed',
} as const;
export const discordWebhookStatusValidator = v.union(
    v.literal(DISCORD_WEBHOOK_STATUSES.SENT),
    v.literal(DISCORD_WEBHOOK_STATUSES.SKIPPED),
    v.literal(DISCORD_WEBHOOK_STATUSES.FAILED)
);
export type DiscordWebhookStatus = Infer<typeof discordWebhookStatusValidator>;

export const DISCORD_WEBHOOK_SKIP_REASONS = {
    PRIVATE_CAMPAIGN: 'private_campaign',
    HIDDEN_CAMPAIGN: 'hidden_campaign',
    NO_WEBHOOK_TARGETS: 'no_webhook_targets',
    MISSING_DATA: 'missing_data',
} as const;
export const discordWebhookSkipReasonValidator = v.union(
    v.literal(DISCORD_WEBHOOK_SKIP_REASONS.PRIVATE_CAMPAIGN),
    v.literal(DISCORD_WEBHOOK_SKIP_REASONS.HIDDEN_CAMPAIGN),
    v.literal(DISCORD_WEBHOOK_SKIP_REASONS.NO_WEBHOOK_TARGETS),
    v.literal(DISCORD_WEBHOOK_SKIP_REASONS.MISSING_DATA)
);
export type DiscordWebhookSkipReason = Infer<typeof discordWebhookSkipReasonValidator>;

// ============================================================================
// REFRESH INTERVALS
// ============================================================================

// Refresh interval presets for dynamic per-campaign crons
export const ACTIVE_REFRESH_INTERVALS = {
    ONE_HOUR: 3_600_000,
    ONE_AND_HALF_HOURS: 5_400_000,
    TWO_HOURS: 7_200_000,
    TWO_AND_HALF_HOURS: 9_000_000,
    THREE_HOURS: 10_800_000,
} as const;

export const COMPLETED_REFRESH_INTERVALS = {
    SEVEN_DAYS: 604_800_000,
    FOURTEEN_DAYS: 1_209_600_000,
    TWENTY_ONE_DAYS: 1_814_400_000,
    TWENTY_EIGHT_DAYS: 2_419_200_000,
} as const;

export const DEFAULT_ACTIVE_REFRESH_INTERVAL = ACTIVE_REFRESH_INTERVALS.TWO_HOURS;
export const DEFAULT_COMPLETED_REFRESH_INTERVAL = COMPLETED_REFRESH_INTERVALS.SEVEN_DAYS;

// ============================================================================
// Page-Centric Validators
// ============================================================================

// Page identity match strategy
export const PAGE_IDENTITY_MATCH_STRATEGY = {
    PLATFORM_ACCOUNT_ID: 'platform_account_id',
    PLATFORM_SEC_UID: 'platform_sec_uid',
    // Reserved for historical data — no longer produced by identity resolution
    USERNAME_FALLBACK: 'username_fallback',
} as const;
export const pageIdentityMatchStrategyValidator = v.union(
    v.literal(PAGE_IDENTITY_MATCH_STRATEGY.PLATFORM_ACCOUNT_ID),
    v.literal(PAGE_IDENTITY_MATCH_STRATEGY.PLATFORM_SEC_UID),
    v.literal(PAGE_IDENTITY_MATCH_STRATEGY.USERNAME_FALLBACK)
);
export type PageIdentityMatchStrategy = Infer<typeof pageIdentityMatchStrategyValidator>;

// ============================================================================
// Users
// ============================================================================
// Source of truth for the users table shape. Exported so query/mutation
// validators can compose views via `userValidator.fields` spreads instead of
// re-declaring fields.
export const userValidator = v.object({
    email: v.string(),
    username: v.optional(v.string()),
    clerkUserId: v.string(),
    roles: v.optional(v.array(roleValidator)),
    walletAddress: v.optional(v.string()), // DEPRECATED: Use savedWalletAddresses table instead
    profileImageKey: v.optional(v.string()), // R2 key for profile picture
    coverImageKey: v.optional(v.string()), // R2 key for cover image
    bio: v.optional(v.string()), // User bio text
    isPrivate: v.optional(v.boolean()), // Private profile - hides public profile, anonymizes in leaderboards
    // Optional Telegram username for priority support — lets promote staff contact creators directly (DEV-54).
    // Stored without a leading "@" (stripped on write). Admin-display side is DEV-53.
    telegramUsername: v.optional(v.string()),
    // Onboarding: set once the creator has seen the panel intro tutorial (DEV-50).
    // Absent/false => show the tutorial; true => already seen. Persisted server-side so it holds across devices.
    hasSeenTutorial: v.optional(v.boolean()),
    // Denormalized role fields for efficient filtering (source of truth is roles array)
    isAdmin: v.optional(v.boolean()),
    isModerator: v.optional(v.boolean()),
    isSuperUser: v.optional(v.boolean()),
    // Denormalized field for efficient filtering of users with usernames
    hasUsername: v.optional(v.boolean()),
    // Combined search text: _id, username, email for full-text search
    searchText: v.optional(v.string()),
    // Denormalized global stats (updated via triggers for O(1) reads)
    globalApprovedPosts: v.optional(v.number()),
    globalTotalViews: v.optional(v.number()),
    globalTotalFollowers: v.optional(v.number()),
    globalTotalPosts: v.optional(v.number()),
    globalCampaignsJoined: v.optional(v.number()),
    // Denormalized wallet metrics for admin users table sorting/filtering
    walletBalanceCents: v.optional(v.number()),
    walletReservedCents: v.optional(v.number()),
    walletUpdatedAt: v.optional(v.number()),
    hasPositiveWalletBalance: v.optional(v.boolean()),
    walletTotalMadeCents: v.optional(v.number()),
    walletTotalWithdrawnCents: v.optional(v.number()),
    // Denormalized connected-account metrics for admin users table sorting/filtering
    verifiedConnectedAccountsCount: v.optional(v.number()),
    hasVerifiedConnectedAccount: v.optional(v.boolean()),
    // Dirty flag for periodic user stats sync (set by triggers, cleared by cron)
    needsStatsSync: v.optional(v.boolean()),
    // --- Clerk sync: security posture (mirrored from Clerk UserJSON via webhook) ---
    twoFactorEnabled: v.optional(v.boolean()),
    totpEnabled: v.optional(v.boolean()),
    backupCodeEnabled: v.optional(v.boolean()),
    hasPassword: v.optional(v.boolean()),
    passwordLastUpdatedAt: v.optional(v.number()),
    // Fields Clerk ships in the webhook payload but doesn't declare on UserJSON.
    // Populated via defensive runtime extraction in `upsertFromClerk` (see users.ts).
    hasPasskey: v.optional(v.boolean()),
    passkeyCount: v.optional(v.number()),
    mfaEnabledAt: v.optional(v.number()),
    mfaDisabledAt: v.optional(v.number()),
    // --- Clerk sync: account state ---
    clerkBanned: v.optional(v.boolean()),
    clerkLocked: v.optional(v.boolean()),
    clerkLockoutExpiresAt: v.optional(v.number()),
    lastSignInAt: v.optional(v.number()),
    lastActiveAt: v.optional(v.number()),
    // --- Clerk sync: provenance / OAuth ---
    primaryOAuthProvider: v.optional(v.string()),
    externalAccountCount: v.optional(v.number()),
    // --- Clerk sync: profile ---
    firstName: v.optional(v.string()),
    lastName: v.optional(v.string()),
    legalAcceptedAt: v.optional(v.number()),
    // --- Clerk sync: source timestamps / verification metadata ---
    clerkCreatedAt: v.optional(v.number()),
    clerkUpdatedAt: v.optional(v.number()),
    primaryEmailVerificationStrategy: v.optional(v.string()),
    // Resend contact UUID, populated lazily by createOrUpdateContact / updateContactEmail
    // to skip the get-by-email round-trip on subsequent updates.
    resendContactId: v.optional(v.string()),
    // Flag — lightweight staff moderation signal on a user ("watch out for this account", DEV-55).
    // Mirrors the page flag fields; reuses pageFlagHistoryEntryValidator as the shared shape.
    isFlagged: v.optional(v.boolean()),
    flagLevel: v.optional(flagLevelValidator),
    flagReason: v.optional(v.string()),
    flaggedBy: v.optional(v.id('users')),
    flaggedAt: v.optional(v.number()),
    flagHistory: v.optional(v.array(pageFlagHistoryEntryValidator)),
    // Global moderation status (platform-wide ban). Independent of pages.globalStatus —
    // banning a user does not auto-ban their pages (see admin/userCascade.ts for the opt-in cascade).
    globalStatus: v.optional(userGlobalStatusValidator),
    globalStatusHistory: v.optional(v.array(userGlobalStatusHistoryEntryValidator)),
});

// ============================================================================
// Public Campaign API keys
// ============================================================================

// 'grants': a brand key (/v1), admin-issued, reading exactly the campaigns granted to it.
// 'integration': an internal-systems key (/internal/v1, integrationApi/), super-user-issued, with no
// grants: it reads every page, campaign, submission and user contact, and writes page labels.
// A future self-serve brand mode would widen this union again.
export const API_KEY_MODES = {
    GRANTS: 'grants',
    INTEGRATION: 'integration',
} as const;
export const apiKeyModeValidator = v.union(v.literal(API_KEY_MODES.GRANTS), v.literal(API_KEY_MODES.INTEGRATION));
export type ApiKeyMode = Infer<typeof apiKeyModeValidator>;

/**
 * How long a key lasts, as the admin chooses it. The server turns the choice into a timestamp with its
 * own clock (publicApi/manage.ts), so a skewed browser clock can't shorten or break it. 'never' is
 * super-user only.
 */
export const API_KEY_LIFETIMES = { '90d': 90, '1y': 365 } as const;
export const apiKeyExpiryValidator = v.union(v.literal('90d'), v.literal('1y'), v.literal('never'));

export const API_KEY_AUDIT_ACTIONS = {
    CREATED: 'created',
    REVOKED: 'revoked',
    GRANT_ADDED: 'grant_added',
    GRANT_REMOVED: 'grant_removed',
    // Name, client or expiry changed (the note lists what); grant changes get their own entries.
    UPDATED: 'updated',
    // On the old key: a replacement was issued (publicApi/manage.ts rotateApiKey); the note says
    // which key and when this one stops working.
    ROTATED: 'rotated',
} as const;
export const apiKeyAuditActionValidator = v.union(
    v.literal(API_KEY_AUDIT_ACTIONS.CREATED),
    v.literal(API_KEY_AUDIT_ACTIONS.REVOKED),
    v.literal(API_KEY_AUDIT_ACTIONS.GRANT_ADDED),
    v.literal(API_KEY_AUDIT_ACTIONS.GRANT_REMOVED),
    v.literal(API_KEY_AUDIT_ACTIONS.UPDATED),
    v.literal(API_KEY_AUDIT_ACTIONS.ROTATED)
);

/**
 * When a rotated key stops working: right away (revoked), or after an overlap so the client can
 * switch to the new key without downtime (the old key's expiry is brought forward).
 */
export const API_KEY_ROTATION_OVERLAPS = { now: 0, '1h': 60 * 60 * 1000, '24h': 24 * 60 * 60 * 1000, '7d': 7 * 24 * 60 * 60 * 1000 } as const;
export const apiKeyRotationOverlapValidator = v.union(v.literal('now'), v.literal('1h'), v.literal('24h'), v.literal('7d'));

/** Headline status of one email in the account-wide log — see resend/emailEvents.ts. */
export const emailMessageStatusValidator = v.union(
    v.literal('sent'),
    v.literal('delivery_delayed'),
    v.literal('delivered'),
    v.literal('opened'),
    v.literal('clicked'),
    v.literal('failed'),
    v.literal('suppressed'),
    v.literal('bounced'),
    v.literal('complained')
);

/**
 * A recipient variable in a personalized send ({{ promote.username }}, optionally with its own
 * fallback). The send's content holds numbered placeholders; slot N says what fills placeholder N
 * for each recipient — see packages/email-templates/src/variables.ts.
 */
export const recipientSlotValidator = v.object({
    key: v.union(v.literal('promote.username'), v.literal('promote.first_name')),
    fallback: v.optional(v.string()),
});

/** Headline status of one recipient of a labeled send — see resend/sendTrackingModel.ts. */
export const sendRecipientStatusValidator = v.union(
    v.literal('queued'),
    v.literal('not_sent'),
    v.literal('suppressed'),
    v.literal('rejected'),
    v.literal('sent'),
    v.literal('delivered'),
    v.literal('opened'),
    v.literal('clicked'),
    v.literal('failed'),
    v.literal('bounced'),
    v.literal('unsubscribed'),
    v.literal('complained')
);

export default defineSchema({
    // Core users table (Clerk-backed)
    users: defineTable(userValidator)
        .index('by_clerkUserId', ['clerkUserId'])
        .index('by_username', ['username'])
        .index('by_email', ['email'])
        .index('by_resendContactId', ['resendContactId'])
        .index('by_isPrivate', ['isPrivate'])
        .index('by_isPrivate_and_hasUsername', ['isPrivate', 'hasUsername'])
        .index('by_isPrivate_hasUsername_followers', ['isPrivate', 'hasUsername', 'globalTotalFollowers'])
        .index('by_isPrivate_hasUsername_posts', ['isPrivate', 'hasUsername', 'globalApprovedPosts'])
        .index('by_isPrivate_hasUsername_views', ['isPrivate', 'hasUsername', 'globalTotalViews'])
        .index('by_isPrivate_hasUsername_totalEarnings', ['isPrivate', 'hasUsername', 'walletTotalMadeCents'])
        .index('by_isAdmin', ['isAdmin'])
        .index('by_isModerator', ['isModerator'])
        .index('by_isSuperUser', ['isSuperUser'])
        .index('by_isAdmin_twoFactorEnabled', ['isAdmin', 'twoFactorEnabled'])
        .index('by_isSuperUser_hasPasskey', ['isSuperUser', 'hasPasskey'])
        .index('by_clerkBanned', ['clerkBanned'])
        .index('by_hasPositiveWalletBalance', ['hasPositiveWalletBalance'])
        .index('by_hasVerifiedConnectedAccount', ['hasVerifiedConnectedAccount'])
        // Global sorts (all users segment)
        .index('by_walletBalanceCents', ['walletBalanceCents'])
        .index('by_walletTotalMadeCents', ['walletTotalMadeCents'])
        .index('by_walletTotalWithdrawnCents', ['walletTotalWithdrawnCents'])
        .index('by_globalTotalViews', ['globalTotalViews'])
        .index('by_globalTotalFollowers', ['globalTotalFollowers'])
        .index('by_globalApprovedPosts', ['globalApprovedPosts'])
        .index('by_needsStatsSync', ['needsStatsSync'])
        .index('by_profileImageKey', ['profileImageKey'])
        .index('by_coverImageKey', ['coverImageKey'])
        .index('by_isFlagged', ['isFlagged'])
        .index('by_globalStatus', ['globalStatus'])
        .searchIndex('search_users', {
            searchField: 'searchText',
            filterFields: ['isAdmin', 'isModerator', 'isSuperUser', 'hasPositiveWalletBalance', 'hasVerifiedConnectedAccount'],
        })
        .searchIndex('search_usernames', {
            searchField: 'username',
            filterFields: ['isPrivate', 'hasUsername'],
        }),

    // Wallets (USD in cents) - 1 wallet per user (enforced by code)
    wallets: defineTable({
        userId: v.id('users'),
        currency: walletCurrencyValidator,
        balanceCents: v.number(),
        reservedCents: v.optional(v.number()),
        updatedAt: v.number(),
    }).index('by_userId', ['userId']),

    // Immutable wallet transaction log (audit trail)
    walletTransactions: defineTable({
        walletId: v.id('wallets'),
        userId: v.id('users'),
        currency: walletCurrencyValidator,
        direction: walletTransactionDirectionValidator,
        amountCents: v.number(), // positive integer cents; enforced in mutations
        type: walletTransactionTypeValidator,
        idempotencyKey: v.string(),
        createdByUserId: v.optional(v.id('users')),
        note: v.optional(v.string()),
        // Denormalized flag — true only for admin_credit/admin_debit rows (set at write time in
        // applyWalletTransactionInSameTx, derived from type.kind). Lets the Admin Adjustments feed
        // (admin Wallet page) find these without scanning the whole table — walletTransactions is dominated by
        // campaign_payout rows, and createdByUserId alone isn't a reliable "admin action" signal
        // (it's also set to the wallet owner on system-generated solana_withdrawal rows).
        isAdminInitiated: v.optional(v.boolean()),
        // Search text for admin lookup — only populated for admin_credit/admin_debit rows (see
        // the searchText trigger in _infra/triggers.ts), same pattern as solanaWithdrawals.searchText.
        searchText: v.optional(v.string()),
        // Set when THIS row is a reversal of another admin_credit/admin_debit row (see
        // admin/wallet.ts:reverseAdjustment). Top-level (not nested in `type`) — deliberately a
        // separate mechanism from refund.type.originalTransactionId above: refund is an
        // unimplemented general "money given back" kind (no mutation creates one yet), while this
        // pair is specific to "a super user manually reversing their own admin_credit/admin_debit
        // correction." If refund is ever implemented, consider whether it should reuse this pair
        // instead of introducing a third lineage mechanism. No index needed — always read via a
        // direct doc lookup (the calling mutation already has the doc in hand), never queried by value.
        reversalOfTransactionId: v.optional(v.id('walletTransactions')),
        // Set on the ORIGINAL row when it's been reversed, pointing at the reversal (the inverse of
        // reversalOfTransactionId above). Denormalized here — rather than queried on read — so the
        // Admin Adjustments feed can compute "has this been reversed" with zero extra reads per row.
        reversedByTransactionId: v.optional(v.id('walletTransactions')),
    })
        .index('by_walletId', ['walletId'])
        .index('by_userId', ['userId'])
        .index('by_idempotencyKey', ['idempotencyKey'])
        .index('by_isAdminInitiated', ['isAdminInitiated'])
        .searchIndex('search_wallet_transactions', {
            searchField: 'searchText',
            filterFields: ['isAdminInitiated'],
        }),

    // Solana USDC withdrawals (external payout rail lifecycle)
    solanaWithdrawals: defineTable({
        userId: v.id('users'),
        walletId: v.id('wallets'),
        cluster: solanaClusterValidator,
        destinationAddress: v.string(),
        mintAddress: v.string(),
        amountCents: v.number(),
        amountUsdcMicros: v.int64(),
        status: solanaWithdrawalStatusValidator,
        idempotencyKey: v.string(),
        txSignature: v.optional(v.string()),
        error: v.optional(v.string()),
        errorDetails: v.optional(v.string()), // JSON-encoded debug details
        // Set only when status is FAILED via failAndReleaseHold. 'admin_override' means the
        // signed row was released WITHOUT on-chain proof — reinitiateFailedWithdrawal refuses
        // to re-send in that case until it's manually re-verified.
        releaseSafety: v.optional(solanaReleaseSafetyValidator),
        // Set only when status is FAILED via failAndReleaseHold — see SOLANA_FAILURE_STAGES.
        failureStage: v.optional(solanaFailureStageValidator),
        // How many times markSending has claimed this row. Each SENDING timeout carries the attempt
        // it was scheduled for, and a release from an older attempt is refused — so once a row can
        // be sent more than once (pre-sign retry), a stale timeout can't settle a newer attempt.
        // Absent on rows claimed before it existed.
        sendAttempt: v.optional(v.number()),
        // Pre-sign retry (withdrawals/preSignRetry.ts): how many times this withdrawal has been
        // handed back to PENDING after an RPC rate limit hit before anything was signed. Kept after
        // it finishes, for reporting. Absent = never retried.
        autoRetryAttempt: v.optional(v.number()),
        // When the next attempt is due. Set only while the row waits in PENDING for a retry;
        // cleared when markSending claims it. The UI reads "PENDING + autoRetryAt" as "retrying".
        autoRetryAt: v.optional(v.number()),
        // The user-facing message and debug details of the last attempt that was retried. Not
        // `error`/`errorDetails`, which mean the withdrawal FAILED.
        lastAttemptError: v.optional(v.string()),
        lastAttemptErrorDetails: v.optional(v.string()),
        createdAt: v.number(),
        updatedAt: v.number(),
        confirmedAt: v.optional(v.number()),
        finalizedWalletTransactionId: v.optional(v.id('walletTransactions')),
        // Denormalized search text: _id, destinationAddress, txSignature, user email/username
        searchText: v.optional(v.string()),
        // Re-initiate audit trail (super-user only). Always points at the TRUE
        // original — never at an intermediate retry — so the chain stays flat and
        // the retry cap (admin/withdrawals.ts) is a single indexed count. Set only
        // on a retry row; absent on a normal user-initiated withdrawal.
        retryOfWithdrawalId: v.optional(v.id('solanaWithdrawals')),
        initiatedBy: v.optional(v.id('users')),
        retryReason: v.optional(v.string()),
    })
        .index('by_userId', ['userId'])
        .index('by_userId_and_status', ['userId', 'status'])
        .index('by_walletId', ['walletId'])
        .index('by_status', ['status'])
        .index('by_idempotencyKey', ['idempotencyKey'])
        .index('by_destinationAddress', ['destinationAddress'])
        .index('by_txSignature', ['txSignature'])
        .index('by_retryOfWithdrawalId', ['retryOfWithdrawalId'])
        // Payouts confirmed since a given time — the hot wallet capacity check subtracts those
        // its cached balance doesn't reflect yet (withdrawals/hotWalletCapacity.ts).
        .index('by_status_and_confirmedAt', ['status', 'confirmedAt'])
        .searchIndex('search_withdrawals', {
            searchField: 'searchText',
            filterFields: ['status'],
        }),

    // Hot wallet balance cache (singleton per cluster)
    // Used to track Solana hot wallet balances and prevent withdrawal requests
    // that would fail due to insufficient hot wallet capacity.
    hotWalletBalance: defineTable({
        // There should only ever be one row per cluster (singleton pattern)
        cluster: solanaClusterValidator,
        address: v.string(),
        // Balances in micro-units (lamports for SOL, micro-USDC for USDC)
        solLamports: v.int64(),
        usdcMicros: v.int64(),
        // Reserved amounts (in-flight withdrawals)
        reservedUsdcMicros: v.optional(v.int64()),
        // Sync metadata
        lastSyncedAt: v.number(),
        // When the chain read behind usdcMicros/solLamports began (lastSyncedAt is when it was
        // written, which can be ~a minute later under RPC contention). Payouts confirmed after
        // this aren't reflected in usdcMicros yet — see withdrawals/hotWalletCapacity.ts. Also
        // orders syncs: a read older than this one never overwrites it. Absent on rows last
        // synced before it existed.
        balanceReadAt: v.optional(v.number()),
        updatedAt: v.number(),
        // Sync lock for request coalescing (prevents concurrent RPC calls)
        syncInProgress: v.optional(v.boolean()),
        syncStartedAt: v.optional(v.number()),
    }).index('by_cluster', ['cluster']),

    // Shared, short-TTL blockhash cache (singleton per cluster). Lets consecutive
    // withdrawals processed within the TTL window reuse one getLatestBlockhash result
    // instead of each fetching fresh — the single most frequent avoidable RPC call
    // against the public RPC's scarce budget. Same lock-acquire/wait/refresh pattern as
    // hotWalletBalance above (see withdrawals/blockhashCache.ts).
    solanaBlockhashCache: defineTable({
        cluster: solanaClusterValidator,
        blockhash: v.string(),
        lastValidBlockHeight: v.int64(),
        fetchedAt: v.number(),
        refreshInProgress: v.optional(v.boolean()),
        refreshStartedAt: v.optional(v.number()),
    }).index('by_cluster', ['cluster']),

    // Withdrawal configuration (singleton per cluster)
    // Controls whether withdrawals are enabled and optional min/max limits.
    withdrawalConfig: defineTable({
        // There should only ever be one row per cluster (singleton pattern)
        cluster: solanaClusterValidator,
        // Master toggle for withdrawals
        isEnabled: v.boolean(),
        // Optional limits in cents (null/undefined = no limit)
        minAmountCents: v.optional(v.number()),
        maxAmountCents: v.optional(v.number()),
        // Custom message shown when withdrawals are disabled
        disabledMessage: v.optional(v.string()),
        // Hot wallet low-balance alert threshold, in micro-USDC (matches
        // hotWalletBalance.usdcMicros units). undefined = no alarm configured.
        hotWalletLowThresholdUsdcMicros: v.optional(v.int64()),
        // Same alarm for SOL, which pays network fees and new-recipient account rent, in
        // lamports (matches hotWalletBalance.solLamports). undefined = no SOL alarm configured.
        hotWalletLowThresholdSolLamports: v.optional(v.int64()),
        // Audit fields
        updatedAt: v.number(),
        updatedBy: v.optional(v.id('users')),
    }).index('by_cluster', ['cluster']),

    // Low-balance email alert for the hot wallet: who gets it, and where the alert stands. One row
    // per cluster. Its own table, not withdrawalConfig, so saving withdrawal limits can't wipe it.
    // See withdrawals/hotWalletAlert.ts.
    hotWalletAlert: defineTable({
        cluster: solanaClusterValidator,
        // Staff picked on the Wallet page. Roles are re-checked at send time: a demoted user is
        // skipped, and each recipient's current role picks their email (super user: full details;
        // admin: status only).
        recipientUserIds: v.array(v.id('users')),
        status: v.union(v.literal('ok'), v.literal('low')),
        statusSince: v.number(),
        // When this low episode's first email went out. Unset = not emailed yet (still debouncing,
        // no recipients, or sends off), so it goes out as soon as it can; recovery is only emailed
        // for an episode that was.
        lowEmailSentAt: v.optional(v.number()),
        lastEmailedAt: v.optional(v.number()),
        // Bumped on every write — the send commits only if nothing changed since it was planned.
        version: v.number(),
        updatedAt: v.number(),
        recipientsUpdatedBy: v.optional(v.id('users')),
    }).index('by_cluster', ['cluster']),

    // Saved wallet addresses for withdrawals (user address book)
    savedWalletAddresses: defineTable({
        userId: v.id('users'),
        label: v.string(), // User-friendly name (e.g., "My Phantom", "Exchange")
        address: v.string(), // Solana address
        isDefault: v.optional(v.boolean()), // Mark one as default
        createdAt: v.number(),
        updatedAt: v.number(),
    })
        .index('by_userId', ['userId'])
        .index('by_userId_and_address', ['userId', 'address']),

    // Shared team-wide saved denial reasons, offered as clickable presets in deny dialogs.
    // Split into separate lists per category ('submission' = video denials, 'page' = page
    // denials, 'account' = creator/connected-account denials) so a video denial reason
    // doesn't clutter the page-ban list and vice versa. Admin/super_user create and delete;
    // moderator-and-above can view and select (see admin/denialReasonPresets.ts).
    // createdBy is attribution only, not an access-control scope — the list is shared.
    denialReasonPresets: defineTable({
        createdBy: v.id('users'),
        category: v.optional(v.union(v.literal('submission'), v.literal('page'), v.literal('account'))),
        title: v.string(),
        text: v.string(),
    }).index('by_category', ['category']),

    connectedAccounts: defineTable({
        userId: v.id('users'),
        platform: socialPlatformValidator,
        status: connectedAccountStatusValidator,
        // @deprecated — use `username` instead. Kept for existing documents.
        accountId: v.optional(v.string()),
        // @deprecated — use `username` instead. Kept for existing documents.
        accountUsername: v.optional(v.string()),
        // Current normalized username (lowercase, no @ prefix). Updated on every successful sync.
        username: v.optional(v.string()),
        // Stable platform identifier (does not change when username/handle changes).
        // - Instagram: profile `data.id` / post `owner.id`
        // - TikTok: profile `data.user.id` / post `author.id`
        // - YouTube: channelId
        platformAccountId: v.optional(v.string()),
        // TikTok secondary stable identifier (useful for reconciliation when available).
        platformSecUid: v.optional(v.string()),
        // @deprecated — data now owned by pages table. Kept for existing documents.
        accountData: v.optional(
            v.object({
                followersCount: v.optional(v.number()),
                videosCount: v.optional(v.number()),
                totalViews: v.optional(v.number()),
            })
        ),
        verifiedAt: v.optional(v.number()),
        rejectedAt: v.optional(v.number()),
        rejectedReason: v.optional(v.string()),
        // Bio verification fields
        verificationCode: v.optional(v.string()),
        verificationCodeExpiresAt: v.optional(v.number()),
        verificationHash: v.optional(v.string()),
        // Sync state is normally owned by the pages table, BUT a page-less CA
        // (first sync, or a cross-user collision detected before a page exists)
        // records its failure here so the pending row shows a visible cause.
        // Actively written by handleSyncResult — do NOT drop in a cleanup migration.
        fetchError: v.optional(v.string()),
        // @deprecated — sync state now owned by pages table. Kept for existing documents.
        lastFetchedAt: v.optional(v.number()),
        // @deprecated — sync state now owned by pages table. Kept for existing documents.
        fetchStatus: v.optional(v.string()),
        // @deprecated — sync state now owned by pages table. Kept for existing documents.
        lastFetchAttemptedAt: v.optional(v.number()),
        // @deprecated — sync state now owned by pages table. Kept for existing documents.
        lastSuccessfulFetchedAt: v.optional(v.number()),
        // Canonical page identity link (populated by page identity resolution)
        pageId: v.optional(v.id('pages')),
        // Soft disconnect fields (replaces hard delete)
        isDisconnected: v.optional(v.boolean()),
        disconnectedAt: v.optional(v.number()),
        disconnectedBy: v.optional(v.id('users')),
    })
        .index('by_userId', ['userId'])
        .index('by_userId_and_status', ['userId', 'status'])
        .index('by_platform', ['platform'])
        .index('by_status', ['status'])
        .index('by_userId_and_platform', ['userId', 'platform'])
        .index('by_platform_and_platformAccountId', ['platform', 'platformAccountId'])
        .index('by_platform_and_username', ['platform', 'username'])
        .index('by_userId_and_platform_and_username', ['userId', 'platform', 'username'])
        .index('by_userId_and_platform_and_platformAccountId', ['userId', 'platform', 'platformAccountId'])
        .index('by_pageId', ['pageId'])
        .index('by_userId_and_isDisconnected', ['userId', 'isDisconnected'])
        .index('by_platform_and_username_and_isDisconnected', ['platform', 'username', 'isDisconnected'])
        .index('by_pageId_and_isDisconnected', ['pageId', 'isDisconnected'])
        // A page's verified accounts without reading its pending ones (a celebrity handle collects
        // many). See lib/pageIdentity.ts:listActiveVerifiedAccountsForPage.
        .index('by_pageId_and_status', ['pageId', 'status']),

    // One row per write made by migrations/stuckConnectUsernames.ts — the rollback record (the
    // unmasked old username lives only here) and how the analytics trigger tells the repair's
    // soft-disconnects apart from a user's. Written in the same transaction as the write itself.
    connectUsernameRepairs: defineTable({
        accountId: v.id('connectedAccounts'),
        userId: v.id('users'),
        platform: socialPlatformValidator,
        action: v.union(v.literal('rewrite'), v.literal('disconnect')),
        reason: v.optional(v.union(v.literal('duplicate'), v.literal('replaced'))),
        oldUsername: v.string(),
        newUsername: v.optional(v.string()),
        // For a disconnect, also the `disconnectedAt` written on the account.
        appliedAt: v.number(),
        revertedAt: v.optional(v.number()),
    }).index('by_accountId', ['accountId']),

    // One row per owner change made by migrations/pageOwnershipRepair.ts — the rollback record.
    // `unassign` clears an owner with no verified account on the page; `restore` hands the page
    // back to the one verified user still linked to it. Written in the same transaction as the patch.
    pageOwnershipRepairs: defineTable({
        pageId: v.id('pages'),
        action: v.union(v.literal('unassign'), v.literal('restore')),
        reason: pageOwnershipRepairReasonValidator,
        oldOwnerUserId: v.id('users'),
        newOwnerUserId: v.optional(v.id('users')),
        followersCount: v.number(),
        appliedAt: v.number(),
        revertedAt: v.optional(v.number()),
    }).index('by_pageId', ['pageId']),

    // One row per creator whose pending-earnings counter migrations/pendingEarningsCorrection.ts changed,
    // or skipped because it was still moving — the record of what was corrected and how to undo it (add
    // back -deltaCents). Written in the same transaction as the correction itself.
    pendingEarningsCorrections: defineTable({
        runId: v.string(),
        userId: v.id('users'),
        actualCents: v.number(),
        expectedCents: v.number(),
        deltaCents: v.number(),
        applied: v.boolean(),
        skipReason: v.optional(pendingCorrectionSkipReasonValidator),
    })
        .index('by_runId', ['runId'])
        .index('by_userId', ['userId']),

    // Global page refresh workflow tracking.
    // Used to prevent duplicate global runs and expose operational state.
    pageRefreshWorkflows: defineTable({
        scope: v.string(),
        workflowId: v.string(),
        startedAt: v.number(),
        startedBy: v.optional(v.id('users')),
    })
        .index('by_scope', ['scope'])
        .index('by_workflowId', ['workflowId']),

    // Campaigns table
    campaigns: defineTable({
        creatorId: v.id('users'),
        // When true, public surfaces should not reveal the real campaign owner identity.
        // Management/admin surfaces can still access creatorId directly from the table.
        isCreatorHidden: v.optional(v.boolean()),
        title: v.string(),
        // Second half of the campaign name ("Title: Subtitle"). Hidden from creators until they are
        // accepted on the campaign; owners and staff always see it. See lib/campaignName.ts.
        subtitle: v.optional(v.string()),
        // Title + subtitle, for the admin search index. Maintained by the campaigns trigger in
        // _infra/triggers.ts — never patch directly. Optional only until the backfill has run
        // (migrations/campaignSearchText.ts).
        searchText: v.optional(v.string()),
        description: v.string(),
        status: campaignStatusValidator,
        isAcceptingMembers: v.optional(v.boolean()),
        isAcceptingSubmissions: v.optional(v.boolean()),
        batchRefreshActive: v.optional(v.boolean()),
        // Admin-controlled earnings freeze (locks earnings at current values)
        isEarningsFrozen: v.optional(v.boolean()),
        earningsFrozenAt: v.optional(v.number()),
        earningsFrozenBy: v.optional(v.id('users')),
        // Auto-freeze state (budget milestones)
        earningsAutoFreezeState: v.optional(earningsAutoFreezeStateValidator),
        earningsFreezeReason: v.optional(earningsFreezeReasonValidator),
        // Manual override for budget-based auto-pause (DEPRECATED: kept for migration)
        budgetPauseOverride: v.optional(v.boolean()),
        // Creator payout cap: percentage of totalBudget allocated to creator payouts (default 100)
        creatorPayoutCapPercent: v.optional(v.number()),
        // Cosmetic flag: when true, public queries show budget as fully spent
        // Auto-set when capped earnings reach creatorBudgetCents ceiling
        isBudgetDisplayedAsExhausted: v.optional(v.boolean()),
        // True when a super user turned the flag on by hand (toggleCosmeticExhaustion); automatic
        // resets (budget edit, rebuild with budget left) then leave it alone.
        isBudgetExhaustedSetManually: v.optional(v.boolean()),
        // Snapshot-based earnings: admin selects a cutoff time, earnings use view counts from that point
        earningsSnapshotCutoffAt: v.optional(v.number()), // Unix ms — views at this time used for earnings
        earningsSnapshotSetBy: v.optional(v.id('users')), // Admin who set it
        earningsSnapshotSetAt: v.optional(v.number()), // When selection was made
        earningsSnapshotTimelineId: v.optional(v.id('campaignEarningsTimeline')), // Direct ref to the timeline entry used
        // When the CURRENT lock episode began — the moment the campaign stopped being live (first
        // live -> locked transition, by either lever: snapshot cutoff or manual freeze). Written
        // once per episode and cleared when the campaign goes live again, so unlike
        // earningsSnapshotSetAt (re-stamped on every cutoff edit) it is a stable answer to "was the
        // campaign live when X happened". Drives settlement scoping — see getSettlementHorizon.
        earningsLockedAt: v.optional(v.number()),
        autoApproveMembers: v.optional(v.boolean()), // Auto-approve members, default false
        isPrivate: v.optional(v.boolean()), // Private mode - requires password to join
        isHidden: v.optional(v.boolean()), // Hidden from home page and user profiles (admin toggle)
        password: v.optional(v.string()), // Password for private campaigns
        slug: v.optional(v.string()), // URL-friendly identifier
        imageKey: v.optional(v.string()), // R2 image key
        // DEPRECATED: Keep for migration, then remove
        rules: v.optional(v.string()),
        // NEW: Structured campaign rules
        campaignRules: v.optional(campaignRulesValidator),
        // Structured Overview content blocks (What to Post, Talking Points, Must Include, Avoid,
        // Accepted Pages) — see campaignContentValidator's doc comment for why this is a sibling
        // field rather than nested under campaignRules.
        campaignContent: v.optional(campaignContentValidator),
        // NEW: Demographics proof requirement
        demographicsProofRequirement: v.optional(demographicsProofRequirementValidator),
        totalBudget: v.number(),
        costPer1kViews: v.number(),
        acceptedPlatforms: v.array(socialPlatformValidator),
        socials: v.optional(
            v.array(
                v.object({
                    platform: socialPlatformValidator,
                    username: v.string(),
                })
            )
        ),
        // Free-text content niches shown to creators as badges (lib/campaignNiches.ts). Informational only.
        acceptedNiches: v.optional(v.array(v.string())),
        startDate: v.optional(v.number()),
        endDate: v.optional(v.number()),
        // Payout tracking - marks when campaign earnings have been paid out to user wallets
        isPaidOut: v.optional(v.boolean()),
        paidOutAt: v.optional(v.number()),
        paidOutBy: v.optional(v.id('users')),
        paidOutNote: v.optional(v.string()), // For legacy campaigns marked as paid without wallet credits
        // Payout workflow tracking - prevents concurrent payout attempts
        payoutInProgress: v.optional(v.boolean()),
        payoutWorkflowId: v.optional(v.string()), // Convex Workflow ID for tracking
        // True from the moment a payout marks the campaign paid out until decrementPendingEarningsBatch has
        // stamped every skipped creator's row (admin/payouts.ts). It also changes counting: while set, an
        // unstamped row still counts as pending earnings and leaves when stamped; once cleared (or never set,
        // on campaigns paid out before the field existed) the campaign's unstamped rows count as out
        // (lib/pendingEarnings.ts trackedPendingCents).
        pendingEarningsClearing: v.optional(v.boolean()),
        // How many times this campaign has been emailed, to any audience: its customEmailBroadcasts
        // rows. Maintained by the trigger in `_infra/triggers.ts` — never patch directly. The admin picker's "Sent N×", and the force-on-resend gate in
        // resend/sendQueue.ts queueSend, read it off one doc.
        broadcastCount: v.optional(v.number()),
        // Pinned admin/super-user note shown to moderators above the Pages and Submissions
        // queues — a single current value, not a history log. updatedBy/updatedAt are for
        // "last edited by X" display only, not an audit trail.
        internalNote: v.optional(v.string()),
        internalNoteUpdatedBy: v.optional(v.id('users')),
        internalNoteUpdatedAt: v.optional(v.number()),
        // Denormalized metrics (updated by triggers, sourced from sharded counters)
        metrics: v.optional(
            v.object({
                // Submissions
                totalSubmissions: v.number(),
                approvedSubmissions: v.number(),
                pendingSubmissions: v.number(),
                deniedSubmissions: v.number(),
                approvedViews: v.number(),
                // Members
                totalMembers: v.number(),
                approvedMembers: v.number(),
                pendingMembers: v.number(),
                deniedMembers: v.number(),
                bannedMembers: v.number(),
                activeMembers: v.optional(v.number()),
                // Pages (member accounts)
                totalPages: v.number(),
                approvedPages: v.number(),
                pendingPages: v.number(),
                deniedPages: v.number(),
                // Budget tracking
                totalPotentialCappedEarningsCents: v.number(),
                lastUpdatedAt: v.number(),
            })
        ),
    })
        .index('by_creatorId', ['creatorId'])
        .index('by_totalBudget', ['totalBudget'])
        .index('by_creatorId_and_totalBudget', ['creatorId', 'totalBudget'])
        .index('by_status', ['status'])
        .index('by_status_and_isHidden', ['status', 'isHidden'])
        // Admin campaigns list payout filter; queried with eq(true) only. Rows can also hold false or be unset.
        .index('by_isPaidOut', ['isPaidOut'])
        .index('by_payoutInProgress', ['payoutInProgress'])
        .index('by_status_isHidden_startDate', ['status', 'isHidden', 'startDate'])
        .index('by_status_isHidden_totalBudget', ['status', 'isHidden', 'totalBudget'])
        .index('by_status_isHidden_costPer1kViews', ['status', 'isHidden', 'costPer1kViews'])
        .index('by_broadcastCount', ['broadcastCount'])
        .index('by_slug', ['slug'])
        // Admin search over title + subtitle (searchText), so one index and one cursor cover both.
        .searchIndex('search_campaigns_by_name', {
            searchField: 'searchText',
            filterFields: ['status', 'creatorId', 'isPrivate', 'isHidden'],
        }),

    // Curated "posts that worked" for the Overview tab's Example Posts block. A satellite table,
    // not an array on `campaigns`, because each post needs its own async fetch lifecycle
    // (fetchStatus/lastFetchedError/refreshDueAt) exactly like showcaseSubmissions — patching
    // that per-item onto the campaign doc on every refresh would hammer the hottest document in
    // the system. Reuses the same fetch pipeline as showcase submissions (see
    // campaigns/examplePosts.ts): detectPlatformFromUrl/extractVideoId for parsing,
    // internal.external.<platform>.fetch*Data for the stats fetch.
    campaignExamplePosts: defineTable({
        campaignId: v.id('campaigns'),
        platform: socialPlatformValidator,
        videoId: v.string(), // Platform-specific video ID — dedupe key alongside campaignId
        videoUrl: v.string(),
        videoStats: v.optional(videoStatsValidator),
        fetchStatus: submissionFetchStatusValidator,
        lastFetchedAt: v.optional(v.number()),
        lastFetchedError: v.optional(v.string()),
        refreshDueAt: v.optional(v.number()),
        order: v.number(), // admin/brand-controlled display order, drag-sortable in the editor
        addedBy: v.id('users'),
    })
        .index('by_campaignId_and_order', ['campaignId', 'order'])
        .index('by_campaignId_and_videoId', ['campaignId', 'videoId'])
        // Global (not campaign-scoped) — the refresh cron scans across every campaign's example
        // posts at once. Scoped to fetchStatus first so every row the range-query sees has a real
        // (defined) refreshDueAt — only SUCCESS rows ever get one set. This table is low-volume (a
        // handful of curated posts per campaign, not hundreds like showcaseSubmissions), so a
        // periodic cron over this index is simpler and proportionate to the actual scale — no need
        // for showcase's full Workflow-based refresh orchestration.
        .index('by_fetchStatus_and_refreshDueAt', ['fetchStatus', 'refreshDueAt']),

    // Every email send from the admin /emails surface — audience is 'all' (every user) or a label
    // ('owners'/'connected'), resolved Promote-side and sent in the background by a workflow
    // (resend/sendQueue.ts) as resend.batch.send chunks. Rows with a resendBroadcastId are the
    // old all-users sends made as Resend Broadcasts, kept as history. contentSource distinguishes campaign-launch
    // content sent to a label from fully custom-composed content; campaignId is set
    // only for the former. contentSource is optional only to tolerate a handful of
    // pre-launch rows written before this field existed — always written going forward,
    // read as `?? 'custom'`.
    customEmailBroadcasts: defineTable({
        subject: v.string(),
        bodyHtml: v.string(),
        bodyText: v.string(),
        labelIds: v.array(v.id('pageLabels')),
        audienceMode: v.union(v.literal('all'), v.literal('owners'), v.literal('connected')),
        // What the audience was narrowed to (earners, tiers, never joined); absent when it wasn't.
        segments: v.optional(audienceSegmentsValidator),
        contentSource: v.optional(v.union(v.literal('campaign_launch'), v.literal('custom'))),
        campaignId: v.optional(v.id('campaigns')),
        // Set only on the old all-users sends made as Resend Broadcasts — drives their
        // "View in Resend" link. Never written now.
        resendBroadcastId: v.optional(v.string()),
        // recipientCount is the audience resolved at send start; sentCount is how many
        // actually went out — they diverge when a batch chunk fails partway through.
        // Both are 0 on the old Resend Broadcast rows: Resend held that audience.
        recipientCount: v.number(),
        sentCount: v.number(),
        status: v.union(v.literal('sending'), v.literal('sent'), v.literal('partial'), v.literal('failed')),
        sentAt: v.number(),
        sentBy: v.id('users'),
        // Chunk size used for THIS send: chunk N is recipients [N*chunkSize, (N+1)*chunkSize), and its
        // Resend idempotency key is broadcast/<id>/chunk-N — persisted so a CHUNK_SIZE change in a
        // later deploy can't remap a resumed send's chunks.
        chunkSize: v.optional(v.number()),
        // Which chunks still need sending — THE source of truth for what's outstanding, and
        // what `status` is derived from. Deliberately not inferred from sentCount: chunks can
        // now succeed out of order (a failed chunk no longer aborts the ones after it), so a
        // send can be 390-of-490 with a hole at chunk 2 rather than a clean 390-long prefix.
        // Absent on rows written before this existed, where sentCount WAS a prefix boundary.
        failedChunkIndices: v.optional(v.array(v.number())),
        // Why the most recent failure happened. Previously the provider's error was thrown
        // into a toast and discarded, so a real production partial send left no record of its
        // own cause — the single biggest obstacle to diagnosing the 200-of-490 incident.
        failureMessage: v.optional(v.string()),
        // The provider's machine-readable error name (Resend's RESEND_ERROR_CODE_KEY), kept
        // alongside the message so failures can be classified without parsing prose.
        failureCode: v.optional(v.string()),
        // Recipients the provider refused INSIDE otherwise-successful chunks. Resend's
        // 'permissive' batch mode accepts a batch and reports individual refusals in the
        // response body, so a send can look clean while real people got nothing. Not
        // outstanding work (retrying an address Resend rejected changes nothing), but
        // recorded so a 'sent' row can never quietly overstate its reach.
        rejectedRecipientCount: v.optional(v.number()),
        // ---- Queued sends (resend/sendQueue.ts): the audience is resolved and sent in the background
        // by a workflow, and the recipient list lives in emailSendRecipients (by position), not here.
        // The workflow driving this send — set on every queued send; absent on older rows.
        workflowId: v.optional(v.string()),
        // When every recipient row was written (positions 0..recipientCount-1). Until then nothing
        // is sent, and a retry rebuilds the list from scratch.
        queuedAt: v.optional(v.number()),
        // Which prepareSend run owns the recipient list: bumped as each run starts (beginPrepare), and
        // every list write must carry it, so a run another one replaced can't write. Absent on older rows.
        prepareAttempt: v.optional(v.number()),
        // Chunks of `chunkSize` recipients, by position. Fresh chunks go out in order from
        // `nextChunk`; `retryChunks` are failed chunks a resume is sending again. Outstanding work =
        // failedChunkIndices + retryChunks + [nextChunk, chunkCount). Saved after every batch of
        // chunks; a chunk replayed after a crash skips recipients already recorded as reached.
        chunkCount: v.optional(v.number()),
        nextChunk: v.optional(v.number()),
        retryChunks: v.optional(v.array(v.number())),
        // ---- Personal sends (the campaign launch email builder). The person the email is sent as
        // (From, and Reply-To); absent → RESEND_FROM_ADDRESS. Fixed when queued, so a resumed chunk
        // goes out exactly as it first did.
        fromName: v.optional(v.string()),
        fromEmail: v.optional(v.string()),
        // What fills each {{{PROMOTE_VAR_n}}} placeholder in subject, bodyHtml and bodyText, per
        // recipient (from the username/firstName copied onto their recipient row).
        personalization: v.optional(v.array(recipientSlotValidator)),
        // The saved template the send started from (attribution only; the content above is what
        // went out, edits and all).
        templateId: v.optional(v.id('emailTemplates')),
    })
        .index('by_sentAt', ['sentAt'])
        .index('by_campaignId', ['campaignId'])
        .index('by_contentSource_and_sentAt', ['contentSource', 'sentAt'])
        .index('by_status', ['status']),

    // RECORD ONLY — one row, no code reads or writes it. The one-time import (2 Oct 2026) that
    // copied opt-outs people made in Resend itself (the old Broadcast emails' unsubscribe page)
    // into emailSuppressions before all-users sends moved to our own pipeline: 118,606 contacts
    // checked, 37 opt-outs. Its code was removed once it finished; it's in git history.
    emailOptOutImport: defineTable({
        key: v.literal('current'),
        status: v.union(v.literal('running'), v.literal('done'), v.literal('failed')),
        // Resend's contact-list cursor (the last contact id of the last finished page).
        cursor: v.optional(v.string()),
        // Contacts created after this can't have used a Resend broadcast unsubscribe link (no
        // broadcast was sent after it), so only earlier ones need a per-contact topic check.
        topicCutoff: v.optional(v.number()),
        contactsChecked: v.number(),
        topicChecks: v.number(),
        optOutsFound: v.number(),
        startedAt: v.number(),
        finishedAt: v.optional(v.number()),
        lastError: v.optional(v.string()),
        // Failed attempts at the current page in a row; reset by a page that succeeds.
        consecutiveFailures: v.optional(v.number()),
        // Last sign of life from the runner (a page, a failure, a wait for a send). A 'running' import
        // quiet for longer than the stall window lost its runner, and can be restarted.
        lastActivityAt: v.optional(v.number()),
    }).index('by_key', ['key']),

    // Addresses that must never receive Promote-sent email again. Populated from two
    // sources: Resend's email.bounced (hard bounce) / email.complained webhook events
    // (resend/index.ts's handleEmailEvent), and the label-targeted batch-send unsubscribe
    // link (http.ts's /email-unsubscribe route — the one audience path Resend's own
    // segment/topic unsubscribe machinery never covers, since it isn't a Resend segment).
    // Consulted before every label-targeted send (admin/emails.ts's
    // resolveCustomEmailAudience) so a bounced/complained/unsubscribed address can't be
    // re-mailed through that path. The whole-segment path (resend.broadcasts.create) needs
    // no equivalent check — Resend enforces its own account-level suppression there.
    // ------------------------------------------------------------------------------------
    // In-house send tracking (resend/sendTracking.ts). Every labeled send writes one
    // emailSendRecipients row per recipient; Resend's webhook events and our own unsubscribe link
    // move it through its statuses (resend/sendTrackingModel.ts). Totals live in sharded
    // counters (emailSendStatsCounters) and the hourly timeline in emailSendTimeline, so no page
    // ever counts rows — a send can have ~100k recipients.
    // ------------------------------------------------------------------------------------
    // Saved designs for the campaign launch email builder (admin/emailTemplates.ts), shared by
    // all super users. The editor's own JSON is stored so a design reopens exactly; the HTML is
    // produced in the builder and sanitized on the server with every send.
    emailTemplates: defineTable({
        kind: v.literal('campaign_launch'),
        name: v.string(),
        // Lowercased, trimmed name: names are unique per kind regardless of case.
        nameKey: v.string(),
        fromName: v.string(),
        fromEmail: v.string(),
        subject: v.string(),
        createdBy: v.id('users'),
        updatedBy: v.id('users'),
        updatedAt: v.number(),
    })
        .index('by_kind_and_nameKey', ['kind', 'nameKey'])
        .index('by_kind_and_updatedAt', ['kind', 'updatedAt']),

    // A template's editor documents, apart from its row so listing templates doesn't read them
    // (up to 200 KB each). One per template, written and deleted with it.
    emailTemplateContents: defineTable({
        templateId: v.id('emailTemplates'),
        // TipTap JSON documents, as strings.
        bodyJson: v.string(),
        signatureJson: v.string(),
    }).index('by_templateId', ['templateId']),

    // What each creator has done, for email targeting segments (resend/emailSegments.ts): one row
    // per user who has connected an account, joined a campaign or earned. Recomputed from source
    // when a change could move a segment (marked in userEmailSegmentsDirty, drained by a cron), so
    // up to a few minutes stale. Kept off `users`, which has a history of write contention.
    userEmailSegments: defineTable({
        userId: v.id('users'),
        // Email set, not banned or Clerk-banned: only sendable users match a segment.
        sendable: v.boolean(),
        // Lifetime earnings (paid + pending), floored to what the segments tell apart: 0 nothing,
        // 1 more than $0, else the highest tier reached in cents (10_000 = $100+ … 1_000_000 =
        // $10K+; resend/emailSegmentsModel.ts earningsFloorCentsOf). Floored, not exact, so the row
        // only changes when a creator crosses a boundary. Changing the tiers needs a re-backfill.
        earningsFloorCents: v.number(),
        // Platforms with an approved post that earned > $0, in a campaign that earned > $0.
        earningPlatforms: v.array(socialPlatformValidator),
        connected: v.boolean(),
        joined: v.boolean(),
        connectedNeverJoined: v.boolean(),
        updatedAt: v.number(),
    })
        .index('by_userId', ['userId'])
        .index('by_sendable_and_earningsFloorCents', ['sendable', 'earningsFloorCents'])
        .index('by_sendable_and_connectedNeverJoined', ['sendable', 'connectedNeverJoined']),

    // Users whose segment row needs recomputing, written by triggers. Insert-only (no read, so it
    // adds no reads or conflicts to bulk payout and rebuild transactions): a user can have several
    // rows, and their recompute deletes them all.
    userEmailSegmentsDirty: defineTable({
        userId: v.id('users'),
        // 'tier': only an earnings amount moved, so only the earnings floor can change (a cheap
        // recompute). 'full': anything else (platforms, connections, joins, sendable).
        scope: v.union(v.literal('tier'), v.literal('full')),
        // When a recompute was scheduled for it, so the next drain doesn't schedule it again. -1
        // (STUCK_SCHEDULED_AT in resend/emailSegments.ts) parks a marker given up on.
        scheduledAt: v.optional(v.number()),
        // Times its recompute failed; given up on past a limit.
        attempts: v.optional(v.number()),
        // The recompute scheduled for it, so an overdue marker can tell "still queued" from "failed".
        recomputeJobId: v.optional(v.id('_scheduled_functions')),
    })
        .index('by_userId', ['userId'])
        .index('by_userId_and_scheduledAt', ['userId', 'scheduledAt'])
        .index('by_scheduledAt', ['scheduledAt']),

    // One row once the segment backfill has finished: until then the segments aren't offered.
    emailSegmentsStatus: defineTable({
        readyAt: v.number(),
    }),

    emailSendRecipients: defineTable({
        broadcastId: v.id('customEmailBroadcasts'),
        email: v.string(), // lowercased
        userId: v.optional(v.id('users')),
        // Copied from the user when the send is queued, for {{ promote.username }} and
        // {{ promote.first_name }}: a later rename can't change a resumed chunk.
        username: v.optional(v.string()),
        firstName: v.optional(v.string()),
        status: sendRecipientStatusValidator,
        // Resend's id for this email, from the first webhook event that names it.
        resendEmailId: v.optional(v.string()),
        sentAt: v.optional(v.number()),
        deliveredAt: v.optional(v.number()),
        firstOpenedAt: v.optional(v.number()),
        lastOpenedAt: v.optional(v.number()),
        openCount: v.number(),
        firstClickedAt: v.optional(v.number()),
        lastClickedAt: v.optional(v.number()),
        clickCount: v.number(),
        // Distinct links this recipient clicked (capped) — decides unique clicks per link.
        clickedLinks: v.array(v.string()),
        bouncedAt: v.optional(v.number()),
        complainedAt: v.optional(v.number()),
        failedAt: v.optional(v.number()),
        unsubscribedAt: v.optional(v.number()),
        // Why it bounced/failed/wasn't sent: provider code and message.
        detailCode: v.optional(v.string()),
        detailMessage: v.optional(v.string()),
        lastEventAt: v.optional(v.number()),
        // Place in the send's audience (0-based). Chunk N is positions [N*chunkSize, (N+1)*chunkSize),
        // and its Resend idempotency key is broadcast/<id>/chunk-N. Set on queued sends.
        position: v.optional(v.number()),
    })
        .index('by_broadcastId_and_email', ['broadcastId', 'email'])
        .index('by_broadcastId_and_status', ['broadcastId', 'status'])
        .index('by_broadcastId_and_position', ['broadcastId', 'position']),

    // Raw event log behind each recipient's activity timeline. Holds open/click IP addresses and
    // user agents (personal data): super-user access only, and purged after
    // SEND_ACTIVITY_RETENTION_DAYS by a daily cron. The aggregates outlive it.
    emailSendActivity: defineTable({
        broadcastId: v.id('customEmailBroadcasts'),
        recipientId: v.id('emailSendRecipients'),
        type: v.union(
            v.literal('delivered'),
            v.literal('opened'),
            v.literal('clicked'),
            v.literal('bounced'),
            v.literal('complained'),
            v.literal('failed'),
            v.literal('unsubscribed')
        ),
        at: v.number(),
        // The webhook delivery (svix-id) this came from, for tracing it in Resend's logs. Dedupe
        // happens upstream (emailWebhookReceipts). Absent for events we generate (our unsubscribe link).
        webhookId: v.optional(v.string()),
        link: v.optional(v.string()),
        ipAddress: v.optional(v.string()),
        userAgent: v.optional(v.string()),
        detailCode: v.optional(v.string()),
        detailMessage: v.optional(v.string()),
    })
        .index('by_recipientId_and_at', ['recipientId', 'at'])
        .index('by_at', ['at']),

    // Hourly engagement per send, split across shards so a burst of deliveries doesn't serialize
    // on one document. A chart sums a send's shards per bucket.
    emailSendTimeline: defineTable({
        broadcastId: v.id('customEmailBroadcasts'),
        bucketStart: v.number(),
        shard: v.number(),
        delivered: v.number(),
        opened: v.number(),
        clicked: v.number(),
        bounced: v.number(),
        complained: v.number(),
        unsubscribed: v.number(),
    }).index('by_broadcastId_and_bucketStart_and_shard', ['broadcastId', 'bucketStart', 'shard']),

    // Clicks per link per send.
    emailSendLinks: defineTable({
        broadcastId: v.id('customEmailBroadcasts'),
        url: v.string(),
        uniqueClicks: v.number(),
        totalClicks: v.number(),
    })
        .index('by_broadcastId_and_url', ['broadcastId', 'url'])
        .index('by_broadcastId_and_uniqueClicks', ['broadcastId', 'uniqueClicks']),

    emailSuppressions: defineTable({
        email: v.string(), // always lowercased — see suppressEmail in resend/index.ts
        // 'manual': added by staff in the admin (resend/suppressions.ts).
        reason: v.union(v.literal('hard_bounce'), v.literal('complaint'), v.literal('unsubscribe'), v.literal('manual')),
        createdAt: v.number(),
        sourceEmailId: v.optional(v.string()), // Resend's email_id, when suppressed via webhook
        addedBy: v.optional(v.id('users')), // staff member, for 'manual'
        note: v.optional(v.string()),
        // The address hard-bounced before a stronger reason (unsubscribe, complaint) replaced the
        // row — still blocks staff alerts (withdrawals/hotWalletAlert.ts alertSuppression).
        hardBounced: v.optional(v.boolean()),
    })
        .index('by_email', ['email'])
        .index('by_createdAt', ['createdAt'])
        .index('by_reason_and_createdAt', ['reason', 'createdAt']),

    // ------------------------------------------------------------------------------------
    // Account-wide email log (resend/emailLog.ts): every email Resend reports on — campaign
    // sends, test sends, staff alerts — kept current from the webhook. Holds recipient addresses
    // (personal data): super-user reads only, purged after EMAIL_LOG_RETENTION_DAYS.
    // ------------------------------------------------------------------------------------
    emailMessages: defineTable({
        resendEmailId: v.string(),
        to: v.string(), // lowercased
        subject: v.optional(v.string()),
        from: v.optional(v.string()),
        category: v.union(v.literal('campaign'), v.literal('test'), v.literal('system')),
        broadcastId: v.optional(v.id('customEmailBroadcasts')),
        status: emailMessageStatusValidator,
        firstEventAt: v.number(),
        lastEventAt: v.number(),
        sentAt: v.optional(v.number()),
        deliveredAt: v.optional(v.number()),
        openedAt: v.optional(v.number()),
        clickedAt: v.optional(v.number()),
        bouncedAt: v.optional(v.number()),
        complainedAt: v.optional(v.number()),
        failedAt: v.optional(v.number()),
        openCount: v.number(),
        clickCount: v.number(),
        detailCode: v.optional(v.string()),
        detailMessage: v.optional(v.string()),
    })
        .index('by_resendEmailId', ['resendEmailId'])
        .index('by_firstEventAt', ['firstEventAt'])
        .index('by_lastEventAt', ['lastEventAt'])
        .index('by_to_and_firstEventAt', ['to', 'firstEventAt'])
        .index('by_status_and_firstEventAt', ['status', 'firstEventAt'])
        .index('by_category_and_firstEventAt', ['category', 'firstEventAt']),

    // Account-wide totals per UTC day, sharded so a burst of events doesn't serialize on one
    // document. Unique per email (an email opened twice counts one open). No personal data.
    emailDailyStats: defineTable({
        day: v.number(),
        shard: v.number(),
        sent: v.number(),
        delivered: v.number(),
        opened: v.number(),
        clicked: v.number(),
        bounced: v.number(),
        complained: v.number(),
        failed: v.number(),
        unsubscribed: v.number(),
    }).index('by_day_and_shard', ['day', 'shard']),

    // Webhook deliveries already processed (svix-id), so a retried delivery is a no-op. Short-lived:
    // Resend retries for days, not weeks; purged after WEBHOOK_RECEIPT_RETENTION_DAYS.
    emailWebhookReceipts: defineTable({
        webhookId: v.string(),
        receivedAt: v.number(),
    })
        .index('by_webhookId', ['webhookId'])
        .index('by_receivedAt', ['receivedAt']),

    // The latest account health check (resend/accountHealth.ts, hourly): Resend's view of the
    // sending domain and our webhook. One row, key 'current'.
    emailAccountHealth: defineTable({
        key: v.literal('current'),
        checkedAt: v.number(),
        checkError: v.optional(v.string()),
        domain: v.optional(
            v.object({
                name: v.string(),
                status: v.string(),
                openTracking: v.optional(v.boolean()),
                clickTracking: v.optional(v.boolean()),
                failingRecords: v.number(),
            })
        ),
        webhook: v.optional(
            v.object({
                endpoint: v.string(),
                status: v.string(),
                missingEvents: v.array(v.string()),
            })
        ),
    }).index('by_key', ['key']),

    // Campaign stats snapshots - daily historical tracking
    campaignStatsSnapshot: defineTable({
        campaignId: v.id('campaigns'),
        snapshotDate: v.number(), // Daily timestamp (start of day in ms)

        // Submission counts
        totalSubmissions: v.number(),
        approvedSubmissions: v.number(),
        pendingSubmissions: v.number(),
        deniedSubmissions: v.number(),

        // View metrics (all statuses and approved-only)
        totalViews: v.number(), // All submissions
        pendingViews: v.number(), // Pending submissions only
        totalApprovedViews: v.number(), // Approved submissions only

        // Social metrics (approved submissions only)
        totalApprovedLikes: v.number(),
        totalApprovedComments: v.number(),
        totalApprovedShares: v.number(),

        // User engagement
        totalUniqueUsers: v.number(),
        approvedUniqueUsers: v.number(),

        // Platform breakdown - social metrics
        metricsByPlatform: platformBreakdownValidator,

        // Platform breakdown - unique creator counts
        creatorsByPlatform: creatorsByPlatformValidator,

        // Budget tracking
        budgetUsed: v.number(),
        budgetRemaining: v.number(),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_campaignId_and_date', ['campaignId', 'snapshotDate'])
        .index('by_date', ['snapshotDate']),

    // Platform-wide daily rollup (DEV-76 admin dashboard).
    // One row per UTC day; written by capturePlatformSnapshot when the daily
    // campaign-snapshot batch chain drains. Metric fields are optional because
    // backfilled historical rows may have gaps (e.g. no follower history).
    platformStatsSnapshots: defineTable({
        snapshotDate: v.number(), // UTC start-of-day ms, matching campaignStatsSnapshot
        // Cumulative counter reads
        totalUsers: v.optional(v.number()),
        totalConnectedPages: v.optional(v.number()),
        totalFollowers: v.optional(v.number()),
        approvedViews: v.optional(v.number()),
        pendingViews: v.optional(v.number()),
        deniedViews: v.optional(v.number()),
        userBalanceCents: v.optional(v.number()),
        userReservedCents: v.optional(v.number()),
        pendingAccounts: v.optional(v.number()),
        // Summed from that day's campaignStatsSnapshot rows
        totalSubmissions: v.optional(v.number()),
        approvedSubmissions: v.optional(v.number()),
        pendingSubmissions: v.optional(v.number()),
        deniedSubmissions: v.optional(v.number()),
        // Daily money from that day's walletTransactions (payouts credited, withdrawals).
        dailyCreditedCents: v.optional(v.number()),
        dailyWithdrawnCents: v.optional(v.number()),
        // Carried-forward cumulative creator earnings, in cents. Each day adds the
        // non-negative per-campaign budgetUsed delta; campaigns missing that day
        // (archived) stay in the total. Day-over-day diff is what creators earned.
        creatorEarningsCents: v.optional(v.number()),
    }).index('by_snapshotDate', ['snapshotDate']),

    // Campaign earnings milestone snapshots (budget cutoff points: 90% and 100%)
    // Used for audit/history and future extension to submission-level history.
    campaignEarningsMilestoneSnapshot: defineTable({
        campaignId: v.id('campaigns'),
        milestonePct: v.union(v.literal(90), v.literal(100)),
        occurredAt: v.number(),
        budgetCents: v.number(),
        budgetUsedCents: v.number(),
        totalPotentialCappedEarningsCents: v.number(),
        scaleRatioBps: v.optional(v.number()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_campaignId_and_milestonePct', ['campaignId', 'milestonePct'])
        .index('by_campaignId_and_occurredAt', ['campaignId', 'occurredAt']),

    /** @deprecated Campaign approval/rejection flow removed */
    campaignReviews: defineTable({
        campaignId: v.id('campaigns'),
        reviewerId: v.id('users'),
        action: campaignReviewActionValidator,
        reason: v.optional(v.string()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_reviewerId', ['reviewerId']),

    /** @deprecated Campaign notes feature removed */
    campaignNotes: defineTable({
        campaignId: v.id('campaigns'),
        authorId: v.id('users'),
        content: v.string(),
        isInternal: v.boolean(), // Always true for admin notes
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_authorId', ['authorId']),

    // Moderator assignments - tracks which moderators are assigned to which campaigns
    moderatorAssignments: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'), // The moderator being assigned
        assignedBy: v.id('users'), // Admin/Super User who made the assignment
        assignedAt: v.number(), // Unix timestamp
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_userId', ['userId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId']),

    // Per-moderator view-permission grants (DEV-66). Default-deny: absence of a row
    // means the moderator cannot access that surface. One row per (moderator, surface).
    moderatorViewPermissions: defineTable({
        userId: v.id('users'), // The moderator being granted the surface
        surface: moderatorViewSurfaceValidator,
        grantedBy: v.id('users'), // Admin/Super User who granted it
        grantedAt: v.number(), // Unix timestamp
    })
        .index('by_userId', ['userId'])
        .index('by_userId_and_surface', ['userId', 'surface']),

    // ------------------------------------------------------------------------
    // Referrals (docs/systems/REFERRAL_SYSTEM.md)
    // ------------------------------------------------------------------------

    // One row per user an admin has ever enabled the referral program for.
    // History lives in referralAuditLog.
    referralPrograms: defineTable({
        userId: v.id('users'),
        status: referralProgramStatusValidator,
        maxActiveLinks: v.optional(v.number()), // override of DEFAULT_MAX_ACTIVE_REFERRAL_LINKS
    }).index('by_userId', ['userId']),

    referralLinks: defineTable({
        referrerUserId: v.id('users'),
        code: v.string(), // system-generated, lowercase, immutable, never reissued
        name: v.string(), // private label shown to the referrer and admins
        nameKey: v.string(), // normalized name; unique per referrer across all their links (D5)
        status: referralLinkStatusValidator,
        // Snapshot of the last status change; full history in referralAuditLog.
        statusChangedAt: v.optional(v.number()),
        statusChangedBy: v.optional(v.id('users')),
        statusChangedByRole: v.optional(referralActorRoleValidator), // 'admin' locks reactivation to admins
    })
        .index('by_code', ['code'])
        .index('by_referrerUserId', ['referrerUserId'])
        .index('by_referrerUserId_and_status', ['referrerUserId', 'status'])
        .index('by_referrerUserId_and_nameKey', ['referrerUserId', 'nameKey']),

    // One row per referred user, ever. Funnel counts come from aggregateReferralFunnel.
    referrals: defineTable({
        referredUserId: v.id('users'),
        referrerUserId: v.id('users'),
        linkId: v.id('referralLinks'),
        source: referralSourceValidator,
        stage: referralStageValidator,
        stageRank: v.number(),
        signedUpAt: v.number(),
        onboardedAt: v.optional(v.number()),
        accountVerifiedAt: v.optional(v.number()),
        campaignJoinedAt: v.optional(v.number()),
        completedAt: v.optional(v.number()),
        completedSubmissionId: v.optional(v.id('submissions')),
        // v2 (§11): campaign of completedSubmissionId, written with it (claim and advance(completed)).
        // Moves on re-completion. Never read for authorization.
        firstCampaignId: v.optional(v.id('campaigns')),
        completionReversedAt: v.optional(v.number()),
        firstPaidAt: v.optional(v.number()),
        status: referralStatusValidator,
        voidedReason: v.optional(v.string()),
        voidedBy: v.optional(v.id('users')), // absent on a voided row = system
        voidedAt: v.optional(v.number()),
    })
        .index('by_referredUserId', ['referredUserId'])
        .index('by_referrerUserId', ['referrerUserId'])
        .index('by_referrerUserId_and_status', ['referrerUserId', 'status'])
        .index('by_linkId_and_status', ['linkId', 'status'])
        // Admin platform listing (§13): newest first, optionally narrowed by status and/or stage.
        .index('by_status', ['status'])
        .index('by_stage', ['stage'])
        .index('by_status_and_stage', ['status', 'stage'])
        // Daily completion-reversal reconciliation: completed referrals not yet marked
        // reversed, restricted to a recent `completedAt` window.
        .index('by_stageRank_and_completionReversedAt_and_completedAt', ['stageRank', 'completionReversedAt', 'completedAt']),

    // Append-only funnel ledger (system-observed facts).
    referralEvents: defineTable({
        referralId: v.id('referrals'),
        type: referralEventTypeValidator,
        stage: v.optional(referralStageValidator),
        subjectId: v.optional(v.string()), // submissionId / connectedAccountId / campaignId
        at: v.number(),
    }).index('by_referralId', ['referralId']),

    // Append-only audit log of every program, link and referral state change.
    // Written in the same mutation as the change it records.
    referralAuditLog: defineTable({
        referrerUserId: v.id('users'),
        action: referralAuditActionValidator,
        linkId: v.optional(v.id('referralLinks')),
        referralId: v.optional(v.id('referrals')),
        actorUserId: v.optional(v.id('users')), // absent = system
        actorRole: referralActorRoleValidator,
        reason: v.optional(v.string()),
        previousValue: v.optional(v.string()),
        newValue: v.optional(v.string()),
        at: v.number(),
    })
        .index('by_referrerUserId', ['referrerUserId'])
        .index('by_linkId', ['linkId'])
        .index('by_referralId', ['referralId'])
        .index('by_action', ['action']),

    // Admin referral analytics (§13). A run recomputes everything from the source tables in
    // batches; admins read the newest complete run, so a half-built run is never visible.
    referralStatsRuns: defineTable({
        status: referralStatsRunStatusValidator,
        trigger: v.union(v.literal('cron'), v.literal('manual')),
        requestedBy: v.optional(v.id('users')),
        startedAt: v.number(),
        finishedAt: v.optional(v.number()),
        phase: referralStatsPhaseValidator,
        ...referralStatsCountsFields,
        programsEnabled: v.number(),
        programsDisabled: v.number(),
        referrersWithReferrals: v.number(),
        error: v.optional(v.string()),
    }).index('by_status', ['status']),

    // One row per referrer per run. Numbers only; names and program status are read live.
    referralReferrerStats: defineTable({
        runId: v.id('referralStatsRuns'),
        referrerUserId: v.id('users'),
        ...referralStatsCountsFields,
    })
        .index('by_runId_and_referrerUserId', ['runId', 'referrerUserId'])
        .index('by_runId_and_signedUp', ['runId', 'signedUp'])
        .index('by_runId_and_completed', ['runId', 'completed'])
        .index('by_runId_and_earningsCents', ['runId', 'earningsCents']),

    // Sign-ups and completions per UTC day for one run, rebuilt from signedUpAt/completedAt.
    referralStatsDaily: defineTable({
        runId: v.id('referralStatsRuns'),
        day: v.number(), // UTC midnight, ms
        signedUp: v.number(),
        completed: v.number(),
    }).index('by_runId_and_day', ['runId', 'day']),

    // Campaign members - tracks user membership and approval workflow
    campaignMembers: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        /** @deprecated Always 'approved'. See MEMBERSHIP_STATUS comment. */
        status: membershipStatusValidator,
        reviewedBy: v.optional(v.id('users')), // Who approved/denied/removed
        reviewedAt: v.optional(v.number()), // When reviewed (Unix timestamp)
        reason: v.optional(v.string()), // Denial or removal reason
        // Denormalized user fields for search
        userEmail: v.optional(v.string()),
        userUsername: v.optional(v.string()),
        // Combined search text: username, email, userId, + connected account usernames/platforms
        searchText: v.optional(v.string()),
        // Control flags for page-centric moderation (memberships are always auto-approved)
        activityFrozen: v.optional(v.boolean()),
        canAddNewPages: v.optional(v.boolean()),
        // DEV-41: admin-only per-creator payout cap override, in CENTS. When set, this overrides
        // the campaign-wide campaignRules.postingRequirements.maxPayoutPerCreator for THIS member
        // only (e.g. one big creator at $6K while the campaign default stays $4K). Admin-set only;
        // must NEVER surface in public/creator-facing queries.
        creatorMaxOverrideCents: v.optional(v.number()),
        // Creator-controlled dashboard visibility preference (Archive/Restore). Purely a display
        // filter consulted by getMyDashboardCampaigns — does not affect submission eligibility,
        // earnings, or admin/moderator visibility. Undefined means "not hidden" (default/visible).
        isHiddenFromDashboard: v.optional(v.boolean()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_userId', ['userId'])
        .index('by_userId_and_status', ['userId', 'status'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId'])
        // DEV-41: lets the earnings rebuild fetch ONLY members with an override set (range
        // gte 0 skips the undefined-keyed rows) instead of scanning every campaign member.
        .index('by_campaignId_and_creatorMaxOverrideCents', ['campaignId', 'creatorMaxOverrideCents'])
        .index('by_campaignId_and_status', ['campaignId', 'status'])
        .index('by_status', ['status'])
        .index('by_campaignId_and_userEmail', ['campaignId', 'userEmail'])
        .index('by_campaignId_and_userUsername', ['campaignId', 'userUsername'])
        .searchIndex('search_members', {
            searchField: 'searchText',
            filterFields: ['campaignId', 'status'],
        }),

    // Campaign member accounts - junction table tracking which connected accounts a user selected when joining
    // Each account has its own approval status independent of the user's membership status
    campaignMemberAccounts: defineTable({
        // Ownership fields — undefined when the CMA is "rogue" (page has no current owner).
        // A rogue CMA retains its pageId, campaignId, and moderation status.
        campaignMemberId: v.optional(v.id('campaignMembers')),
        connectedAccountId: v.optional(v.id('connectedAccounts')),
        status: memberAccountStatusValidator,
        reviewedBy: v.optional(v.id('users')),
        reviewedAt: v.optional(v.number()),
        reason: v.optional(v.string()),
        // Optional proof link required by some campaigns when joining/adding accounts
        demographicsProofUrl: v.optional(v.string()),
        // Denormalized fields for efficient follower aggregation
        campaignId: v.optional(v.id('campaigns')),
        platform: v.optional(socialPlatformValidator),
        followersCount: v.optional(v.number()),
        // Denormalized connected account verification status for follower aggregate filtering
        connectedAccountStatus: v.optional(connectedAccountStatusValidator),
        // Canonical page identity link (populated from connectedAccount.pageId)
        pageId: v.optional(v.id('pages')),
        // Combined search text for campaign Pages tab full-text search
        searchText: v.optional(v.string()),
        // Denormalized count of this account's currently-APPROVED submissions in this campaign.
        // Maintained by the submissions trigger in _infra/triggers.ts (increments/decrements on
        // transitions into/out of SUBMISSION_STATUS.APPROVED). Gates the reach counters
        // (campaignReach / campaignReachByPlatform) so a page approved as a campaign member but
        // with no approved content doesn't count toward "reach" — see readBrandCampaignReach and
        // getCampaignStats. Undefined/0 means no approved submissions yet.
        approvedSubmissionCount: v.optional(v.number()),
        // Mirrors statusHistoryEntryValidator.cascadeGroupId — set only when the current
        // reviewedBy/reviewedAt/reason reflect a bulk-deny action (page-ban cascade, "deny
        // all"), so many CMAs denied in one action can be traced back to and counted as a
        // single moderator decision. Destructively overwritten on every re-review just like
        // reviewedBy/reviewedAt/reason — every write path touching those fields must also
        // set this (or explicitly clear it to undefined). See lib/moderationEvents.ts.
        cascadeGroupId: v.optional(v.string()),
    })
        .index('by_campaignMemberId', ['campaignMemberId'])
        .index('by_connectedAccountId', ['connectedAccountId'])
        .index('by_campaignId_and_connectedAccountId', ['campaignId', 'connectedAccountId'])
        .index('by_campaignMemberId_and_connectedAccountId', ['campaignMemberId', 'connectedAccountId'])
        .index('by_campaignMemberId_and_status', ['campaignMemberId', 'status'])
        .index('by_status', ['status'])
        .index('by_campaignId_and_status', ['campaignId', 'status'])
        .index('by_campaignId_and_platform_and_status', ['campaignId', 'platform', 'status'])
        .index('by_pageId', ['pageId'])
        .index('by_campaignId_and_pageId', ['campaignId', 'pageId'])
        .index('by_pageId_and_status', ['pageId', 'status'])
        .index('by_campaignId', ['campaignId'])
        .index('by_campaignId_and_platform', ['campaignId', 'platform'])
        .index('by_campaignId_and_followersCount', ['campaignId', 'followersCount'])
        .index('by_campaignId_and_status_and_followersCount', ['campaignId', 'status', 'followersCount'])
        .index('by_campaignId_and_platform_and_followersCount', ['campaignId', 'platform', 'followersCount'])
        .index('by_campaignId_and_platform_and_status_and_followersCount', ['campaignId', 'platform', 'status', 'followersCount'])
        .searchIndex('search_pages', {
            searchField: 'searchText',
            filterFields: ['campaignId', 'status', 'platform'],
        }),

    // Distinct submitters per campaign (one row per [campaignId, userId] that has at least one submission)
    campaignSubmissionUsers: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_userId', ['userId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId']),

    // Distinct submitters per campaign+platform (one row per [campaignId, platform, userId] that has at least one submission)
    campaignSubmissionUsersByPlatform: defineTable({
        campaignId: v.id('campaigns'),
        platform: socialPlatformValidator,
        userId: v.id('users'),
    })
        .index('by_campaignId_and_platform', ['campaignId', 'platform'])
        .index('by_campaignId_and_platform_and_userId', ['campaignId', 'platform', 'userId']),

    submissions: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        platform: socialPlatformValidator,
        status: submissionStatusValidator,
        connectedAccountId: v.optional(v.id('connectedAccounts')), // Link to the account that submitted

        videoId: v.string(), // Platform-specific video ID for uniqueness checking
        videoUrl: v.string(),
        /** @deprecated Phase 5 — read from submissionStats table via enrichment. Will be removed in Phase 5b. */
        videoStats: v.optional(videoStatsValidator),

        denialReason: v.optional(v.string()), // Reason for denial
        statusHistory: v.optional(v.array(statusHistoryEntryValidator)),

        /** @deprecated Phase 5 — read from submissionRefreshState via enrichment. Will be removed in Phase 5b. */
        fetchStatus: v.optional(submissionFetchStatusValidator),
        /** @deprecated Phase 5 — read from submissionRefreshState via enrichment. Will be removed in Phase 5b. */
        lastFetchedAt: v.optional(v.number()), // Unix timestamp when data was last successfully fetched
        /** @deprecated Phase 5 — read from submissionRefreshState via enrichment. Will be removed in Phase 5b. */
        lastFetchedError: v.optional(v.string()), // Error message if fetch failed

        // Denormalized flag for efficient refresh queries
        // Set to true when submission needs stats refresh (approved + stale)
        /** @deprecated Phase 5 — read from submissionRefreshState via enrichment. Will be removed in Phase 5b. */
        needsFetch: v.optional(v.boolean()),

        // Next time (Unix ms) this approved submission should be refreshed.
        // This replaces the global "mark stale submissions" scan by allowing fully indexed due-time queries.
        /** @deprecated Phase 5 — read from submissionRefreshState via enrichment. Will be removed in Phase 5b. */
        refreshDueAt: v.optional(v.number()),

        // Per-video earnings live in submissionEarnings (read via enrichment). The old denormalized
        // `earnings` cache was stripped (#534) and dropped from the schema (#535).

        // Snapshot-based earnings: view count used for earnings when admin selects a snapshot cutoff
        /** @deprecated Phase 5a — computed at query time from submissionViewSnapshots table. Will be removed in Phase 5b. */
        snapshotViewCount: v.optional(v.number()), // View count from snapshot used for earnings
        /** @deprecated Phase 5a — computed at query time from submissionViewSnapshots table. Will be removed in Phase 5b. */
        snapshotFetchedAt: v.optional(v.number()), // When that snapshot was taken

        // @deprecated 2026-09-16 — backed the removed `search_submissions` free-text index. Nothing
        // reads or writes it any more (submission tables filter by exact entity IDs, see
        // admin/submissionFilters.ts). Unset by migrations/stripSubmissionSearchText; drop this
        // field once that has run on prod (CONVEX_MIGRATIONS_RUNBOOK.md, 2026-10-22).
        searchText: v.optional(v.string()),

        // Denormalized view count for indexed sorting (synced from videoStats.viewCount via triggers)
        indexedViewCount: v.optional(v.number()),

        // Denormalized potential earnings (cents) for indexed admin sorting
        // (synced from submissionEarnings.potentialEarningsCents via trigger — see SE3 in _infra/triggers.ts)
        indexedEarningsCents: v.optional(v.number()),

        // Denormalized engagement rate ((likes+comments+shares)/views) for indexed admin sorting
        // (synced from submissionStats via trigger — see T6 in _infra/triggers.ts). 0 when there's
        // no view data yet, rather than null, so the field stays a plain sortable number.
        indexedEngagementRate: v.optional(v.number()),

        // Denormalized flag for efficient filtering of submissions without connected accounts
        // Set to true when connectedAccountId is undefined/null (maintained by trigger)
        isUnlinked: v.optional(v.boolean()),

        // Duplicate submission tracking (managed by DUPLICATE FLAG SYNC TRIGGER)
        // Uses ASYMMETRIC logic: the oldest submission (by _creationTime) in each
        // (campaignId, videoId) group is the "original" and is NOT flagged.
        // - Original (oldest): isDuplicate=false, duplicateOfSubmissionId=undefined
        // - Duplicates (newer): isDuplicate=true, duplicateOfSubmissionId=<original's ID>
        isDuplicate: v.optional(v.boolean()),
        // Points to the original (oldest) submission in the duplicate group.
        // Only set when isDuplicate=true; undefined for originals and non-duplicates.
        duplicateOfSubmissionId: v.optional(v.id('submissions')),
        // Canonical page identity link (populated during submission processing)
        pageId: v.optional(v.id('pages')),
        // Platform ad-code (e.g. TikTok Spark Code) attached at submission time. Required when
        // campaign.campaignRules.sparkCodeRequirement.enabled is true; format is platform-specific
        // free text (no fixed pattern across platforms).
        sparkCode: v.optional(v.string()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_userId', ['userId'])
        .index('by_status', ['status'])
        // Global indexes for platform-wide submission sorting (admin dashboard)
        .index('by_indexedViewCount', ['indexedViewCount'])
        .index('by_status_and_indexedViewCount', ['status', 'indexedViewCount'])
        // Global indexes for platform-wide sort by earnings/engagement (admin dashboard).
        // No status-composite variant — the status filter is applied via filterWith on these
        // two axes instead, to stay within the table's index budget (unlike indexedViewCount,
        // which predates that budget pressure and got its own composite index).
        .index('by_indexedEarningsCents', ['indexedEarningsCents'])
        .index('by_indexedEngagementRate', ['indexedEngagementRate'])
        // @deprecated Phase 5 — refreshDueAt moved to submissionRefreshState. Remove in Phase 5b.
        .index('by_status_and_refreshDueAt', ['status', 'refreshDueAt'])
        .index('by_videoId', ['videoId'])
        .index('by_videoId_and_status', ['videoId', 'status'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId'])
        .index('by_campaignId_and_userId_and_status', ['campaignId', 'userId', 'status'])
        // Index for efficient minViewsForPayout filtering (range query on indexedViewCount)
        .index('by_campaignId_and_userId_and_status_and_indexedViewCount', ['campaignId', 'userId', 'status', 'indexedViewCount'])
        .index('by_campaignId_and_status', ['campaignId', 'status'])
        // @deprecated Phase 5 — refreshDueAt moved to submissionRefreshState. Remove in Phase 5b.
        .index('by_campaignId_and_status_and_refreshDueAt', ['campaignId', 'status', 'refreshDueAt'])
        .index('by_campaignId_and_videoId', ['campaignId', 'videoId'])
        .index('by_campaignId_and_status_and_platform', ['campaignId', 'status', 'platform'])
        .index('by_campaignId_and_platform', ['campaignId', 'platform'])
        .index('by_campaignId_and_platform_and_userId', ['campaignId', 'platform', 'userId'])
        // @deprecated Phase 5 — needsFetch moved to submissionRefreshState. Remove in Phase 5b.
        .index('by_campaignId_and_status_and_needsFetch', ['campaignId', 'status', 'needsFetch'])
        .index('by_connectedAccountId', ['connectedAccountId'])
        .index('by_connectedAccountId_and_status', ['connectedAccountId', 'status'])
        .index('by_campaignId_and_connectedAccountId', ['campaignId', 'connectedAccountId'])
        .index('by_campaignId_and_connectedAccountId_and_status', ['campaignId', 'connectedAccountId', 'status'])
        .index('by_pageId', ['pageId'])
        .index('by_campaignId_and_pageId', ['campaignId', 'pageId'])
        .index('by_campaignId_and_pageId_and_status', ['campaignId', 'pageId', 'status'])
        .index('by_campaign_account_viewCount', ['campaignId', 'connectedAccountId', 'indexedViewCount'])
        // Indexes for sorting by views (indexedViewCount must be last for proper ordering)
        .index('by_campaignId_and_indexedViewCount', ['campaignId', 'indexedViewCount'])
        .index('by_campaignId_and_status_and_indexedViewCount', ['campaignId', 'status', 'indexedViewCount'])
        // Campaign-scoped indexes for sorting by earnings/engagement (status applied via filterWith)
        .index('by_campaignId_and_indexedEarningsCents', ['campaignId', 'indexedEarningsCents'])
        .index('by_campaignId_and_indexedEngagementRate', ['campaignId', 'indexedEngagementRate'])
        // Index for filtering by isUnlinked (used by denyUnlinkedSubmissionsInBatches)
        .index('by_campaignId_and_status_and_isUnlinked', ['campaignId', 'status', 'isUnlinked'])
        // Global indexes for counter backfill migrations (required by @convex-dev/migrations)
        .index('by_isUnlinked', ['isUnlinked'])
        .index('by_isDuplicate', ['isDuplicate']),

    // Per-submission earnings cache (in cents). This supports:
    // - Budget-capped earnings at the submission level (sum never exceeds campaign budget)
    // - Manual campaign freeze that locks submission earnings while views continue to update
    submissionEarnings: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        submissionId: v.id('submissions'),
        // Earnings in cents (integers)
        rawEarningsCents: v.number(), // raw (no caps) for this submission
        videoCappedEarningsCents: v.number(), // after per-video cap
        potentialEarningsCents: v.number(), // after creator-cap allocation (per-user cap)
        isVideoCapped: v.boolean(),
        isWindowClosed: v.optional(v.boolean()), // Derived each reallocation (mirrors isVideoCapped). Optional → existing rows need no backfill.
        lastCalculatedAt: v.number(),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId'])
        .index('by_submissionId', ['submissionId'])
        .index('by_campaignId_and_submissionId', ['campaignId', 'submissionId']),

    // Per-submission refresh pipeline state (fetchStatus + last-fetched metadata).
    // Exclusively owned by the refresh pipeline — no other pipeline writes here.
    // 1:1 relationship with submissions via submissionId.
    submissionRefreshState: defineTable({
        submissionId: v.id('submissions'),
        fetchStatus: submissionFetchStatusValidator,
        /** Intentionally unused since 2026-04-15 — reserved for future adaptive-refresh logic. */
        needsFetch: v.optional(v.boolean()),
        /** Intentionally unused since 2026-04-15 — reserved for future adaptive-refresh logic. */
        refreshDueAt: v.optional(v.number()),
        lastFetchedAt: v.optional(v.number()),
        lastFetchedError: v.optional(v.string()),
        /**
         * Last refresh attempt that reached the platform and failed (markRefreshFailed). A deleted or private video
         * fails every time, so lastFetchedAt (last success) stops moving; age-based refresh tiers measure its 12h
         * cadence from this instead (lib/refreshPolicy.ts).
         */
        lastFailedAt: v.optional(v.number()),
        /**
         * First time a refresh got the platform's confirmed "deleted or private" signal (lib/videoAvailability.ts).
         * Flag only — stats and earnings are untouched; surfaced in the pre-payout data check. Cleared by the
         * next successful refresh.
         */
        videoUnavailableSince: v.optional(v.number()),
        /**
         * Facebook only, set by the listing views source (external/facebook.ts): 'rounded' when the count came
         * from /page/reels (rounded to 100s) instead of the exact /page/videos read. Surfaced in the admin and
         * the pre-payout data check. Absent for every other platform and for the legacy feed source.
         */
        viewCountPrecision: v.optional(facebookViewCountPrecisionValidator),
    })
        .index('by_submissionId', ['submissionId'])
        .index('by_needsFetch_and_refreshDueAt', ['needsFetch', 'refreshDueAt'])
        .index('by_fetchStatus', ['fetchStatus']),

    // Fair-share refresh queue. Each row is one platform refresh attempt for a
    // submission. The dispatcher (parallelism-1 workpool) picks pending items
    // round-robin across campaigns so no single campaign monopolizes a platform's
    // refresh slots. Replaces direct workpool.enqueueAction calls from
    // enqueueBatchRefreshForWorkflow / enqueueSubmissionForRefresh.
    refreshQueueItems: defineTable({
        campaignId: v.id('campaigns'),
        submissionId: v.id('submissions'),
        platform: socialPlatformValidator,
        status: refreshQueueItemStatusValidator,

        // Lifecycle timestamps (Unix ms).
        enqueuedAt: v.number(),
        startedAt: v.optional(v.number()),
        finishedAt: v.optional(v.number()),

        // Bumped every time the dispatcher promotes this row to PROCESSING. The
        // reaper resets stuck PROCESSING rows to PENDING; that retry counts here.
        attemptCount: v.number(),

        // Pass-through context for the underlying refresh action.
        refreshSource: v.optional(snapshotSourceValidator),
        // Pass-through context for the existing checkRefreshCompletion barrier.
        // Undefined for manual single-submission refreshes (no batch tracking).
        trackingId: v.optional(v.id('campaignRefreshWorkflows')),

        // Set when promoted to PROCESSING — lets onComplete confirm it's matching
        // the right attempt (the workpool delivers onComplete at-least-once).
        workpoolWorkId: v.optional(v.string()),

        // Last error message for FAILED items (UI/diagnostics).
        lastError: v.optional(v.string()),
    })
        // Dispatcher: distinct-campaign loose scan + per-campaign oldest fetch +
        // inflight count, all keyed off (platform, status, campaignId).
        .index('by_platform_and_status_and_campaignId', ['platform', 'status', 'campaignId'])
        // Cancel/reap drain + post-refresh failure count — find queue rows
        // belonging to a workflow tracking record by status.
        .index('by_trackingId_and_status', ['trackingId', 'status'])
        // Reaper: find rows stuck in PROCESSING past a deadline.
        .index('by_status_and_startedAt', ['status', 'startedAt']),

    // Per-submission video stats from platform APIs (viewCount, likes, thumbnail, etc.).
    // Written by refresh processors, read by display and allocation.
    // 1:1 relationship with submissions via submissionId.
    // campaignId and userId are denormalized for efficient queries without joining back to submissions.
    submissionStats: defineTable({
        submissionId: v.id('submissions'),
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        viewCount: v.number(),
        likeCount: v.optional(v.number()),
        commentCount: v.optional(v.number()),
        shareCount: v.optional(v.number()),
        // Absent when the platform doesn't report them (only Instagram does today).
        saveCount: v.optional(v.number()),
        repostCount: v.optional(v.number()),
        thumbnail: v.optional(v.string()),
        videoCreatedTime: v.optional(v.number()),
        accountName: v.optional(v.string()),
    })
        .index('by_submissionId', ['submissionId'])
        .index('by_campaignId', ['campaignId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId']),

    // Campaign leaderboard - denormalized per-user stats for efficient ranking
    // Updated via triggers when submissions change
    campaignLeaderboard: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        totalViews: v.number(), // Sum of approved submission views
        postsCount: v.number(), // Count of approved submissions
        primaryPlatform: socialPlatformValidator, // Platform with most submissions
        cappedEarnings: v.optional(v.number()), // Pre-calculated earnings in cents (after caps)
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId'])
        .index('by_userId', ['userId']),

    // Campaign user earnings - denormalized earnings with cap enforcement
    // Updated via triggers when submissions change or campaign rules change
    campaignUserEarnings: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),

        // View metrics
        rawViews: v.number(), // Total raw views (never capped)
        payableViews: v.number(), // Views that count toward budget after caps
        submissionCount: v.number(), // Count of approved submissions

        // Earnings in cents
        uncappedEarnings: v.number(), // What they WOULD earn without caps
        cappedEarnings: v.number(), // After video + creator caps applied

        // Cap status flags
        hasReachedVideoCap: v.boolean(), // At least one video hit cap
        hasReachedCreatorCap: v.boolean(), // Creator cap was reached

        // Metadata
        lastCalculatedAt: v.number(),

        // Payout tracking - set when campaign is paid out
        paidAmountCents: v.optional(v.number()), // Final amount paid to this user
        paidAt: v.optional(v.number()), // When they were paid
        walletTransactionId: v.optional(v.id('walletTransactions')), // Link to the wallet transaction
        // Ban hold (lib/banHolds.ts): part of these earnings was withheld at payout because the videos'
        // pages were under an active hold. Cleared when the hold is released (credited) or expires.
        // While set on an unpaid row, the earnings stay in the pending-earnings counter.
        withheldCents: v.optional(v.number()),
        // What a ban-hold forfeit took out of the pending-earnings counter for this unpaid row, so a later
        // payout run that settles the creator again puts back exactly that (lib/banHolds.ts).
        pendingSettledCents: v.optional(v.number()),
        // When the paid-out campaign's decrementPendingEarningsBatch took this unpaid, unheld row out of the
        // pending-earnings counter (admin/payouts.ts). From then on the row adds nothing pending, whatever
        // changes on it. Unset on rows cleared before the field existed (lib/pendingEarnings.ts).
        pendingClearedAt: v.optional(v.number()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_userId', ['userId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId'])
        .index('by_userId_and_cappedEarnings', ['userId', 'cappedEarnings'])
        .index('by_userId_and_rawViews', ['userId', 'rawViews']),

    // Campaign refresh workflows - tracks active refresh workflows per campaign
    // Used to prevent duplicate workflows and show status in admin panel
    campaignRefreshWorkflows: defineTable({
        campaignId: v.id('campaigns'),
        workflowId: v.string(), // Convex Workflow ID
        startedAt: v.number(), // Unix timestamp when workflow started
        startedBy: v.optional(v.id('users')), // null = cron, user = manual trigger
        // Completion barrier — set when workflow finishes dispatching to workpools
        enqueuedCount: v.optional(v.number()),
        dispatchCompletedAt: v.optional(v.number()),
        refreshSource: v.optional(v.string()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_workflowId', ['workflowId']),

    // Completion markers for refresh job tracking (zero-OCC insert pattern)
    // Each workpool onComplete inserts a marker. A separate check mutation counts them.
    refreshJobCompletions: defineTable({
        trackingId: v.id('campaignRefreshWorkflows'),
        workId: v.string(),
    })
        .index('by_trackingId', ['trackingId'])
        .index('by_workId', ['workId']),

    // Campaign earnings timeline — precomputed earnings totals per refresh round
    // Used for efficient snapshot cutoff recommendations (avoids expensive per-user recalculation)
    // One row per campaign: the ticket of the NEWEST earnings rebuild (lib/rebuildTicket.ts). A rebuild
    // claims a ticket in the same transaction that reads its settings, and every write step checks it
    // still holds the newest — so a rebuild that started under older settings can never overwrite one
    // that started later (the Oct 2026 Apex overlap: $9,880.41 written against an $8,300 budget).
    // Its own table, not a campaigns field: campaign updates run triggers that schedule rebuilds.
    earningsRebuildTickets: defineTable({
        campaignId: v.id('campaigns'),
        ticket: v.number(),
    }).index('by_campaignId', ['campaignId']),

    campaignEarningsTimeline: defineTable({
        campaignId: v.id('campaigns'),
        recordedAt: v.number(), // Unix ms — when this entry was recorded (end of refresh round)
        totalCappedEarningsCents: v.number(), // Sum of all users' cappedEarnings at this point
        userCount: v.number(), // Number of users with earnings
        totalViewCount: v.number(), // Sum of all submission view counts at this point
        source: earningsTimelineSourceValidator,
        // The settings the recompute behind this point ran under. Freezing at recordedAt re-runs
        // TODAY's settings, so a point recorded while frozen or under another budget does not predict
        // what freezing there pays. Absent on points written before Oct 2026 (context unknown); on
        // newer points creatorBudgetCents is always set, and an absent horizonAt then means live.
        horizonAt: v.optional(v.number()),
        creatorBudgetCents: v.optional(v.number()),
    }).index('by_campaignId_and_recordedAt', ['campaignId', 'recordedAt']),

    // View history snapshots — records view count at every API fetch for audit, graphs, and snapshot-based earnings
    submissionViewSnapshots: defineTable({
        submissionId: v.id('submissions'),
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        viewCount: v.number(),
        likeCount: v.number(),
        commentCount: v.number(),
        // Absent (undefined) when the platform didn't expose shares at fetch time.
        shareCount: v.optional(v.number()),
        saveCount: v.optional(v.number()),
        repostCount: v.optional(v.number()),
        fetchedAt: v.number(), // Unix ms — when platform API returned these stats
        source: snapshotSourceValidator,
    })
        .index('by_submissionId_and_fetchedAt', ['submissionId', 'fetchedAt'])
        .index('by_campaignId_and_fetchedAt', ['campaignId', 'fetchedAt'])
        .index('by_campaignId_and_userId_and_fetchedAt', ['campaignId', 'userId', 'fetchedAt']),

    // Temporary cursor used for “complex backfills” of sharded counters.
    // Pattern based on the official sharded-counter example:
    // https://github.com/get-convex/sharded-counter/blob/main/example/convex/example.ts
    backfillCursor: defineTable({
        name: v.string(), // e.g. "submissions", "users"
        creationTime: v.number(),
        id: v.string(),
        isDone: v.boolean(),
    }).index('by_name', ['name']),

    counterVerifyResults: defineTable({
        runId: v.string(),
        scope: v.string(),
        totalChecks: v.number(),
        mismatchCount: v.number(),
        mismatches: v.array(
            v.object({
                key: v.string(),
                oldValue: v.float64(),
                newValue: v.float64(),
                diff: v.float64(),
            })
        ),
    })
        .index('by_runId', ['runId'])
        .index('by_runId_and_mismatchCount', ['runId', 'mismatchCount']),

    // Payout events - immutable audit log of all payout-related activities
    // Used to track why users were paid, skipped, or had adjustments made
    payoutEvents: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        eventType: payoutEventTypeValidator,
        // For payout_success - amount credited
        amountCents: v.optional(v.number()),
        walletTransactionId: v.optional(v.id('walletTransactions')),
        // For payout_skipped - reason why
        skipReason: v.optional(payoutSkipReasonValidator),
        // Additional context
        actorUserId: v.optional(v.id('users')), // Admin who triggered the action
        note: v.optional(v.string()),
        // Snapshot of earnings data at time of event (for audit purposes)
        metadata: v.optional(
            v.object({
                cappedEarnings: v.optional(v.number()),
                qualifiedCappedEarnings: v.optional(v.number()), // Legacy snapshot field from older payout previews
                rawViews: v.optional(v.number()), // User's cumulative approved raw views at payout time
                effectiveEarnings: v.optional(v.number()),
                minimumThreshold: v.optional(v.number()),
                minViewsThreshold: v.optional(v.number()), // Campaign's user-level minViewsForPayout setting
                totalSubmissions: v.optional(v.number()), // Total submissions for the user
                qualifiedSubmissions: v.optional(v.number()), // Legacy snapshot field from older payout previews
                budgetCents: v.optional(v.number()),
                totalPotentialCents: v.optional(v.number()),
                paidUserCount: v.optional(v.number()),
                totalPaidCents: v.optional(v.number()),
                // Ban holds (lib/banHolds.ts): amount withheld at payout, and the hold it belongs to.
                withheldCents: v.optional(v.number()),
                banHoldId: v.optional(v.id('banHolds')),
            })
        ),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_userId', ['userId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId'])
        .index('by_campaignId_and_eventType', ['campaignId', 'eventType'])
        .index('by_eventType', ['eventType']),

    // Payout allocations - record of individual submission payouts during a payout run
    // Used to track which submissions were allocated how much, including capping decisions
    payoutAllocations: defineTable({
        campaignId: v.id('campaigns'),
        userId: v.id('users'),
        submissionId: v.id('submissions'),
        payoutRunId: v.string(),
        allocatedAmountCents: v.number(),
        viewCountAtPayout: v.number(),
        rawEarningsCents: v.number(),
        wasVideoCapped: v.boolean(),
        wasCreatorCapped: v.boolean(),
        paidOut: v.boolean(),
        paidAt: v.optional(v.number()),
        createdAt: v.number(),
        // Ban hold (lib/banHolds.ts): the video's page was under an active hold when the payout ran, so
        // this allocation was withheld (paidOut stays false). It is credited late if the hold is
        // released (releasedAt) or never paid if it expires (forfeitedAt).
        withheldBanHoldId: v.optional(v.id('banHolds')),
        releasedAt: v.optional(v.number()),
        forfeitedAt: v.optional(v.number()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_campaignId_and_userId', ['campaignId', 'userId'])
        .index('by_submissionId', ['submissionId'])
        .index('by_payoutRunId', ['payoutRunId'])
        .index('by_withheldBanHoldId', ['withheldBanHoldId']),

    // ============================================================================
    // Showcase Campaigns - Simplified campaign system for legacy/external video display
    // No budgets, payouts, earnings, or member approvals - just video metrics tracking
    // ============================================================================

    // Showcase campaigns - main table
    showcaseCampaigns: defineTable({
        title: v.string(),
        description: v.optional(v.string()),
        createdBy: v.id('users'),
        imageKey: v.optional(v.string()), // R2 image key
        // Rate limiting for global refresh
        lastGlobalRefreshAt: v.optional(v.number()), // Last time all stats were refreshed
    })
        .index('by_createdBy', ['createdBy'])
        .searchIndex('search_showcaseCampaigns', {
            searchField: 'title',
            filterFields: ['createdBy'],
        }),

    // Showcase submissions - videos added to showcase campaigns
    showcaseSubmissions: defineTable({
        showcaseCampaignId: v.id('showcaseCampaigns'),
        platform: socialPlatformValidator,
        videoId: v.string(), // Platform-specific video ID for uniqueness checking
        videoUrl: v.string(),
        videoStats: v.optional(videoStatsValidator),
        fetchStatus: submissionFetchStatusValidator,
        lastFetchedAt: v.optional(v.number()), // Unix timestamp when data was last successfully fetched
        lastFetchedError: v.optional(v.string()), // Error message if fetch failed
        // Next time (Unix ms) this submission should be refreshed.
        refreshDueAt: v.optional(v.number()),
        // Page/account info from the video
        pageInfo: v.optional(
            v.object({
                username: v.optional(v.string()),
                platformAccountId: v.optional(v.string()),
                followersCount: v.optional(v.number()),
            })
        ),
        // Denormalized view count for indexed sorting
        indexedViewCount: v.optional(v.number()),
    })
        .index('by_showcaseCampaignId', ['showcaseCampaignId'])
        .index('by_platform', ['platform'])
        .index('by_videoId', ['videoId'])
        .index('by_showcaseCampaignId_and_videoId', ['showcaseCampaignId', 'videoId'])
        .index('by_showcaseCampaignId_and_platform', ['showcaseCampaignId', 'platform'])
        .index('by_showcaseCampaignId_and_fetchStatus', ['showcaseCampaignId', 'fetchStatus'])
        .index('by_showcaseCampaignId_and_refreshDueAt', ['showcaseCampaignId', 'refreshDueAt'])
        .index('by_showcaseCampaignId_and_indexedViewCount', ['showcaseCampaignId', 'indexedViewCount'])
        .index('by_showcaseCampaignId_and_platform_and_indexedViewCount', ['showcaseCampaignId', 'platform', 'indexedViewCount']),

    // Showcase refresh workflows - tracks active refresh workflows per showcase campaign
    // Used to prevent duplicate workflows and show status in admin panel
    showcaseRefreshWorkflows: defineTable({
        showcaseCampaignId: v.id('showcaseCampaigns'),
        workflowId: v.string(), // Convex Workflow ID
        startedAt: v.number(), // Unix timestamp when workflow started
        startedBy: v.optional(v.id('users')), // null = cron, user = manual trigger
    })
        .index('by_showcaseCampaignId', ['showcaseCampaignId'])
        .index('by_workflowId', ['workflowId']),

    // Showcase access - tracks which users have access to view showcase campaigns
    showcaseAccess: defineTable({
        showcaseCampaignId: v.id('showcaseCampaigns'),
        userId: v.id('users'),
        grantedBy: v.id('users'),
        grantedAt: v.number(),
    })
        .index('by_showcaseCampaignId', ['showcaseCampaignId'])
        .index('by_userId', ['userId'])
        .index('by_showcaseCampaignId_and_userId', ['showcaseCampaignId', 'userId']),

    // Showcase import jobs - tracks CSV import jobs for bulk submission uploads
    showcaseImportJobs: defineTable({
        showcaseCampaignId: v.id('showcaseCampaigns'),
        r2Key: v.string(), // CSV file key in R2
        status: v.union(v.literal('pending'), v.literal('processing'), v.literal('completed'), v.literal('failed')),
        totalUrls: v.optional(v.number()), // Total URLs parsed from CSV
        processedUrls: v.number(), // Number of URLs processed so far
        addedCount: v.number(), // Successfully added submissions
        skippedCount: v.number(), // Skipped (duplicates)
        errorCount: v.number(), // Failed to process
        errors: v.optional(v.array(v.object({ url: v.string(), error: v.string() }))), // First 100 errors only
        startedAt: v.number(),
        completedAt: v.optional(v.number()),
        startedBy: v.id('users'),
        workflowId: v.optional(v.string()),
    })
        .index('by_showcaseCampaignId', ['showcaseCampaignId'])
        .index('by_status', ['status']),

    // ============================================================================
    // Page-Centric Tables
    // ============================================================================

    // Canonical page identity — one row per unique social media page across all campaigns
    // Facebook page listing cache for the exact-views lookup (lib/facebookListings.ts). One row per
    // author page we walk. Internal: never returned by a public query.
    facebookPages: defineTable({
        /** Author page id from /post (`author.id`). */
        pageId: v.string(),
        /** Required by /page/videos alongside page_id. */
        delegatePageId: v.optional(v.string()),
        /** Opaque id /page/reels needs; only /page/details returns it. */
        reelsPageId: v.optional(v.string()),
        idsResolvedAt: v.optional(v.number()),
        /** Oldest item timestamp (unix sec) reached by the last walk of each listing, and when it ran. */
        videosWalk: v.optional(facebookListingWalkValidator),
        reelsWalk: v.optional(facebookListingWalkValidator),
        /** Where a walk cut short stopped, so the next lookup continues from there instead of page 1. */
        videosResume: v.optional(facebookListingResumeValidator),
        reelsResume: v.optional(facebookListingResumeValidator),
        /** Single-walker lease so sibling submissions on one page share a walk instead of racing the provider. */
        walkLeaseUntil: v.optional(v.number()),
    }).index('by_pageId', ['pageId']),

    // Latest play_count seen per Facebook video in a page listing. Exact (/page/videos) and rounded
    // (/page/reels) reads are kept separately so a rounded read never overwrites or lowers an exact one.
    facebookVideoCounts: defineTable({
        videoId: v.string(),
        pageId: v.string(),
        exactPlayCount: v.optional(v.number()),
        exactObservedAt: v.optional(v.number()),
        roundedPlayCount: v.optional(v.number()),
        roundedObservedAt: v.optional(v.number()),
        /**
         * Newest observation of either kind. Required: the table has never held rows without it (added before
         * the listing source first deployed). Drives the retention prune (facebookListingsCache.pruneStaleVideoCounts).
         */
        lastObservedAt: v.number(),
    })
        .index('by_videoId', ['videoId'])
        .index('by_lastObservedAt', ['lastObservedAt']),

    pages: defineTable({
        platform: socialPlatformValidator,
        username: v.string(),
        platformAccountId: v.optional(v.string()),
        platformSecUid: v.optional(v.string()),
        identityKey: v.string(),
        identityStrategy: pageIdentityMatchStrategyValidator,
        currentOwnerUserId: v.optional(v.id('users')),
        // Profile metadata (from social platform API)
        displayName: v.optional(v.string()),
        bio: v.optional(v.string()),
        isVerified: v.optional(v.boolean()),
        isPrivate: v.optional(v.boolean()),
        // Account metrics (top-level for indexing/sorting)
        followersCount: v.optional(v.number()),
        videosCount: v.optional(v.number()),
        totalViews: v.optional(v.number()),
        // Sync lifecycle fields (pages are the authoritative sync target)
        fetchStatus: v.optional(pageFetchStatusValidator),
        fetchError: v.optional(v.string()),
        lastFetchedAt: v.optional(v.number()),
        lastFetchAttemptedAt: v.optional(v.number()),
        lastSuccessfulFetchedAt: v.optional(v.number()),
        lastSeenAt: v.optional(v.number()),
        // Profile picture (stored in R2)
        profilePicKey: v.optional(v.string()),
        profilePicSourceUrl: v.optional(v.string()),
        profilePicLastSyncedAt: v.optional(v.number()),
        linkedAccountsCount: v.optional(v.number()),
        activeLinkedAccountsCount: v.optional(v.number()),
        campaignCount: v.optional(v.number()),
        activeCampaignCount: v.optional(v.number()),
        totalSubmissions: v.optional(v.number()),
        approvedSubmissions: v.optional(v.number()),
        pendingSubmissions: v.optional(v.number()),
        deniedSubmissions: v.optional(v.number()),
        // Sum of viewCount across approved submissions linked to this page.
        // Maintained by the event queue consumer processPageSubmissionCountDeltas.
        totalApprovedViews: v.optional(v.number()),
        // Denormalized boolean for search index filtering (true when currentOwnerUserId is set)
        isOwned: v.optional(v.boolean()),
        searchText: v.optional(v.string()),
        // Global moderation status (platform-wide ban)
        globalStatus: v.optional(pageGlobalStatusValidator),
        globalStatusHistory: v.optional(v.array(pageGlobalStatusHistoryEntryValidator)),
        // Flag — lightweight mod signal ("watch out for this page"). isFlagged stays true for
        // yellow/red only (derived from flagLevel) — green is a positive "safe to accept" signal,
        // not a "needs attention" one, so it must not trip any consumer keyed on isFlagged.
        isFlagged: v.optional(v.boolean()),
        flagLevel: v.optional(flagLevelValidator),
        flagReason: v.optional(v.string()),
        flaggedBy: v.optional(v.id('users')),
        flaggedAt: v.optional(v.number()),
        flagHistory: v.optional(v.array(pageFlagHistoryEntryValidator)),
        // Cached "is this page still live on its social platform" signal, refreshed on a 48h
        // window (distinct from fetchStatus/fetchError above, which track our own sync pipeline's
        // health, not platform-side liveness). See lib/pageActivity.ts.
        platformActivityStatus: v.optional(pageActivityStatusValidator),
        // Advances ONLY on a definitive check result (found -> ACTIVE, not_found -> INACTIVE).
        // This is what the 48h due-check reads. Mirrors lastSuccessfulFetchedAt's role above.
        platformActivityCheckedAt: v.optional(v.number()),
        // Advances on EVERY check attempt, success or failure. Mirrors lastFetchAttemptedAt.
        // Used only for the short retry-cooldown in lib/pageActivity.ts; never read by the 48h
        // freshness check, so a transient failure can never masquerade as a fresh confirmed result.
        platformActivityLastAttemptedAt: v.optional(v.number()),
        platformActivityError: v.optional(v.string()),
        // When the platform first said "not found" without that being confirmed yet. A single
        // not-found isn't trusted (Instagram's provider returns the same empty envelope for some
        // of its own errors), so the first one only sets this and schedules a confirmation check;
        // see pages.ts:recordPageActivityResult. Cleared by any "found" result.
        platformActivityNotFoundAt: v.optional(v.number()),
        // Tombstone pointer. Set by migrations/pageDuplicateMerge when this row was merged into
        // another page: every reference (submissions, CMAs, connected accounts, labels, events,
        // notes, ban requests) has been repointed at the target, and this row's stable identity
        // fields were cleared so no identity lookup can ever resolve back to it. Kept rather than
        // deleted so the merge stays auditable and reversible until `purgeMergedPages` runs.
        mergedIntoPageId: v.optional(v.id('pages')),
    })
        .index('by_identityKey', ['identityKey'])
        .index('by_mergedIntoPageId', ['mergedIntoPageId'])
        // A page stores ONE identityKey, but an account can present several identity candidates,
        // and `updateExistingPage` re-keys a page to the most stable candidate it has seen. So a
        // page first keyed on platformAccountId becomes keyed on platformSecUid the moment a
        // secUid arrives — and is then unreachable from any later resolution that only has the
        // accountId, which creates a duplicate page for a handle that already has one. These two
        // indexes let resolution fall back to the stable ids the page actually stores, whichever
        // key it happens to be filed under.
        .index('by_platform_and_platformAccountId', ['platform', 'platformAccountId'])
        .index('by_platform_and_platformSecUid', ['platform', 'platformSecUid'])
        .index('by_platform', ['platform'])
        .index('by_platform_and_username_and_globalStatus', ['platform', 'username', 'globalStatus'])
        .index('by_currentOwnerUserId', ['currentOwnerUserId'])
        .index('by_platform_and_currentOwnerUserId', ['platform', 'currentOwnerUserId'])
        .index('by_currentOwnerUserId_and_globalStatus', ['currentOwnerUserId', 'globalStatus'])
        // Sort indexes for global pages table
        .index('by_followersCount', ['followersCount'])
        .index('by_platform_and_followersCount', ['platform', 'followersCount'])
        .index('by_totalSubmissions', ['totalSubmissions'])
        .index('by_activeCampaignCount', ['activeCampaignCount'])
        .index('by_profilePicKey', ['profilePicKey'])
        .index('by_globalStatus', ['globalStatus'])
        .index('by_isFlagged', ['isFlagged'])
        // The admin Pages list's default view (Owner: Assigned) by newest and by followers, with and
        // without a platform — see admin/pages.ts getPages. Unowned rows dominate the top by
        // followers, so filtering them out after the read came back short.
        .index('by_isOwned', ['isOwned'])
        .index('by_isOwned_and_followersCount', ['isOwned', 'followersCount'])
        .index('by_platform_and_isOwned_and_followersCount', ['platform', 'isOwned', 'followersCount'])
        // Drives the recheck-inactive-pages cron: confirmed-inactive pages, oldest result first.
        // Without it the cron would scan the whole table to find a small slice.
        .index('by_platformActivityStatus_and_platformActivityCheckedAt', ['platformActivityStatus', 'platformActivityCheckedAt'])
        // No dedicated by_flagLevel index — a low-volume, admin-only filter slice on an already
        // heavily-indexed, write-hot table. Query the by_isFlagged-scoped set with filterWith()
        // for an exact color (the admin list filters activity status through the search index's
        // filterFields below), mirroring admin/submissions.ts's documented move away from many
        // index branches toward filterWith().
        .searchIndex('search_pages_global', {
            searchField: 'searchText',
            filterFields: ['platform', 'isOwned', 'isFlagged', 'globalStatus', 'flagLevel', 'platformActivityStatus'],
        }),

    pageLabels: defineTable({
        name: v.string(),
        normalizedName: v.string(), // lowercased + trimmed; uniqueness + case-insensitive lookup
        color: pageLabelColorValidator,
        createdBy: v.id('users'),
    }).index('by_normalizedName', ['normalizedName']),

    pageToLabel: defineTable({
        pageId: v.id('pages'),
        labelId: v.id('pageLabels'),
        // denormalized from the page so a label-filtered list can sort/filter via index
        // (mirrors the campaignMemberAccounts precedent). Maintained by the pages trigger —
        // see _infra/triggers.ts, which must patch flagLevel alongside isFlagged/globalStatus
        // whenever the source page's flag changes, or this projection silently goes stale.
        followersCount: v.number(),
        platform: socialPlatformValidator,
        globalStatus: pageGlobalStatusValidator,
        isOwned: v.boolean(),
        isFlagged: v.boolean(),
        flagLevel: v.optional(flagLevelValidator),
    })
        .index('by_pageId', ['pageId'])
        .index('by_labelId', ['labelId'])
        .index('by_pageId_and_labelId', ['pageId', 'labelId'])
        .index('by_labelId_and_followersCount', ['labelId', 'followersCount']),

    // Campaign-side tagging ("categories"): standardized tags an admin attaches to a campaign so
    // creators can narrow discovery to the kinds of work they want (logos, music promo, clipping…).
    // A sibling of `pageLabels` rather than a `kind` column on it: in Convex the table name IS the
    // id type, so unifying would mean either renaming `pageLabels` (rewriting every
    // `v.id('pageLabels')` reference, including emailBroadcasts.labelIds) or keeping campaign tags
    // in a table literally named `pageLabels`. The two share the color palette and the admin UI,
    // not the storage. Uniqueness is per-table, so a page label and a campaign label may share a name.
    campaignLabels: defineTable({
        name: v.string(),
        normalizedName: v.string(), // lowercased + trimmed; uniqueness + case-insensitive lookup
        color: pageLabelColorValidator, // same palette as page labels — one set of tokens in @repo/ui
        createdBy: v.id('users'),
        // Denormalized counts maintained by the campaignToLabel trigger — never patch directly.
        // Plain fields rather than `pageLabels`' sharded counters: tagging is a rare, admin-only
        // write, so there is no contention to shard away. The point is the READ side — without
        // these, listing labels means collecting every junction row, which puts the whole table
        // in the read set of a public query and re-runs it for every viewer on any tag change.
        // `v.optional` follows the `campaigns.broadcastCount` precedent for a denormalized count
        // added to a table that already has rows; treat a missing value as 0.
        campaignCount: v.optional(v.number()),
        // Campaigns this label can actually surface in discovery: not hidden, and active or
        // completed — the exact set `listCampaigns` serves.
        discoverableCampaignCount: v.optional(v.number()),
    }).index('by_normalizedName', ['normalizedName']),

    campaignToLabel: defineTable({
        campaignId: v.id('campaigns'),
        labelId: v.id('campaignLabels'),
        // Denormalized from the campaign so a label-filtered discovery feed can slice on the same
        // axes the public campaign indexes use (every one of them is by_status_isHidden_*) without
        // loading each campaign. Maintained by the campaigns trigger in _infra/triggers.ts — adding
        // these later would be a backfill, so they land with the table. Sort keys (startDate,
        // totalBudget, costPer1kViews) are deliberately not projected yet; add them with the
        // discovery feed that needs them.
        status: campaignStatusValidator,
        isHidden: v.boolean(),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_labelId', ['labelId'])
        .index('by_campaignId_and_labelId', ['campaignId', 'labelId'])
        .index('by_labelId_and_status_and_isHidden', ['labelId', 'status', 'isHidden']),

    // Page ownership and participation event log — append-only, no edits, no deletes.
    pageEvents: defineTable({
        pageId: v.id('pages'),
        eventType: pageEventTypeValidator,
        actorUserId: v.optional(v.id('users')),
        campaignId: v.optional(v.id('campaigns')),
        campaignMemberAccountId: v.optional(v.id('campaignMemberAccounts')),
        connectedAccountId: v.optional(v.id('connectedAccounts')),
        metadata: v.optional(v.record(v.string(), v.string())),
        timestamp: v.number(),
    })
        .index('by_pageId', ['pageId'])
        .index('by_pageId_and_eventType', ['pageId', 'eventType'])
        // One application's own events, for its status history (lib/cmaStatusHistory.ts). Added staged in
        // #547 so its backfill didn't block that deploy; unstaged once the backfill finished.
        .index('by_campaignMemberAccountId_and_eventType', ['campaignMemberAccountId', 'eventType']),

    // Moderation quality analytics — one row per approve/deny transition on a submission or a
    // page (campaignMemberAccount). Populated by triggers on live writes (see _infra/triggers.ts)
    // and backfilled from submissions.statusHistory / pageEvents (see migrations/backfillModerationEvents*).
    // Exists because neither source is efficiently queryable by moderator+time-range: submissions'
    // statusHistory is an unindexed array, and campaignMemberAccounts' reviewedBy/reviewedAt/reason
    // are destructively overwritten on every re-review (no history at all). Fields are kept flat
    // (not nested in a union) so they stay indexable — same reasoning as walletTransactions'
    // reversalOfTransactionId/reversedByTransactionId living outside the `type` union.
    moderationEvents: defineTable({
        entityType: v.union(v.literal('submission'), v.literal('page')),
        submissionId: v.optional(v.id('submissions')), // set iff entityType === 'submission'
        campaignMemberAccountId: v.optional(v.id('campaignMemberAccounts')), // set iff entityType === 'page'
        campaignId: v.id('campaigns'),
        moderatorId: v.optional(v.id('users')), // actor for this transition; undefined for system/auto-deny events (see `source`) or unattributable backfill gaps
        action: v.union(v.literal('approved'), v.literal('denied')),
        occurredAt: v.number(),
        submittedAt: v.number(), // entity's _creationTime, denormalized to avoid a join for duration calc
        approvalDurationMs: v.optional(v.number()), // set iff action === 'approved': occurredAt - submittedAt
        // Set for BOTH approve and deny events: occurredAt - submittedAt. Powers the median/p90
        // decision-speed metric (approvalDurationMs only ever covered approvals). Historical rows
        // predating this field are backfilled — see migrations/backfillDecisionDurationMs.ts.
        decisionDurationMs: v.optional(v.number()),
        denialReason: v.optional(v.string()), // set iff action === 'denied'
        wasApprovedBefore: v.optional(v.boolean()), // set iff action === 'denied': was there a prior 'approved' event for this entity?
        firstApprovedByModeratorId: v.optional(v.id('users')), // set iff wasApprovedBefore
        firstApprovedAt: v.optional(v.number()),
        // Set iff entityType === 'submission' && wasApprovedBefore: sum of paid-out payoutAllocations
        // for this submission. Page-level events never carry this (pages have no payoutAllocations of
        // their own) — a cascade group's page-level clawback total is recovered by joining on
        // cascadeGroupId across the group's submission-level events, not computed per-page-event.
        clawedBackCents: v.optional(v.number()),
        // Mirrors statusHistoryEntry.source for submissions (STATUS_HISTORY_SOURCE) — carried onto
        // the event so 'auto-deny' rows can be displayed distinctly (e.g. "System (page cap)")
        // without being mistaken for an unattributable backfill gap. Undefined for page events,
        // which have no source-tracking field on the CMA doc today.
        source: v.optional(statusHistorySourceValidator),
        // Set only when this transition was part of a bulk-deny action (page-ban cascade, "deny
        // all"), so N events from one moderator action can be grouped back into a single counted
        // decision for reversal/decision-count metrics — never for the timing/percentile metrics,
        // where every individual video/page keeps its own real wait-time datapoint. No group id =
        // not a cascade; this also correctly describes every historical row (no backfill needed).
        cascadeGroupId: v.optional(v.string()),
    })
        .index('by_campaignId_and_occurredAt', ['campaignId', 'occurredAt'])
        .index('by_occurredAt', ['occurredAt'])
        // Self-referential lookup: since campaignMemberAccounts overwrites reviewedBy on re-review,
        // this table (not the CMA doc) is the source of truth for "who approved this page before".
        .index('by_campaignMemberAccountId_and_occurredAt', ['campaignMemberAccountId', 'occurredAt'])
        // Backfill-migration idempotency check (has this transition already been recorded?)
        // and a future per-submission "moderation history" lookup.
        .index('by_submissionId_and_occurredAt', ['submissionId', 'occurredAt'])
        // Fetch every event in a cascade group — collapsed decision-tree row + clawback sum
        // (queried on the cascadeGroupId prefix alone), and the per-entityType member list for
        // one expanded decision-tree row (queried on both fields).
        .index('by_cascadeGroupId', ['cascadeGroupId', 'entityType']),

    // Moderation KPIs reset marker (singleton — at most one row). When present,
    // admin/moderationAnalytics.ts's resolveModerationSince clamps every range's `since`
    // to `resetAt` so a super user can zero out historical KPI comparisons across every
    // moderationEvents consumer without touching the underlying event history.
    moderationKpiResets: defineTable({
        resetAt: v.number(),
        resetBy: v.id('users'),
        // Prior resets, oldest first — appended before each overwrite so an investigator
        // can reconstruct every baseline change, not just the current one.
        history: v.optional(v.array(v.object({ resetAt: v.number(), resetBy: v.id('users') }))),
    }),

    // Page moderator notes — full CRUD with edit history.
    pageNotes: defineTable({
        pageId: v.id('pages'),
        authorId: v.id('users'),
        content: v.string(),
        editHistory: v.optional(v.array(pageNoteEditHistoryEntryValidator)),
    })
        .index('by_pageId', ['pageId'])
        .searchIndex('search_pageNotes', {
            searchField: 'content',
            filterFields: ['pageId', 'authorId'],
        }),

    // User staff notes — full CRUD with edit history (DEV-55). Mirrors pageNotes for user profiles.
    userNotes: defineTable({
        userId: v.id('users'),
        authorId: v.id('users'),
        content: v.string(),
        editHistory: v.optional(v.array(pageNoteEditHistoryEntryValidator)),
    })
        .index('by_userId', ['userId'])
        .searchIndex('search_userNotes', {
            searchField: 'content',
            filterFields: ['userId', 'authorId'],
        }),

    // Moderator-submitted, admin-reviewed ban requests. targetType discriminates page vs user
    // (mirrors moderationEvents.entityType's pattern) rather than two separate tables. See
    // admin/banRequests.ts. Low-volume table — no denormalization concerns.
    banRequests: defineTable({
        targetType: banRequestTargetTypeValidator,
        pageId: v.optional(v.id('pages')), // set iff targetType === 'page'
        userId: v.optional(v.id('users')), // set iff targetType === 'user'
        // Required iff targetType === 'user' — banUser (unlike banPage) takes an explicit
        // soft/hard level, so the requester must specify which one they're asking for.
        // Ignored for targetType === 'page' (banPage is binary, no level to choose).
        requestedUserBanStatus: v.optional(banUserStatusValidator),
        requestedBy: v.id('users'), // moderator (or admin) who submitted
        reason: v.string(), // required, non-empty — why this should be banned
        // Links to the videos the ban is for. Required (1–10) on new requests; unset on requests
        // submitted before links existed.
        evidence: v.optional(v.array(banRequestEvidenceValidator)),
        status: banRequestStatusValidator,
        reviewedBy: v.optional(v.id('users')),
        reviewedAt: v.optional(v.number()),
        reviewNote: v.optional(v.string()),
        statusHistory: v.array(banRequestStatusHistoryEntryValidator),
    })
        .index('by_status', ['status'])
        // getMyBanRequests: all of the current staff member's requests (any status), newest
        // first. Deliberately single-field, not (requestedBy, status): a partial-index query
        // only orders by _creationTime once every field to its left in the index is pinned by
        // .eq() — a compound (requestedBy, status) index queried with only requestedBy pinned
        // would sort by status first, then _creationTime within each status group, which is not
        // "newest first".
        .index('by_requestedBy', ['requestedBy'])
        // getBanRequestsForTarget: all requests (any status) against one target.
        .index('by_pageId', ['pageId'])
        .index('by_userId', ['userId'])
        // Dedupe check: is there already a pending request against this exact target?
        .index('by_pageId_and_status', ['pageId', 'status'])
        .index('by_userId_and_status', ['userId', 'status'])
        // getBanRequestHistory with both a requester and a target filter: both fields pinned,
        // so the date range still runs on the trailing _creationTime.
        .index('by_requestedBy_and_pageId', ['requestedBy', 'pageId'])
        .index('by_requestedBy_and_userId', ['requestedBy', 'userId']),

    // One row per banned page (lib/banHolds.ts). The ban no longer denies the
    // page's memberships and videos straight away: they stay as they are (approved videos keep earning)
    // and payouts withhold their money. Unbanning within 30 days releases the hold; on day 30 the hold
    // expires and today's deny cascade runs. The page ban itself stays two-state (pages.globalStatus).
    banHolds: defineTable({
        pageId: v.id('pages'),
        // The page's owner when the hold started.
        ownerUserId: v.optional(v.id('users')),
        source: banHoldSourceValidator,
        bannedBy: v.id('users'),
        banReason: v.string(),
        banAt: v.number(),
        expiresAt: v.number(),
        status: banHoldStatusValidator,
        resolvedAt: v.optional(v.number()),
        resolvedBy: v.optional(v.id('users')),
        resolveReason: v.optional(v.string()),
        // Payout money this hold is withholding now: the allocations it holds back and that weren't credited
        // or forfeited (lib/banHolds.ts isStillWithheld). Kept by the payoutAllocations trigger; holds from
        // before the field read as unset until migrations/backfillBanHoldWithheld runs.
        withheldCents: v.optional(v.number()),
    })
        .index('by_pageId_and_status', ['pageId', 'status'])
        .index('by_status_and_expiresAt', ['status', 'expiresAt'])
        .index('by_status_and_resolvedAt', ['status', 'resolvedAt'])
        .index('by_ownerUserId', ['ownerUserId'])
        .index('by_ownerUserId_and_status', ['ownerUserId', 'status']),

    // One row per "restore from ban" run (lib/banRestoration.ts): who reversed which hard-ban episode,
    // why, and what changed or could not be restored. The ban itself and the unban are recorded in
    // users.globalStatusHistory; this is the third leg of that audit trail.
    banRestorations: defineTable({
        userId: v.id('users'),
        // The ban episode = users.globalStatusHistory entry (status 'banned') with this changedAt.
        banAt: v.number(),
        bannedBy: v.id('users'),
        banReason: v.string(),
        restoredBy: v.id('users'),
        reason: v.string(),
        allowReducingOthers: v.boolean(),
        status: banRestorationStatusValidator,
        startedAt: v.number(),
        finishedAt: v.optional(v.number()),
        userUnbanned: v.boolean(),
        pages: v.array(banRestorationPageOutcomeValidator),
        // Processing order; `campaigns` gains one outcome per campaign as each step finishes.
        campaignIds: v.array(v.id('campaigns')),
        campaigns: v.array(banRestorationCampaignOutcomeValidator),
        // Manual wallet credits made since the ban that staff confirmed before restoring, so a
        // compensation already paid is visible next to what the restoration pays (no double credit).
        acknowledgedCredits: v.array(v.object({ transactionId: v.id('walletTransactions'), amountCents: v.number(), reason: v.optional(v.string()) })),
        error: v.optional(v.string()),
    })
        .index('by_userId', ['userId'])
        .index('by_userId_and_status', ['userId', 'status']),

    // Campaign audit log - immutable record of config changes and key actions
    campaignAuditLog: defineTable({
        campaignId: v.id('campaigns'),
        actorUserId: v.id('users'),
        action: v.string(),
        field: v.optional(v.string()),
        previousValue: v.optional(v.string()),
        newValue: v.optional(v.string()),
        timestamp: v.number(),
        note: v.optional(v.string()),
    }).index('by_campaignId_and_timestamp', ['campaignId', 'timestamp']),

    // ============================================================================
    // EVENT QUEUE (OCC-free propagation buffer)
    // ============================================================================

    propagationQueue: defineTable({
        eventType: v.string(),
        campaignId: v.id('campaigns'),
        userId: v.optional(v.id('users')),
        submissionId: v.optional(v.id('submissions')),
        payload: v.any(),
        createdAt: v.number(),
    })
        .index('by_campaignId_and_createdAt', ['campaignId', 'createdAt'])
        .index('by_eventType_campaignId_createdAt', ['eventType', 'campaignId', 'createdAt'])
        // Per-creator lookup of queued changes (lib/pendingEarnings.ts checks for pending-earnings deltas).
        .index('by_eventType_userId_createdAt', ['eventType', 'userId', 'createdAt']),

    // ============================================================================
    // NOTIFICATIONS (docs/systems/NOTIFICATIONS_SYSTEM.md)
    // ============================================================================

    // Outbox between domain writes and the notification component. Triggers only INSERT here
    // (zero OCC with the host write); the cron drainer (notifications/outbox.ts) reads rows older
    // than its buffer, resolves recipient + payload, creates the notification and deletes the row.
    // A row that can't be processed is kept with failedAt/failReason instead of blocking the queue.
    // Deliberately separate from propagationQueue: that one is campaign-partitioned and coalesces
    // deltas, and a stranded type there starves recovery for every consumer.
    notificationOutbox: defineTable({
        source: v.union(
            v.object({ type: v.literal('moderationEvent'), id: v.id('moderationEvents') }),
            v.object({
                type: v.literal('withdrawal'),
                id: v.id('solanaWithdrawals'),
                status: v.union(v.literal('confirmed'), v.literal('failed')),
            })
        ),
        createdAt: v.number(),
        failedAt: v.optional(v.number()),
        failReason: v.optional(v.string()),
    }).index('by_failedAt_and_createdAt', ['failedAt', 'createdAt']),

    // One row per notification that has an email policy: the idempotency record for the scheduled
    // "email if still unseen" path (notifications/email/), and its audit trail.
    notificationEmails: defineTable({
        notificationId: v.string(), // component id (string across the component boundary)
        userId: v.id('users'),
        kind: notificationKindValidator,
        // planned → rendering (claimed by one email, possibly a summary of several) → sent | skipped
        status: v.union(v.literal('planned'), v.literal('rendering'), v.literal('sent'), v.literal('skipped')),
        reason: v.optional(notificationEmailSkipReasonValidator), // why it was skipped
        // When it may be sent. Due rows of the same recipient go out together as one summary email.
        // Optional only for rows planned before it existed (dev data); every new row sets it.
        dueAt: v.optional(v.number()),
        createdAt: v.number(),
        updatedAt: v.number(),
    })
        .index('by_notificationId', ['notificationId'])
        .index('by_userId_and_status_and_dueAt', ['userId', 'status', 'dueAt'])
        .index('by_createdAt', ['createdAt']),

    // A creator's notification channel (notifications/preferences.ts). Its own table, keyed by the
    // Clerk subject, so the bell can read it without reading the users row (rewritten on every stats
    // refresh). No row = the default ('both').
    notificationPreferences: defineTable({
        clerkUserId: v.string(),
        userId: v.id('users'),
        channel: notificationChannelValidator,
        updatedAt: v.number(),
    }).index('by_clerkUserId', ['clerkUserId']),

    // ============================================================================
    // DYNAMIC CRON CONFIG
    // ============================================================================

    // Per-campaign refresh cron configuration.
    // One row per campaign. Stores the interval and cron name for the dynamic cron component.
    // Created when the first submission is approved; deleted when campaign is archived.
    //
    // nextRefreshAt is the expected time of the next refresh. Maintained by
    // runPostRefreshSequence (set to Date.now() + intervalMs after each successful
    // refresh) and by pause/resume/interval mutations. Paused rows get
    // Number.MAX_SAFE_INTEGER so they fall out of the supervisor's overdue index query.
    // Optional only during rollout — backfilled via migrations/campaignRefreshConfig.
    campaignRefreshConfig: defineTable({
        campaignId: v.id('campaigns'),
        intervalMs: v.number(),
        cronName: v.string(),
        lastManualRefreshAt: v.optional(v.number()),
        // Last refresh requested by a brand (owner without a staff role); gates BRAND_REFRESH_COOLDOWN_MS.
        lastBrandRefreshAt: v.optional(v.number()),
        // Staff opt-out of age-based refresh tiers: every scheduled tick refreshes every video. Absent = tiers on.
        refreshAllVideos: v.optional(v.boolean()),
        paused: v.optional(v.boolean()),
        nextRefreshAt: v.optional(v.number()),
    })
        .index('by_campaignId', ['campaignId'])
        .index('by_nextRefreshAt', ['nextRefreshAt']),

    // Refresh history — one entry per completed refresh workflow.
    // Written by handleWorkflowComplete, pruned to 20 entries per campaign.
    refreshHistory: defineTable({
        campaignId: v.id('campaigns'),
        completedAt: v.number(),
        submissionsProcessed: v.number(),
        failureCount: v.number(),
        durationMs: v.number(),
        source: v.union(v.literal('auto'), v.literal('manual')),
        triggeredBy: v.optional(v.id('users')),
        // The run succeeded but no video was due (a tiered tick or gated refresh where everything was fresh).
        nothingDue: v.optional(v.boolean()),
    }).index('by_campaignId_and_completedAt', ['campaignId', 'completedAt']),

    // Audit log for outbound Discord webhook announcements.
    // One row per attempt (sent, skipped, or failed). Polymorphic by eventType.
    discordWebhooks: defineTable({
        eventType: discordWebhookEventTypeValidator,
        status: discordWebhookStatusValidator,
        skipReason: v.optional(discordWebhookSkipReasonValidator),
        // Polymorphic context fields — populated based on eventType.
        campaignId: v.optional(v.id('campaigns')),
        // Delivery outcome (for sent/failed; 0/0 for skipped).
        targetsAttempted: v.number(),
        targetsSuccessful: v.number(),
        errorMessage: v.optional(v.string()),
        createdAt: v.number(),
    })
        .index('by_eventType_and_createdAt', ['eventType', 'createdAt'])
        .index('by_campaignId', ['campaignId'])
        .index('by_status_and_createdAt', ['status', 'createdAt']),

    // ============================================================================
    // Campaign Presets (admin-only reusable campaign templates)
    // ============================================================================

    campaignPresets: defineTable({
        name: v.string(),
        createdBy: v.id('users'),
        title: v.optional(v.string()),
        subtitle: v.optional(v.string()),
        description: v.optional(v.string()),
        totalBudget: v.optional(v.number()),
        costPer1kViews: v.optional(v.number()),
        acceptedPlatforms: v.optional(v.array(socialPlatformValidator)),
        imageKey: v.optional(v.string()),
        socials: v.optional(
            v.array(
                v.object({
                    platform: socialPlatformValidator,
                    username: v.string(),
                })
            )
        ),
        acceptedNiches: v.optional(v.array(v.string())),
        campaignRules: v.optional(campaignRulesValidator),
        campaignContent: v.optional(campaignContentValidator),
        isAcceptingMembers: v.optional(v.boolean()),
        isAcceptingSubmissions: v.optional(v.boolean()),
        isPrivate: v.optional(v.boolean()),
        password: v.optional(v.string()),
        isHidden: v.optional(v.boolean()),
        isCreatorHidden: v.optional(v.boolean()),
    })
        .index('by_name', ['name'])
        .searchIndex('search_campaignPresets', {
            searchField: 'name',
        }),

    // ========================================================================
    // Public Campaign API (docs/superpowers/plans/2026-09-25-campaign-dashboard-api.md)
    // ========================================================================

    // One row per API key. The key itself is never stored — only an HMAC of it (lib/apiKeys.ts),
    // looked up by index on every request. v1 keys are admin-issued with explicit campaign grants
    // (mode 'grants').
    // expiresAt: set by manage.ts expiresAtFor from an API_KEY_LIFETIMES choice ('90d' | '1y') on the
    // server clock; only super users may leave it unset (never expires).
    apiKeys: defineTable({
        name: v.string(), // "Acme — production integration"
        clientLabel: v.string(), // who the key was issued to, e.g. "Acme Corp"
        keyHash: v.string(), // hex HMAC-SHA256(API_KEY_PEPPER, rawKey)
        keyPrefix: v.string(), // first characters of the key, for display/identification only
        mode: apiKeyModeValidator,
        createdBy: v.id('users'),
        createdAt: v.number(),
        expiresAt: v.optional(v.number()), // absent = never expires
        revokedAt: v.optional(v.number()),
        revokedBy: v.optional(v.id('users')),
        revokeReason: v.optional(v.string()),
        // Throttled (written at most hourly) so request traffic doesn't contend on the key doc.
        lastUsedAt: v.optional(v.number()),
        // When a request with this key, while revoked/expired, was last written to its audit trail
        // (publicApi/auth.ts): at most once a minute, so a hammered dead key costs cache hits.
        lastRefusalAuditAt: v.optional(v.number()),
        // How many campaigns the key is granted, maintained with apiKeyCampaignGrants (manage.ts) so
        // listing keys doesn't read every grant row. Optional only for keys created before it.
        grantCount: v.optional(v.number()),
        // Rotation links (publicApi/manage.ts rotateApiKey): the key that replaced this one, and the
        // key this one replaced. A key is rotated at most once.
        replacedByKeyId: v.optional(v.id('apiKeys')),
        rotatedFromKeyId: v.optional(v.id('apiKeys')),
    })
        .index('by_keyHash', ['keyHash'])
        .index('by_createdAt', ['createdAt']),

    // Change logs behind the integration API's `changedSince` feeds (integrationApi/changes.ts). A
    // trigger appends a row when a field the API returns changes; readers walk `_creationTime`, and
    // the per-campaign feeds read by_campaignId (submissionChanges, applicationChanges). Rows can repeat for one entity (readers dedupe) and are purged after
    // CHANGE_LOG_RETENTION_DAYS. A log rather than an `updatedAt` on the rows: no backfill of the
    // two biggest tables, no extra write on every video refresh, no index on `submissions`.
    // A deleted entity gets a row too; readers report it because its document is gone.
    pageChanges: defineTable({ pageId: v.id('pages') }),
    // campaignId: the campaign the submission was in when this change was written, behind the
    // per-campaign feed (`/submissions?campaign=&changedSince=`). Absent on rows written before
    // this field was deployed; those age out of the 30-day retention.
    submissionChanges: defineTable({ submissionId: v.id('submissions'), campaignId: v.optional(v.id('campaigns')) }).index('by_campaignId', ['campaignId']),
    // One row per change to an application (a campaignMemberAccounts row) that the integration API
    // returns. campaignId is the application's campaign at that change; it is absent on a new
    // application until populateMemberAccountFields sets it (integrationApi/changeLog.ts).
    applicationChanges: defineTable({ cmaId: v.id('campaignMemberAccounts'), campaignId: v.optional(v.id('campaigns')) }).index('by_campaignId', [
        'campaignId',
    ]),

    // Which campaigns a key may read. A separate table (not an array on the key) so a campaign
    // page can list the keys that can see it, and so grant changes are single-row writes.
    apiKeyCampaignGrants: defineTable({
        apiKeyId: v.id('apiKeys'),
        campaignId: v.id('campaigns'),
        grantedBy: v.id('users'),
        grantedAt: v.number(),
    })
        .index('by_apiKeyId_and_campaignId', ['apiKeyId', 'campaignId'])
        .index('by_campaignId', ['campaignId']),

    // Lifecycle trail for API keys (modelled on referralAuditLog). Request traffic is NOT logged
    // here — that goes to structured function logs.
    apiKeyAuditLog: defineTable({
        apiKeyId: v.id('apiKeys'),
        actorUserId: v.optional(v.id('users')), // absent = system
        action: apiKeyAuditActionValidator,
        campaignId: v.optional(v.id('campaigns')),
        note: v.optional(v.string()),
        at: v.number(),
    }).index('by_apiKeyId_and_at', ['apiKeyId', 'at']),

    // Per-request audit trail for the public Campaign API — the equivalent of a Google Cloud
    // "Data Access" audit log entry. One row per request made with a recognised key, including
    // refused ones (revoked/expired key) so a leaked key's use is visible — but a refused key is
    // audited at most once a minute (apiKeys.lastRefusalAuditAt), so it can't amplify writes. Unknown
    // keys are never written here, and 429s only reach the daily counters (within the
    // publicApiRefusalAudit budget). Everything is in the function logs. Purged after API_REQUEST_LOG_RETENTION_DAYS
    // (publicApi/usage.ts).
    apiRequestLog: defineTable({
        apiKeyId: v.id('apiKeys'),
        at: v.number(),
        requestId: v.string(), // same id returned to the client in X-Request-Id / error bodies
        operation: v.string(), // route operationId, e.g. 'listSubmissions'
        path: v.string(), // e.g. /v1/campaigns/k57…/submissions
        query: v.optional(v.string()), // query string with pageToken elided and anything key-shaped redacted
        campaignId: v.optional(v.string()), // raw path segment; may not be a valid id
        status: v.number(), // HTTP status returned
        reason: v.optional(v.string()), // error reason when status >= 400
        latencyMs: v.number(),
        callerIp: v.optional(v.string()), // CF-Connecting-IP, set by Cloudflare in front of Convex (router.ts callerIpOf)
        userAgent: v.optional(v.string()),
        method: v.optional(v.string()), // HTTP method; absent on rows from before it was recorded (all GET then)
    })
        .index('by_apiKeyId_and_at', ['apiKeyId', 'at'])
        .index('by_at', ['at'])
        // Support lookups: a client quotes a requestId (docs/systems/PUBLIC_API.md runbook).
        .index('by_requestId', ['requestId']),

    // Per-key, per-UTC-day, per-operation usage counters (Google Cloud's per-credential traffic,
    // error and latency metrics). Kept indefinitely; one small row per key/day/operation.
    apiKeyUsageDaily: defineTable({
        apiKeyId: v.id('apiKeys'),
        day: v.string(), // YYYY-MM-DD (UTC)
        operation: v.string(),
        requests: v.number(),
        clientErrors: v.number(), // 4xx other than 429
        serverErrors: v.number(), // 5xx
        rateLimited: v.number(), // 429
        latencyMsTotal: v.number(), // sum over requests, for averages
        // Each key/day/operation is spread over USAGE_SHARDS rows (publicApi/usage.ts) so parallel
        // requests don't all rewrite one document; readers sum the shards. Absent on rows written
        // before sharding.
        shard: v.optional(v.number()),
    }).index('by_apiKeyId_and_day_and_operation_and_shard', ['apiKeyId', 'day', 'operation', 'shard']),

    // Same counters per UTC hour, for the admin "last 24 hours" view. Short-lived: purged after
    // API_USAGE_HOURLY_RETENTION_HOURS by the purge-api-request-log cron (publicApi/usage.ts).
    apiKeyUsageHourly: defineTable({
        apiKeyId: v.id('apiKeys'),
        hour: v.number(), // start of the UTC hour, ms
        operation: v.string(),
        shard: v.number(),
        requests: v.number(),
        clientErrors: v.number(),
        serverErrors: v.number(),
        rateLimited: v.number(),
        latencyMsTotal: v.number(),
    })
        .index('by_apiKeyId_and_hour_and_operation_and_shard', ['apiKeyId', 'hour', 'operation', 'shard'])
        .index('by_hour', ['hour']),

    // The same counters since the key was issued, for the admin "all time" view — so it never has to
    // sum a key's whole daily history. Kept indefinitely; USAGE_SHARDS rows per key/operation.
    apiKeyUsageTotals: defineTable({
        apiKeyId: v.id('apiKeys'),
        operation: v.string(),
        shard: v.number(),
        requests: v.number(),
        clientErrors: v.number(),
        serverErrors: v.number(),
        rateLimited: v.number(),
        latencyMsTotal: v.number(),
    }).index('by_apiKeyId_and_operation_and_shard', ['apiKeyId', 'operation', 'shard']),
});
